import { useEffect, useMemo, useState, useRef, createContext, useContext } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import PhoneInput from "react-phone-number-input";
import { BASE_URL } from "../config";
import { showSuccess, showError, showConfirm } from "../utils/dialog";
import DateNavigator from "../tempStaff/components/DateNavigator";
import {
  FaEllipsisV,
  FaSlidersH,
  FaUpload,
  FaEye,
  FaDownload,
  FaSearch,
  FaEdit,
  FaTrash,
  FaTimes,
  FaUser,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import axios from "axios";
import FilterBar from "./FilterBar";
import FilterButton from "./FilterButton";
import AddressAutocomplete from "./AddressAutocomplete";
import {
  applyAddressToForm,
  buildAddressAutocompleteValue,
} from "../utils/addressFormUtils";
import ActionDropdown from "./ActionDropdown";
import DocumentPreviewModal from "./DocumentPreviewModal";
import DateInput from "./DateInput";
import RichAuditsPanel from "./RichAuditsPanel";
import { AtlasListToolbar, AtlasRefreshIconButton, SortableTableHeader, TableHeaderCell } from "./list-toolbar";
import { EmbeddedFilterPanel, useEmbeddedFilterLayout } from "./embedded-filter";
import {
  AtlasFilterChipBar,
  FilterToggle,
  FilterMultiSelect,
  FilterSelect,
  PAY_SORT_OPTIONS,
  buildPersonnelPayFilterChips,
  FILE_SORT_OPTIONS,
  FILE_STATE_OPTIONS,
  buildPersonnelFilesFilterChips,
  FORM_SORT_OPTIONS,
  buildPersonnelFormsFilterChips,
  NOTES_SORT_OPTIONS,
  buildPersonnelNotesFilterChips,
  RELIABILITY_SORT_OPTIONS,
  RELIABILITY_REASON_OPTIONS,
  RELIABILITY_NOTICE_OPTIONS,
  RELIABILITY_AFFECTS_OPTIONS,
  RELIABILITY_STATUS_OPTIONS,
  RELIABILITY_DEFAULT_FILTERS,
  buildPersonnelReliabilityFilterChips,
  FilterField,
} from "./filters";
import ClickableDropdown from "./ClickableDropdown";
import UnassignShiftDialog from "./UnassignShiftDialog";
import { resolveBackendUrl, DEFAULT_AVATAR_SRC } from "../utils/accountAvatarUrl";
import defaultProfilePic from "../assets/default_profile_pic.jpeg";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../utils/staffDocumentValidation";
import { formatAccountDateTime } from "../utils/accountDateTime";
import { formatAppDate, formatAppDateTime, formatAppTime, formatAppIntlLabel, resolveAppInstant, getAppTodayIso, parseDbDateTime } from "../utils/appDateTime";
import PersonnelTempCalendar from "./PersonnelTempCalendar";
import { certDisplayName } from "../utils/registrationProvince";
import { useAppPermissionsOptional } from "../context/PermissionContext";
import ProfilePhotoInput from "./ProfilePhotoInput";

export const PersonnelModalZContext = createContext("z-[10150]");

// Simple token management
const getAuthToken = () => {
  // Support both legacy 'token' and newer 'authToken' keys, checking session first
  return (
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token")
  );
};

const setAuthToken = (token) => {
  localStorage.setItem("authToken", token);
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function resolvePersonDisplayName(person) {
  if (!person) return "";
  const direct = String(person.name || "").trim();
  if (direct) return direct;
  const fromParts = `${person.first_name || person.firstName || ""} ${person.last_name || person.lastName || ""}`.trim();
  if (fromParts) return fromParts;
  const preferred = String(person.preferred_name || person.preferredName || "").trim();
  if (preferred) return preferred;
  return "";
}

// Debug flag: when true, MultiSelect dropdowns won't close on outside click.
const DEBUG_DISABLE_MULTISELECT_OUTSIDE_CLOSE = true;

/** Product order for temp staff detail sidebar tabs */
export const TEMP_STAFF_TAB_DEFS = [
  { id: "profile", label: "Profile" },
  { id: "notes", label: "Notes" },
  { id: "calendar", label: "Calendar" },
  { id: "skills", label: "Skills" },
  { id: "certifications", label: "Certifications" },
  { id: "preferences", label: "Preferences" },
  { id: "reliability", label: "Reliability" },
  { id: "pay", label: "Pay" },
  { id: "files", label: "Files" },
  { id: "forms", label: "Forms" },
  { id: "audits", label: "Audits" },
];

const ALLOWED_NOTE_RELATED_TYPES = new Set([
  "fieldstaff",
  "officestaff",
  "candidate",
  "outsourcedstaff",
  "contact",
  "client",
]);

const filterAllowedNoteRelatedTypeOptions = (options) => {
  const rows = Array.isArray(options) ? options : [];
  const filtered = rows.filter((opt) => {
    const key = String(opt?.value ?? opt?.label ?? "")
      .trim()
      .toLowerCase();
    return ALLOWED_NOTE_RELATED_TYPES.has(key);
  });

  const byKey = new Map();
  for (const item of filtered) {
    const key = String(item?.value ?? item?.label ?? "")
      .trim()
      .toLowerCase();
    if (!key) continue;
    byKey.set(key, item);
  }

  const requiredOrder = [
    ["fieldstaff", "Field Staff"],
    ["officestaff", "Office Staff"],
    ["candidate", "Candidate"],
    ["outsourcedstaff", "Outsourced Staff"],
    ["contact", "Contact"],
    ["client", "Client"],
  ];

  return requiredOrder.map(([value, label]) => {
    const existing = byKey.get(value);
    return existing || { value, label };
  });
};

const normalizeNoteRelatedTypeKey = (val) =>
  String(val || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

// Residency mapping helpers at module scope
const mapServerResidencyToUI = (val) => {
  if (!val && val !== "") return val;
  const s = String(val || "").trim();
  switch (s) {
    case "study_resident":
    case "study_permit":
    case "study_res":
      return "study_permit";
    case "temporary_resident":
    case "work_permit":
    case "temp_res":
      return "work_permit";
    case "other":
    case "refugee":
      return "refugee";
    case "citizen":
    case "canadian":
      return "canadian";
    default:
      return s;
  }
};

const mapUIResidencyToServer = (val) => {
  if (!val && val !== "") return val;
  const s = String(val || "").trim();
  switch (s) {
    case "study_permit":
      return "study_resident";
    case "work_permit":
      return "temporary_resident";
    case "refugee":
      return "other";
    case "canadian":
      return "citizen";
    default:
      return s;
  }
};

const normalizeAddressLine1 = (row) => {
  const source = row || {};
  const addressObj =
    source.address && typeof source.address === "object" ? source.address : {};

  const candidates = [
    source.line1,
    source.street,
    source.address_line_1,
    source.address1,
    addressObj.street,
    source.name,
    addressObj.name,
    typeof source.address === "string" ? source.address : "",
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const text = String(candidate).trim();
    if (text) return text;
  }
  return "";
};

// Format actor display: prefer full name if provided, but when the server
// returns only an id (e.g. "(22912)" or "22912"), show a clearer label.
// Format actor display: prefer full name if provided, but when the server
// returns only an id (e.g. "(22912)" or "22912"), show a clearer label.
const formatActor = (actor) => {
  if (!actor && actor !== 0) return "—";
  try {
    const s = String(actor).trim();
    // If it's only digits or wrapped in parentheses like "(123)", show as "Account (123)"
    const onlyDigits = /^\(?\d+\)?$/.test(s);
    if (onlyDigits) {
      const m = s.match(/(\d+)/);
      return m ? `Account (${m[1]})` : s;
    }
    return s || "—";
  } catch (e) {
    return String(actor || "—");
  }
};

// Simple login function to get JWT token
const loginAndGetToken = async () => {
  try {
    const res = await fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: "admin@atlas.com",
        password: "admin123",
      }),
    });
    const data = await res.json();
    if (data.token) {
      setAuthToken(data.token);
      return data.token;
    }
    throw new Error("No token received");
  } catch (error) {
    console.error("Login failed:", error);
    return null;
  }
};

const CreatorAvatar = ({ avatarUrl, name }) => {
  const [imgErr, setImgErr] = useState(false);
  return (
    <div className="w-5 h-5 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center shrink-0 border border-gray-100">
      {!imgErr && avatarUrl ? (
        <img
          src={avatarUrl}
          className="w-full h-full object-cover"
          onError={() => setImgErr(true)}
        />
      ) : (
        <FaUser className="text-gray-400 text-[8px]" />
      )}
    </div>
  );
};

const ProfileAvatarDisplay = ({ url, firstName, lastName, isOffice = false }) => {
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [url]);

  if (imgErr || !url) {
    return (
      <img
        src={defaultProfilePic}
        alt="Profile"
        className="w-full h-full object-cover"
      />
    );
  }

  return (
    <img
      key={url}
      src={url}
      alt="Profile"
      className="w-full h-full object-cover"
      onError={() => {
        setImgErr(true);
      }}
    />
  );
};

const SortArrow = ({ field, currentSort, currentDirection }) => {
  if (currentSort !== field)
    return <FaSort className="text-gray-300 ml-1.5" size={10} />;
  return currentDirection === "asc" ? (
    <FaSortUp className="text-red-600 ml-1.5" size={10} />
  ) : (
    <FaSortDown className="text-red-600 ml-1.5" size={10} />
  );
};

export default function  PersonnelDetailModal({
  person,
  type,
  isOpen,
  onClose,
  onUpdate,
  initialTab = "profile",
  readOnly = false,
  overlayZClass = "z-[9999]",
}) {
  const [activeTab, setActiveTab] = useState("profile");
  const [confirmDialog, setConfirmDialog] = useState(null);

  const actionMenuZClass = useMemo(() => {
    if (
      overlayZClass &&
      (overlayZClass.includes("505") ||
        overlayZClass.includes("500") ||
        overlayZClass.includes("503"))
    ) {
      return "z-[50550]";
    }
    return "z-[10150]";
  }, [overlayZClass]);

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab || "profile");
  }, [isOpen, person?.id, initialTab]);
  // Debug: track tab changes and person context so we can trace when notes components should load
  useEffect(() => {
    console.debug("[PersonnelDetailModal] state", {
      isOpen: !!isOpen,
      activeTab,
      personId: person?.id,
    });
  }, [isOpen, activeTab, person?.id]);

  // Debug: Log person data when modal opens
  useEffect(() => {
    if (isOpen && person) {
      console.log("=== PERSONNEL DETAIL MODAL DEBUG ===");
      console.log("Modal opened for person:", person?.id);
      console.log("Person object:", person);
      console.log("Reliability data:", {
        reliability: person?.reliability,
        utilization: person?.utilization,
        completed_shifts: person?.completed_shifts,
        total_assigned: person?.total_assigned,
        hours_worked: person?.hours_worked,
        max_hours: person?.max_hours,
      });
      console.log("=== END MODAL DEBUG ===");
    }
  }, [isOpen, person]);

  const tabDefs = useMemo(() => {
    if (type === "temp") {
      return TEMP_STAFF_TAB_DEFS;
    }
    return [
      { id: "profile", label: "Profile" },
      { id: "notes", label: "Notes" },
      { id: "files", label: "Files" },
      { id: "audits", label: "Audits" },
    ];
  }, [type]);

  if (!isOpen || !person) return null;

  const titleType = type === "temp" ? "Temp Staff" : "Office Staff";

  return (
    <PersonnelModalZContext.Provider value={actionMenuZClass}>
      <div
        className={`fixed inset-0 ${overlayZClass} flex items-center justify-center bg-black/50 p-2 sm:p-4`}
      >
        <div className="bg-white w-full max-w-5xl h-[95vh] sm:h-[90vh] rounded-lg shadow-lg overflow-hidden flex flex-col">
          <div className="flex items-start justify-between p-4 border-b border-gray-200">
            <div className="text-sm">
              <div className="font-semibold text-gray-700">
                {titleType} #{person?.id ?? "—"}
              </div>
              <div className="text-xl text-gray-900">
                {resolvePersonDisplayName(person) || "Unnamed"}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2"
            >
              &#x2715;
            </button>
          </div>
          <div className="flex flex-col sm:flex-row flex-1 min-h-0">
            <aside className="w-full sm:w-56 border-b sm:border-b-0 sm:border-r border-gray-200 p-3 bg-gray-50 flex-shrink-0 sm:overflow-y-auto">
              <nav className="flex flex-row sm:flex-col gap-2 sm:gap-1 overflow-x-auto sm:overflow-visible whitespace-nowrap scrollbar-hide">
                {tabDefs.map((t) => (
                  <button
                    key={t.id}
                    className={`text-left px-3 py-2 rounded transition-colors flex-shrink-0 ${
                      activeTab === t.id
                        ? "bg-red-600 text-white font-medium"
                        : "hover:bg-gray-100 text-gray-700"
                    }`}
                    onClick={() => setActiveTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </nav>
            </aside>
            <section className="flex-1 min-w-0 min-h-0 overflow-auto bg-white p-3 sm:p-6 sm:pb-12">
              <TabContent
                activeTab={activeTab}
                person={person}
                type={type}
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
                readOnly={readOnly}
                onUpdate={onUpdate}
                actionMenuZClass={actionMenuZClass}
              />
            </section>
          </div>
        </div>
      </div>
    </PersonnelModalZContext.Provider>
  );
}

function TempCalendar({ person }) {
  return (
    <div className="w-full min-h-0">
      <PersonnelTempCalendar person={person} />
    </div>
  );
}

function TabContent({
  activeTab,
  person,
  type,
  confirmDialog,
  setConfirmDialog,
  readOnly = false,
  onUpdate,
  actionMenuZClass = "z-[10150]",
}) {
  let content;
  switch (activeTab) {
    case "profile":
      content =
        type === "temp" ? (
        <TempProfile
          person={person}
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
          onUpdate={onUpdate}
        />
      ) : (
        <OfficeProfile
          person={person}
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
          onUpdate={onUpdate}
        />
      );
      break;
    case "notes":
      content = (
        <TempNotes
          person={person}
          active={true}
          actionMenuZClass={actionMenuZClass}
        />
      );
      break;
    case "calendar":
      content = <TempCalendar person={person} />;
      break;
    case "skills":
      content = <TempSkills person={person} />;
      break;
    case "certifications":
      content = (
        <TempCertifications
          person={person}
          actionMenuZClass={actionMenuZClass}
        />
      );
      break;
    case "preferences":
      content = <TempPreferences person={person} />;
      break;
    case "reliability":
      content = <TempReliability person={person} />;
      break;
    case "pay":
      content = <TempPay person={person} />;
      break;
    case "files":
      content = (
        <TempFiles person={person} actionMenuZClass={actionMenuZClass} />
      );
      break;
    case "forms":
      content = (
        <TempForms person={person} actionMenuZClass={actionMenuZClass} />
      );
      break;
    case "audits":
      content = (
        <RichAuditsPanel
          subjectId={person?.id}
          fetchUrl={
            person?.id ? `${BASE_URL}/api/personnel/accounts/${person.id}/audits/rich` : ""
          }
          title="Audit History"
        />
      );
      break;
    default:
      content = <div className="text-gray-500">Select a tab</div>;
  }

  return (
    <fieldset
      disabled={!!readOnly}
      className="min-h-0 min-w-0 border-0 m-0 p-0 w-full h-full"
    >
      {content}
    </fieldset>
  );
}

function Field({ label, value, hint }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-medium text-gray-600">{label}</div>
      <div className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px]">
        {value ?? ""}
      </div>
      {hint ? <div className="text-xs text-gray-400">{hint}</div> : null}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      {children}
    </div>
  );
}

function ModernMultiSelect({
  options = [],
  value = [],
  onChange,
  onOpen,
  disabled = false,
  placeholder = "Select...",
  displayKey = "name",
  valueKey = "id",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllSelected, setShowAllSelected] = useState(false);
  const containerRef = useRef(null);

  // Filter options based on search
  const filteredOptions = options.filter((option) =>
    option[displayKey]?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get selected options for display
  const selectedOptions = options.filter((option) =>
    value.includes(option[valueKey] || option.id),
  );
  const visibleSelectedOptions = showAllSelected
    ? selectedOptions
    : selectedOptions.slice(0, 2);

  const handleToggle = async () => {
    if (disabled) return;

    if (!isOpen && onOpen) {
      try {
        await onOpen();
      } catch (error) {
        console.error("Error loading options:", error);
      }
    }

    setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionId) => {
    const newValue = value.includes(optionId)
      ? value.filter((id) => id !== optionId)
      : [...value, optionId];
    onChange(newValue);
  };

  const handleRemoveTag = (optionId) => {
    onChange(value.filter((id) => id !== optionId));
  };

  useEffect(() => {
    if (selectedOptions.length <= 2 && showAllSelected) {
      setShowAllSelected(false);
    }
  }, [selectedOptions.length, showAllSelected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Selected values display */}
      <div
        className={`min-h-[2.5rem] w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer flex flex-wrap gap-1 items-center ${
          disabled
            ? "bg-gray-50 cursor-not-allowed opacity-60"
            : "hover:border-gray-400 focus:border-gray-500 focus:ring-2 focus:ring-gray-500/20"
        } ${isOpen ? "ring-2 ring-gray-500/20 border-gray-500" : ""}`}
        onClick={handleToggle}
      >
        {selectedOptions.length > 0 ? (
          <>
            {visibleSelectedOptions.map((option) => (
              <span
                key={option[valueKey] || option.id}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
              >
                {option[displayKey] || option.name}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTag(option[valueKey] || option.id);
                    }}
                    className="ml-1 inline-flex items-center p-0.5 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                  >
                    <svg
                      className="h-3 w-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </span>
            ))}
            {selectedOptions.length > 2 && !showAllSelected && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllSelected(true);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                +{selectedOptions.length - 2} more
              </button>
            )}
            {selectedOptions.length > 2 && showAllSelected && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllSelected(false);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Show less
              </button>
            )}
          </>
        ) : (
          <span className="text-gray-500">{placeholder}</span>
        )}

        <div className="ml-auto">
          <svg
            className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown content */}
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search divisions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const optionId = option[valueKey] || option.id;
                  const isSelected = value.includes(optionId);

                  return (
                    <div
                      key={optionId}
                      onClick={() => handleOptionClick(optionId)}
                      className={`px-3 py-2 cursor-pointer flex items-center hover:bg-gray-50 ${
                        isSelected ? "bg-gray-50" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // Handled by parent onClick
                        className="mr-3 h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-900">
                        {option[displayKey] || option.name}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No divisions found
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


//! this is a tempProfile 1242
// Payroll payer pre-selected for these Temp Staff types (Outsourced Staff: no default)
const DEFAULT_PAYER_TYPES = ["candidate", "fieldstaff"];
const DEFAULT_TEMP_PAYER_NAME = "Cheque - 44hrs";

function TempProfile({ person, confirmDialog, setConfirmDialog, onUpdate }) {
  // Do Not Place: ticking asks for a required reason (saved as a note on Save);
  // once saved, only a Super Admin can untick it.
  const isSuperAdmin = !!useAppPermissionsOptional()?.isSuperAdmin;
  const [dnpReasonOpen, setDnpReasonOpen] = useState(false);
  const [dnpReasonDraft, setDnpReasonDraft] = useState("");
  const [dnpReason, setDnpReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [isSendingActivation, setIsSendingActivation] = useState(false);
  const [data, setData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [residencyUpload, setResidencyUpload] = useState({
    name: "",
    uploading: false,
    error: "",
  });

  const [divisions, setDivisions] = useState([]);
  const [offices, setOffices] = useState([]);
  const [payers, setPayers] = useState([]);

  const fileInputRef = useRef(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [localConfirmDialog, setLocalConfirmDialog] = useState(null);
  const statusMenuRef = useRef(null);
  const dialog =
    confirmDialog !== undefined ? confirmDialog : localConfirmDialog;
  const setDialog =
    setConfirmDialog !== undefined ? setConfirmDialog : setLocalConfirmDialog;
  const [activationEmailSentDate, setActivationEmailSentDate] = useState(null);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [isScrolling, setIsScrolling] = useState(false);

  // Auto-scroll to bottom first, then show dropdown when menu opens
  useEffect(() => {
    if (statusMenuOpen && statusMenuRef.current) {
      // First, scroll to the very bottom
      setTimeout(() => {
        if (!statusMenuRef.current) return;

        // Find the scrollable parent container (modal or main container)
        let scrollableParent = null;
        let element = statusMenuRef.current;

        // Walk up the DOM tree to find scrollable parent
        while (
          element &&
          element !== document.body &&
          element !== document.documentElement
        ) {
          element = element.parentElement;
          if (!element) break;

          const style = window.getComputedStyle(element);
          const overflowY = style.overflowY;
          const hasScroll =
            overflowY === "auto" ||
            overflowY === "scroll" ||
            (element.scrollHeight > element.clientHeight &&
              element.clientHeight > 0);

          // Also check for common modal/scrollable container classes
          const classList = element.classList;
          const isModalContainer =
            classList &&
            (classList.contains("overflow-auto") ||
              classList.contains("overflow-y-auto") ||
              classList.contains("overflow-scroll") ||
              classList.toString().includes("overflow"));

          if (hasScroll || isModalContainer) {
            scrollableParent = element;
            break;
          }
        }

        // Scroll to bottom first
        if (
          scrollableParent &&
          scrollableParent !== document.body &&
          scrollableParent !== document.documentElement
        ) {
          const maxScroll =
            scrollableParent.scrollHeight - scrollableParent.clientHeight;
          // Scroll to bottom immediately
          scrollableParent.scrollTop = maxScroll;
        } else {
          // Fallback: use window scroll to bottom
          window.scrollTo({
            top: document.documentElement.scrollHeight - window.innerHeight,
            behavior: "auto",
          });
        }

        // After scrolling to bottom, update dropdown position
        setTimeout(() => {
          if (!statusMenuRef.current) return;

          const buttonRect = statusMenuRef.current.getBoundingClientRect();
          const dropdownHeight = 450; // Approximate dropdown height with all options
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - buttonRect.bottom;

          // Check if we need to adjust scroll to show dropdown
          if (spaceBelow < dropdownHeight + 20) {
            // Need to scroll up a bit to show dropdown
            if (
              scrollableParent &&
              scrollableParent !== document.body &&
              scrollableParent !== document.documentElement
            ) {
              const currentScrollTop = scrollableParent.scrollTop;
              const scrollUp = dropdownHeight + 40 - spaceBelow; // Scroll up to show dropdown
              const targetScroll = Math.max(0, currentScrollTop - scrollUp);

              scrollableParent.scrollTop = targetScroll;

              // Update dropdown position after scroll adjustment
              setTimeout(() => {
                if (statusMenuRef.current) {
                  const newButtonRect =
                    statusMenuRef.current.getBoundingClientRect();
                  setDropdownStyle({
                    position: "fixed",
                    top: `${newButtonRect.bottom + 4}px`,
                    right: `${window.innerWidth - newButtonRect.right}px`,
                  });
                }
              }, 50);
            } else {
              // Window scroll adjustment
              const scrollUp = dropdownHeight + 40 - spaceBelow;
              window.scrollTo({
                top: Math.max(0, window.scrollY - scrollUp),
                behavior: "auto",
              });

              setTimeout(() => {
                if (statusMenuRef.current) {
                  const newButtonRect =
                    statusMenuRef.current.getBoundingClientRect();
                  setDropdownStyle({
                    position: "fixed",
                    top: `${newButtonRect.bottom + 4}px`,
                    right: `${window.innerWidth - newButtonRect.right}px`,
                  });
                }
              }, 50);
            }
          } else {
            // Enough space, just update dropdown position
            setDropdownStyle({
              position: "fixed",
              top: `${buttonRect.bottom + 4}px`,
              right: `${window.innerWidth - buttonRect.right}px`,
            });
          }
        }, 100); // Wait for scroll to bottom to complete
      }, 50);
    } else {
      // Reset dropdown style when closed
      setDropdownStyle({});
    }
  }, [statusMenuOpen]);

  const [divisionOptions, setDivisionOptions] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);

  const [payerOptions, setPayerOptions] = useState([]);
  const [payoutOptions, setPayoutOptions] = useState([]);
  const [recruiterOptions, setRecruiterOptions] = useState([]);

  const loadDivisions = async () => {
    try {
      const res1 = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/divisions`,
        { headers: getAuthHeaders() },
      );
      const assigned = await res1.json();

      const res2 = await fetch(`${BASE_URL}/api/personnel/divisions`, {
        headers: getAuthHeaders(),
      });
      const all = await res2.json();

      const selectedIds = assigned.success
        ? assigned.data.map((d) => d.division_id)
        : [];
      // Only set options here — avoid mutating `data` during dropdown open
      // which causes a re-render and can close the dropdown prematurely.
      setDivisionOptions(all);
    } catch (e) {
      console.error("Division load failed", e);
    }
  };

  const loadOffices = async () => {
    if (officeOptions.length > 0) return;
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/offices`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      setOfficeOptions(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error("Office load failed", e);
    }
  };

  const loadPayers = async () => {
    if (payerOptions.length > 0) return;
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/payers`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      // Accept either a raw array (rows) or an object { data: [] }
      const list = Array.isArray(json)
        ? json
        : json && Array.isArray(json.data)
          ? json.data
          : [];
      setPayerOptions(list || []);
    } catch (e) {
      console.error("Payer load failed", e);
    }
  };

  const loadPayoutStaff = async () => {
    console.debug(
      "loadPayoutStaff called; current payoutOptions.length=",
      payoutOptions.length,
    );
    if (payoutOptions.length > 0) return;
    // First try with cookie credentials (some environments use session cookies)
    try {
      // Use admin accounts endpoint (mounted at /api/admin/accounts)
      let res = await fetch(`${BASE_URL}/api/admin/accounts?limit=1000`, {
        credentials: "include",
      });
      if (!res.ok) {
        // Retry with Authorization header if cookie-based auth fails
        console.debug(
          "admin/accounts fetch with credentials failed, status=",
          res.status,
          "trying with Authorization header",
        );
        res = await fetch(`${BASE_URL}/api/admin/accounts?limit=1000`, {
          headers: getAuthHeaders(),
        });
      }

      const json = await res.json().catch(() => null);
      // admin route returns { success: true, data: rows }
      const list = Array.isArray(json)
        ? json
        : json && Array.isArray(json.data)
          ? json.data
          : [];
      setPayoutOptions(list || []);
      console.log("Loaded payout accounts:", list);
    } catch (e) {
      console.error("Payout staff load failed", e);
    }
  };

  const loadRecruiters = async () => {
    if (recruiterOptions.length > 0) return;
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/recruiters`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => null);
      const list = Array.isArray(json)
        ? json
        : json && Array.isArray(json.data)
          ? json.data
          : [];
      // normalize to { id, label }
      setRecruiterOptions(
        (list || []).map((r) => ({
          id: r.account_id || r.id || r.accountId,
          label:
            r.name ||
            `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
            `#${r.account_id || r.id}`,
        })),
      );
    } catch (e) {
      console.error("Recruiter load failed", e);
      setRecruiterOptions([]);
    }
  };

  useEffect(() => {
    // Debug: log when options or selected value change so developer can paste console output
    if (data) {
      console.debug(
        "Payout debug: payoutOptions.length=",
        payoutOptions.length,
        "selected payout_account_id=",
        getVal("payout_account_id"),
      );
    }
  }, [payoutOptions, data]);

  useEffect(() => {
    const load = async () => {
      if (!person?.id) return;

      // Pre-load dropdown data to ensure labels are displayed properly
      loadRecruiters();
      loadPayoutStaff();
      loadPayers();

      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}`,
          { headers: getAuthHeaders() },
        );
        const json = await res.json().catch(() => null);
        // If response shape is unexpected, log full response for debugging
        if (!json || json.data === undefined) {
          console.warn("Personnel detail GET returned unexpected body", {
            status: res.status,
            body: json,
          });
        }
        console.log("Initial data load:", json && json.data);
        // Map avatar_url to profile_picture for compatibility
        const mappedData =
          json && json.data
            ? {
                ...json.data,
                profile_picture:
                  json.data.avatar_url || json.data.profile_picture,
              }
            : null;
        console.log(
          "Mapped data with profile_picture:",
          mappedData?.profile_picture,
        );
        setData(mappedData);
        setSnapshot(mappedData);
        // Load activation email sent date if available
        if (mappedData?.activation_email_sent_date) {
          setActivationEmailSentDate(mappedData.activation_email_sent_date);
        }
        // Try to fetch reliability summary and merge into local data so summary cards stay up-to-date
        try {
          const r = await fetch(
            `${BASE_URL}/api/personnel/accounts/${person.id}/reliability`,
            { credentials: "include" },
          );
          const rjson = await r.json().catch(() => ({}));
          if (r.ok && rjson.success && rjson.data) {
            setData((prev) => ({
              ...(prev || {}),
              reliability: rjson.data.reliability_score,
              shiftsWorked:
                rjson.data.total_shifts ??
                rjson.data.worked_shift_count ??
                rjson.data.reliability_details?.attended_shifts ??
                0,
            }));
          }
        } catch (__) {
          // ignore reliability merge failures; detailed tab will still fetch
        }

        console.log("Initial data load (raw):", json.data);

        const raw = json && json.data ? json.data : {};
        const normalized = { ...raw };
        if (!String(normalized.line1 || "").trim()) {
          normalized.line1 = normalizeAddressLine1(raw);
        }

        // ✅ Normalize DIVISIONS
        if (Array.isArray(raw.division_ids)) {
          normalized.division_ids = raw.division_ids;
        } else if (Array.isArray(raw.divisions)) {
          // if API sends objects like { division_id, name }
          normalized.division_ids = raw.divisions.map((d) => d.division_id);
        } else if (raw.division_id) {
          normalized.division_ids = [raw.division_id];
        } else {
          normalized.division_ids = [];
        }

        // ✅ Normalize OFFICES
        if (Array.isArray(raw.office_ids)) {
          normalized.office_ids = raw.office_ids;
        } else if (Array.isArray(raw.offices)) {
          normalized.office_ids = raw.offices.map((o) => o.office_id);
        } else if (raw.office_id) {
          normalized.office_ids = [raw.office_id];
        } else {
          normalized.office_ids = [];
        }
        // Keep legacy single-value binding in sync so dropdown reflects saved office immediately.
        if (normalized.office_ids && normalized.office_ids.length > 0) {
          normalized.employment_office_id = normalized.office_ids[0];
        } else if (raw.employment_office_id || raw.office_id) {
          normalized.employment_office_id =
            raw.employment_office_id || raw.office_id;
        } else {
          normalized.employment_office_id = null;
        }

        // Expose a single employment_office_id for legacy UI bindings
        if (normalized.office_ids && normalized.office_ids.length > 0) {
          normalized.employment_office_id = normalized.office_ids[0];
        } else if (raw.employment_office_id || raw.office_id) {
          normalized.employment_office_id =
            raw.employment_office_id || raw.office_id;
        } else {
          normalized.employment_office_id = null;
        }

        // Map server residency values to UI-friendly values
        try {
          const serverVal =
            normalized.residency_status || normalized.residencyStatus || null;
          if (serverVal !== undefined && serverVal !== null) {
            normalized.residency_status = mapServerResidencyToUI(serverVal);
          }
        } catch (e) {
          console.warn("Residency mapping failed", e);
        }

        // Map server-side note/cheque fields to UI-friendly keys
        const rawNote = String(raw.schedule_note ?? raw.note ?? "");
        normalized.schedule_note = rawNote
          .replace(/office_ids:?\s*[^;\n]+/i, "")
          .trim();
        // Accept either payment_delivery_method or legacy cheque_method from server.
        // If DB stores the canonical enum (delivery_pickup / delivery_mail), map
        // those to the UI-friendly labels used by the radios ('Pickup' / 'Mail').
        const _rawPayment = String(
          (raw.payment_delivery_method ?? raw.cheque_method) || "",
        ).trim();
        const lp = _rawPayment.toLowerCase();
        if (
          lp.includes("pickup") ||
          lp === "delivery_pickup" ||
          lp === "delivery-pickup"
        ) {
          normalized.payment_delivery_method = "Pickup";
        } else if (
          lp.includes("mail") ||
          lp === "delivery_mail" ||
          lp === "delivery-mail"
        ) {
          normalized.payment_delivery_method = "Mail";
        } else if (_rawPayment) {
          // fallback to raw string if it's non-empty
          normalized.payment_delivery_method = _rawPayment;
        } else {
          normalized.payment_delivery_method = "";
        }

        console.log("Normalized data:", normalized);

        setData(normalized);
        setSnapshot(normalized);

        // If a residency file was previously linked on the server (columns or note),
        // try to resolve it to a signed URL so the UI shows the filename and remove button.
        (async () => {
          try {
            // don't overwrite if client already has preview
            if (
              normalized.residency_document ||
              normalized.residency_document_url ||
              normalized.residency_document_name
            )
              return;

            // look for common server-side columns
            let fileId =
              normalized.work_permit_file_id ||
              normalized.study_permit_file_id ||
              normalized.refugee_file_id ||
              null;

            // fallback: parse accounts.note for entries like 'refugee_file_id:123;'
            if (!fileId && typeof normalized.note === "string") {
              const mRef = normalized.note.match(
                /refugee_file_id\s*:\s*(\d+)/i,
              );
              const mWork = normalized.note.match(
                /work_permit_file_id\s*:\s*(\d+)/i,
              );
              const mStudy = normalized.note.match(
                /study_permit_file_id\s*:\s*(\d+)/i,
              );
              if (mRef) fileId = mRef[1];
              else if (mWork) fileId = mWork[1];
              else if (mStudy) fileId = mStudy[1];
            }

            if (!fileId) return;

            const resp = await fetch(
              `${BASE_URL}/api/registration/file/${fileId}/url`,
              { headers: getAuthHeaders() },
            );
            const j = await resp.json().catch(() => null);
            if (!resp.ok || !j) return;
            const url = j.url || j.signedUrl || j.data?.url || "";
            const name =
              j.name || j.filename || (url ? url.split("/").pop() : "") || "";
            if (url) {
              setData((prev) => ({
                ...(prev || {}),
                residency_document: fileId,
                residency_document_url: url,
                residency_document_name: name,
              }));
              setSnapshot((prev) => ({
                ...(prev || {}),
                residency_document: fileId,
                residency_document_url: url,
                residency_document_name: name,
              }));
            }
          } catch (e) {
            console.warn("Failed to resolve persisted residency file", e);
          }
        })();

        // Preload division/office option lists so selected labels render immediately
        try {
          // loadDivisions/loadOffices are idempotent (they early-return if already loaded)
          await loadDivisions();
        } catch (e) {
          console.warn("Failed to preload divisions", e);
        }
        try {
          await loadOffices();
        } catch (e) {
          console.warn("Failed to preload offices", e);
        }
        try {
          await loadPayers();
        } catch (e) {
          console.warn("Failed to preload payers", e);
        }
        try {
          await loadPayoutStaff();
        } catch (e) {
          console.warn("Failed to preload payout staff", e);
        }
        try {
          await loadRecruiters();
        } catch (e) {
          console.warn("Failed to preload recruiters", e);
        }

        // 🔁 Reliability stuff (unchanged, just moved below)
        try {
          const r = await fetch(
            `${BASE_URL}/api/personnel/accounts/${person.id}/reliability`,
            { credentials: "include" },
          );
          const rjson = await r.json().catch(() => ({}));
          if (r.ok && rjson.success && rjson.data) {
            setData((prev) => ({
              ...(prev || {}),
              reliability: rjson.data.reliability_score,
              shiftsWorked:
                rjson.data.total_shifts ??
                rjson.data.worked_shift_count ??
                rjson.data.reliability_details?.attended_shifts ??
                0,
            }));
          }
        } catch (__) {
          // ignore reliability merge failures; detailed tab will still fetch
        }
      } catch (_) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [person?.id]);

  useEffect(() => {
    if (!person?.id) return;
    loadDivisions(); // ✅ FORCE load
  }, [person?.id]);

  // Load recruiters if person has a recruiter and options are not loaded
  useEffect(() => {
    if (
      (data?.recruiter_account_id || person?.recruiter_account_id) &&
      recruiterOptions.length === 0
    ) {
      loadRecruiters();
    }
  }, [
    data?.recruiter_account_id,
    person?.recruiter_account_id,
    recruiterOptions.length,
  ]);

  // Ensure the current recruiter is in the recruiters list
  useEffect(() => {
    const recruiterId =
      data?.recruiter_account_id || person?.recruiter_account_id;
    console.log(
      "Checking recruiter in options",
      recruiterId,
      recruiterOptions.length,
      recruiterOptions.find((r) => String(r.id) === String(recruiterId)),
    );
    if (
      recruiterId &&
      recruiterOptions.length > 0 &&
      !recruiterOptions.find((r) => String(r.id) === String(recruiterId))
    ) {
      console.log("Fetching current recruiter", recruiterId);
      // Fetch the recruiter details and add to list
      fetch(`${BASE_URL}/api/personnel/accounts/${recruiterId}`, {
        headers: getAuthHeaders(),
      })
        .then((res) => res.json())
        .then((json) => {
          if (json.data) {
            const rec = {
              id: json.data.account_id,
              label:
                `${json.data.first_name || ""} ${json.data.last_name || ""}`.trim() ||
                `#${json.data.account_id}`,
            };
            console.log("Adding recruiter to options", rec);
            setRecruiterOptions((prev) => [...prev, rec]);
          } else {
            console.warn("No data for recruiter", recruiterId, json);
          }
        })
        .catch((e) => console.warn("Failed to load current recruiter", e));
    }
  }, [
    data?.recruiter_account_id,
    person?.recruiter_account_id,
    recruiterOptions,
  ]);

  function MultiSelectDropdown({
    label,
    options,
    value = [],
    onChange,
    disabled,
    onOpen,
    required = false,
  }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Use a fullscreen overlay when open instead of document click listener.
    // This avoids ordering issues where document-level handlers close the
    // dropdown immediately after opening.

    // check box controller
    const toggleValue = (id) => {
      const valueIds = Array.isArray(value)
        ? value.map((v) =>
            v && typeof v === "object"
              ? (v.id ?? v.value ?? v.account_id ?? v.division_id ?? v)
              : v,
          )
        : [];
      const exists = valueIds.map(String).includes(String(id));
      if (exists) {
        onChange(
          valueIds
            .filter((v) => String(v) !== String(id))
            .map((v) => (Number.isNaN(Number(v)) ? v : Number(v))),
        );
      } else {
        onChange([...valueIds, Number.isNaN(Number(id)) ? id : Number(id)]);
      }
    };

    // ✅ Convert selected IDs → label text (normalize value objects)
    const valueIds = Array.isArray(value)
      ? value.map((v) =>
          v && typeof v === "object"
            ? (v.id ?? v.value ?? v.account_id ?? v.division_id ?? v)
            : v,
        )
      : [];
    const selectedText = options
      .filter((o) => valueIds.map(String).includes(String(o.id)))
      .map((o) => o.label)
      .join(", ");

    return (
      <div className="relative" ref={ref}>
        {/* overlay to catch outside clicks when dropdown is open */}
        {open && (
          <div
            className="fixed inset-0 z-[10000]"
            onClick={() => setOpen(false)}
          />
        )}
        <label className="text-xs font-medium text-gray-600">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>

        <div
          className={`border rounded px-3 py-2 bg-white cursor-pointer ${
            disabled ? "opacity-60 pointer-events-none" : ""
          }`}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={async (e) => {
            e.stopPropagation();
            if (disabled) return;
            if (!open && typeof onOpen === "function") {
              try {
                await onOpen();
              } catch (err) {
                console.warn("onOpen failed for MultiSelectDropdown", err);
              }
            }
            const next = !open;
            if (next) {
              // delay opening so overlay/document handlers don't close it immediately
              setTimeout(() => setOpen(true), 50);
            } else {
              setOpen(false);
            }
          }}
        >
          {selectedText || "Select"}
        </div>

        {open && !disabled && (
          <div className="absolute z-[10001] mt-1 w-full bg-white border rounded shadow max-h-48 overflow-y-auto">
            {options.map((opt) => (
              <label
                key={opt.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={valueIds.map(String).includes(String(opt.id))}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleValue(opt.id);
                  }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Single-select dropdown that supports multi-line option labels
  function SingleSelectDropdown({
    label,
    options,
    value = "",
    onChange,
    disabled,
    onOpen,
  }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const valueId =
      value && typeof value === "object"
        ? (value.id ??
          value.value ??
          value.account_id ??
          value.office_id ??
          value.employment_office_id ??
          value)
        : value;
    const selected = options.find((o) => String(o.id) === String(valueId));
    const selectedText = selected ? selected.label : "Select";

    // Replace document click listener with overlay approach when open.

    const toggle = async () => {
      if (disabled) return;
      if (!open && typeof onOpen === "function") {
        try {
          await onOpen();
        } catch (err) {
          console.warn("onOpen failed for SingleSelectDropdown", err);
        }
      }
      const next = !open;
      if (next) {
        // delay opening so the click event that triggered this finishes
        setTimeout(() => setOpen(true), 50);
      } else {
        setOpen(false);
      }
    };

    const onChoose = (id) => {
      // Return id as number when possible
      const out = Number.isNaN(Number(id)) ? id : Number(id);
      onChange(out);
      setOpen(false);
    };

    return (
      <div className="relative" ref={ref}>
        {/* overlay to catch outside clicks when open */}
        {open && (
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
        )}
        <div
          className={`w-full border rounded px-3 py-2 cursor-pointer ${disabled ? "opacity-60" : ""}`}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <div className="text-sm text-gray-800 break-words whitespace-normal">
            {selectedText}
          </div>
        </div>

        {open && (
          <div className="absolute z-50 mt-1 w-80 max-w-full bg-white border rounded shadow max-h-60 overflow-y-auto">
            {options.map((opt) => (
              <div
                key={String(opt.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  onChoose(opt.id);
                }}
                className={`px-3 py-2 hover:bg-gray-100 cursor-pointer ${String(opt.id) === String(value) ? "bg-gray-100" : ""}`}
                style={{ whiteSpace: "normal" }}
              >
                <div className="text-sm text-gray-900 break-words">
                  {opt.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const getVal = (k, fallback) =>
    data && data[k] !== undefined && data[k] !== null ? data[k] : fallback;
  const setField = (k, v) => setData((prev) => ({ ...(prev || {}), [k]: v }));

  // Candidate / Field Staff default to the "Cheque - 44hrs" payer (matched by name, since ids
  // differ per database). Outsourced Staff keep the manual payer selection as before.
  const getDefaultPayerId = () => {
    const match = payerOptions.find(
      (p) => String(p.name || "").trim().toLowerCase() === DEFAULT_TEMP_PAYER_NAME.toLowerCase(),
    );
    return match ? String(match.payer_id || match.id) : "";
  };
  const handleTypeChange = (type) => {
    setField("type", type);
    if (DEFAULT_PAYER_TYPES.includes(type)) {
      const defaultPayerId = getDefaultPayerId();
      if (defaultPayerId) setField("payer_id", defaultPayerId);
    }
  };

  // While editing, fill an empty payer with the default for Candidate / Field Staff
  useEffect(() => {
    if (!editing || payerOptions.length === 0) return;
    const type = String(getVal("type", person?.type) || "").trim().toLowerCase();
    const currentPayer = getVal("payer_id", person?.payer || person?.payrollPayer);
    if (!DEFAULT_PAYER_TYPES.includes(type) || currentPayer) return;
    const defaultPayerId = getDefaultPayerId();
    if (defaultPayerId) setField("payer_id", defaultPayerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, payerOptions]);
  const dnpSavedOn = Boolean(Number(snapshot?.global_do_not_place));
  const dnpLocked = dnpSavedOn && !isSuperAdmin;
  const confirmDnpReason = () => {
    const reason = dnpReasonDraft.trim();
    if (!reason) return;
    setDnpReason(reason);
    setField("global_do_not_place", 1);
    setDnpReasonOpen(false);
  };
  // Upload residency document (work_permit or study_permit)
  const handleResidencyFile = async (file) => {
    if (!file) return;
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      return;
    }
    const docType = (
      getVal("residency_status", person?.residency_status) || ""
    ).trim();
    // allow work_permit, study_permit, and refugee to upload
    if (!["work_permit", "study_permit", "refugee"].includes(docType)) {
      console.warn(
        "Residency upload attempted without valid docType:",
        docType,
      );
      return;
    }

    setResidencyUpload({ name: file.name, uploading: true, error: "" });

    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);

    try {
      const resp = await axios.post(
        `${BASE_URL}/api/registration/${person.id}/upload-and-link`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getAuthHeaders(),
          },
        },
      );
      const d = resp.data || {};
      if (!d || !d.file_id) {
        console.error(
          "Residency upload failed: server did not return file id",
          d,
        );
        return;
      }
      // Persist returned file reference in local state so UI can show filename / url
      setData((prev) => ({
        ...(prev || {}),
        residency_document: d.file_id,
        residency_document_name: file.name,
        residency_document_url: d.url || d.s3Key || "",
      }));
      setResidencyUpload({ name: "", uploading: false, error: "" });
      console.log("Residency upload succeeded:", d);
    } catch (err) {
      console.error("Residency upload error", err);
      setResidencyUpload({
        name: file.name,
        uploading: false,
        error: "Upload failed. Please try again." + (err.message ? ` ${err.message}` : ""),
      });
    }
  };

  // Remove/unlink residency file from account and clear local preview
  const removeResidencyFile = async () => {
    const fileId = getVal("residency_document");
    setResidencyUpload({ name: "", uploading: false, error: "" });
    if (!fileId) {
      // nothing to do
      setData((prev) => ({
        ...(prev || {}),
        residency_document: null,
        residency_document_url: "",
        residency_document_name: "",
      }));
      return;
    }

    try {
      const resp = await axios.delete(
        `${BASE_URL}/api/registration/${person.id}/files/${fileId}`,
        { headers: getAuthHeaders() },
      );
      const d = resp.data || {};
      if (d && d.success === false) {
        console.warn("Failed to remove residency file", d);
      }
    } catch (err) {
      console.error("Error removing residency file", err);
    }

    // Clear local state regardless of backend outcome so UI updates immediately
    setData((prev) => ({
      ...(prev || {}),
      residency_document: null,
      residency_document_url: "",
      residency_document_name: "",
    }));
  };

  const handleDownloadResidencyFile = async () => {
    try {
      let url = getVal("residency_document_url", "");
      const fileId = getVal("residency_document", "");
      if (!url && fileId) {
        const resp = await fetch(
          `${BASE_URL}/api/registration/file/${fileId}/url`,
          {
            headers: getAuthHeaders(),
          },
        );
        const j = await resp.json().catch(() => null);
        if (resp.ok && j) {
          url = j.url || j.signedUrl || j.data?.url || "";
        }
      }
      if (!url) {
        showError("Unable to download residency document.", "Download Error");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download =
        getVal("residency_document_name", "") || "residency-document";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Residency download error", err);
      showError("Unable to download residency document.", "Download Error");
    }
  };
  const onCancel = () => {
    setData(snapshot);
    setPhoneError("");
    setEditing(false);
    setDnpReason("");
  };

  const handleStatusChange = async (newStatus) => {
    if (!person?.id) return;
    setDialog(null);
    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ state: newStatus }),
        },
      );
      const resJson = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          (resJson && resJson.message) ||
          `Status update failed (${res.status})`;
        throw new Error(msg);
      }
      showSuccess("Account status updated successfully!");
      // Update local data
      setData((prev) => ({ ...(prev || {}), state: newStatus }));
      onUpdate?.({
        id: person.id,
        account_id: person.id,
        state: newStatus,
        status: newStatus,
      });
      // Reload data to ensure consistency
      const refresh = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}`,
        { headers: getAuthHeaders() },
      );
      const refreshJson = await refresh.json().catch(() => null);
      if (refresh.ok && refreshJson && refreshJson.data) {
        setData(refreshJson.data);
        setSnapshot(refreshJson.data);
        onUpdate?.({
          ...refreshJson.data,
          id: person.id,
          account_id: person.id,
        });
      }
    } catch (e) {
      const msg = e.message || "Failed to update account status";
      showError(msg, "Status Update Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSendActivationEmail = async () => {
    if (!person?.id) return;
    if (isSendingActivation) return;
    const userEmail = getVal("email", person?.email);
    if (!userEmail) {
      showError(
        "User email not found. Cannot send activation email.",
        "Email Error",
      );
      return;
    }

    setIsSendingActivation(true);
    try {
      // Generate a random password
      const generatePassword = (len = 12) => {
        const lower = "abcdefghijklmnopqrstuvwxyz";
        const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const nums = "0123456789";
        const syms = "!@#$%^&*()-_=+[]{}<>?";
        const all = lower + upper + nums + syms;
        let out = "";
        out += lower[Math.floor(Math.random() * lower.length)];
        out += nums[Math.floor(Math.random() * nums.length)];
        out += upper[Math.floor(Math.random() * upper.length)];
        out += syms[Math.floor(Math.random() * syms.length)];
        for (let i = out.length; i < len; i++) {
          out += all[Math.floor(Math.random() * all.length)];
        }
        return out
          .split("")
          .sort(() => Math.random() - 0.5)
          .join("");
      };

      const newPassword = generatePassword(12);

      // Call dedicated activation email endpoint
      const res = await fetch(
        `${BASE_URL}/api/personnel/${person.id}/send-activation-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({
            password: newPassword,
            send_email: true,
            force_change_on_next_signin: true,
            auto_generated: true,
          }),
        },
      );

      const resJson = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          (resJson && resJson.message) ||
          `Failed to send activation email (${res.status})`;
        throw new Error(msg);
      }

      showSuccess(`Activation email sent successfully to ${userEmail}!`);
      // Update activation email sent date
      const sentDate = new Date().toISOString();
      setActivationEmailSentDate(sentDate);
      // Update local data
      setData((prev) => ({
        ...(prev || {}),
        activation_email_sent_date: sentDate,
      }));
    } catch (e) {
      const msg = e.message || "Failed to send activation email";
      showError(msg, "Email Send Failed");
    } finally {
      setIsSendingActivation(false);
    }
  };

  const onSave = async () => {
    if (!person?.id || !data) return;
    setSaving(true);

    try {
      // Validate required fields: Type, First Name, Last Name, Email, Payroll Payer
      const typeVal = String(getVal("type", person?.type) || "").trim();
      const firstNameVal = String(
        getVal("first_name", person?.firstName || person?.first_name) || "",
      ).trim();
      const lastNameVal = String(
        getVal("last_name", person?.lastName || person?.last_name) || "",
      ).trim();
      const emailVal = String(
        getVal("email", person?.email) || "",
      ).trim();
      const rawPayer = getVal("payer_id", person?.payer || person?.payrollPayer);
      const payerVal = String(
        typeof rawPayer === "object"
          ? rawPayer?.payer_id || rawPayer?.id || ""
          : rawPayer || "",
      ).trim();

      const missingFields = [];
      if (!typeVal) missingFields.push("Type");
      if (!firstNameVal) missingFields.push("First Name");
      if (!lastNameVal) missingFields.push("Last Name");
      if (!emailVal) missingFields.push("Email");
      if (!payerVal) missingFields.push("Payroll Payer");

      if (missingFields.length > 0) {
        showError(
          `Please fill in all required fields: ${missingFields.join(", ")}`,
          "Validation Error",
        );
        setSaving(false);
        return;
      }
      // If residency requires a document, ensure one was uploaded via the upload-and-link flow.
      const residency = (
        data.residency_status ||
        person?.residencyStatus ||
        ""
      ).trim();
      const hasResidencyFile = Boolean(
        (data &&
          (data.residency_document ||
            data.residency_document_url ||
            data.residency_document_name)) ||
        person?.residency_document ||
        person?.residency_document_url ||
        person?.residency_document_name,
      );
      if (
        ["work_permit", "study_permit", "refugee"].includes(residency) &&
        !hasResidencyFile
      ) {
        const label =
          residency === "work_permit"
            ? "Work Permit"
            : residency === "study_permit"
              ? "Study Permit"
              : "Refugee document";
        showError(
          `Please upload the required ${label} before saving.`,
          "Missing Residency Document",
        );
        setSaving(false);
        return;
      }
      const divisionIds = Array.isArray(data.division_ids)
        ? data.division_ids
        : [];
      const officeIds = Array.isArray(data.office_ids) ? data.office_ids : [];

      // Build payload from current `data` state to ensure all edited fields are persisted.
      const allowedKeys = [
        "first_name",
        "last_name",
        "preferred_name",
        "middle_name",
        "email",
        "profile_picture",
        "gender",
        "date_of_birth",
        "phone_mobile",
        "phone",
        "personal_number",
        "personal_email",
        "emergency_name",
        "emergency_phone",
        "type",
        "start_date",
        "hire_date",
        "availability_state",
        "state",
        "hst",
        "hst_number",
        "cheque_method",
        "payout_account_id",
        "payer_id",
        "division_id",
        "office_id",
        "max_hours",
        "schedule_note",
        "recruiter_account_id",
        "applicable_tax_form",
        "line1",
        "line2",
        "city",
        "province",
        "postal_code",
        "country",
        "global_do_not_place",
        "payment_delivery_method",
        "sin",
        "residency_status",
        // include multi-select fields so selections persist
        "division_ids",
        "office_ids",
      ];
      const payload = {};
      for (const k of allowedKeys) {
        if (data[k] !== undefined) payload[k] = data[k];
      }
      // Ensure arrays are sent for divisions/offices (server expects arrays named division_ids/office_ids)
      payload.division_ids = divisionIds;
      payload.office_ids = officeIds;
      if (dnpReason && Number(payload.global_do_not_place)) {
        payload.dnp_reason = dnpReason;
      }
      // prefer explicit start_date if absent but hire_date present
      if (!payload.start_date && data.hire_date)
        payload.start_date = data.hire_date;
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify(payload),
        },
      );
      console.log("Saving profile payload:", payload);
      const resJson = await res.json().catch(() => null);
      console.log("Save response:", res.status, resJson);
      if (!res.ok) {
        const msg =
          (resJson && resJson.message) || `Save failed (${res.status})`;
        if (resJson?.duplicate && resJson?.field === "phone_mobile") {
          setPhoneError(msg);
          showError(
            `${msg}. Please enter a different mobile number.`,
            "Duplicate Phone Number",
          );
          return;
        }
        throw new Error(msg);
      }

      setPhoneError("");
      showSuccess("Profile updated successfully!");
      setDnpReason("");

      // Re-fetch account to ensure server-persisted values (division_ids/office_ids) are loaded
      try {
        const refresh = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}`,
          { headers: getAuthHeaders() },
        );
        const refreshJson = await refresh.json().catch(() => null);
        const raw = refreshJson && refreshJson.data ? refreshJson.data : {};
        const normalized = { ...raw };
        if (!String(normalized.line1 || "").trim()) {
          normalized.line1 = normalizeAddressLine1(raw);
        }
        if (Array.isArray(raw.division_ids)) {
          normalized.division_ids = raw.division_ids;
        } else if (Array.isArray(raw.divisions)) {
          normalized.division_ids = raw.divisions.map((d) => d.division_id);
        } else if (raw.division_id) {
          normalized.division_ids = [raw.division_id];
        } else {
          normalized.division_ids = [];
        }
        if (Array.isArray(raw.office_ids)) {
          normalized.office_ids = raw.office_ids;
        } else if (Array.isArray(raw.offices)) {
          normalized.office_ids = raw.offices.map((o) => o.office_id);
        } else if (raw.office_id) {
          normalized.office_ids = [raw.office_id];
        } else {
          normalized.office_ids = [];
        }
        // Map residency value from server tokens to UI tokens after refresh
        try {
          const serverVal =
            normalized.residency_status || normalized.residencyStatus || null;
          if (serverVal !== undefined && serverVal !== null) {
            normalized.residency_status = mapServerResidencyToUI(serverVal);
          }
        } catch (e) {
          console.warn("Residency mapping failed during refresh", e);
        }
        // Map schedule_note from server; strip any appended office_ids marker
        try {
          const rawNote = typeof raw.note === "string" ? raw.note : "";
          const cleanedNote = rawNote
            .replace(/office_ids:?\s*[^;\n]+/i, "")
            .trim();
          normalized.schedule_note = raw.schedule_note ?? (cleanedNote || "");
        } catch (e) {
          console.warn("Schedule note mapping failed during refresh", e);
        }
        // Map payment_delivery_method / cheque_method to UI labels so radios reflect change immediately
        try {
          const _rawPayment = String(
            (normalized.payment_delivery_method ?? normalized.cheque_method) ||
              "",
          ).trim();
          const lp = _rawPayment.toLowerCase();
          if (
            lp.includes("pickup") ||
            lp === "delivery_pickup" ||
            lp === "delivery-pickup"
          ) {
            normalized.payment_delivery_method = "Pickup";
          } else if (
            lp.includes("mail") ||
            lp === "delivery_mail" ||
            lp === "delivery-mail"
          ) {
            normalized.payment_delivery_method = "Mail";
          } else if (_rawPayment) {
            normalized.payment_delivery_method = _rawPayment;
          } else {
            normalized.payment_delivery_method = "";
          }
        } catch (e) {
          console.warn("Payment mapping failed during refresh", e);
        }
        setData(normalized);
        setSnapshot(normalized);
        console.log("Refreshed data after save:", normalized);
        onUpdate?.({
          ...normalized,
          id: person.id,
          account_id: person.id,
          avatar_url: normalized.avatar_url || normalized.profile_picture || data.avatar_url,
          profile_picture: normalized.profile_picture || normalized.avatar_url || data.profile_picture,
        });

        // Try to resolve any residency file id returned by the API (so UI shows filename/url)
        (async () => {
          try {
            let fileId =
              normalized.work_permit_file_id ||
              normalized.study_permit_file_id ||
              normalized.refugee_file_id ||
              null;
            if (!fileId && typeof normalized.note === "string") {
              const mRef = normalized.note.match(
                /refugee_file_id\s*:\s*(\d+)/i,
              );
              const mWork = normalized.note.match(
                /work_permit_file_id\s*:\s*(\d+)/i,
              );
              const mStudy = normalized.note.match(
                /study_permit_file_id\s*:\s*(\d+)/i,
              );
              if (mRef) fileId = mRef[1];
              else if (mWork) fileId = mWork[1];
              else if (mStudy) fileId = mStudy[1];
            }
            if (!fileId) return;
            const resp = await fetch(
              `${BASE_URL}/api/registration/file/${fileId}/url`,
              { headers: getAuthHeaders() },
            );
            const j = await resp.json().catch(() => null);
            if (!resp.ok || !j) return;
            const url = j.url || j.signedUrl || j.data?.url || "";
            const name =
              j.name || j.filename || (url ? url.split("/").pop() : "") || "";
            if (url) {
              setData((prev) => ({
                ...(prev || {}),
                residency_document: fileId,
                residency_document_url: url,
                residency_document_name: name,
              }));
              setSnapshot((prev) => ({
                ...(prev || {}),
                residency_document: fileId,
                residency_document_url: url,
                residency_document_name: name,
              }));
            }
          } catch (e) {
            console.warn("Failed to resolve residency file after save", e);
          }
        })();
      } catch (e) {
        console.warn("Failed to refresh after save", e);
      }

      setEditing(false);
    } catch (e) {
      console.error(e);
      showError(e?.message || "Save failed", "Save Failed");
    } finally {
      setSaving(false);
    }
  };

  const onAvatarSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !person?.id) return;

    console.log("Avatar upload started:", {
      fileName: file.name,
      fileSize: file.size,
      personId: person.id,
    });

    try {
      const form = new FormData();
      form.append("file", file);
      const uploadHeaders = { ...getAuthHeaders() };
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/avatar`,
        { method: "POST", headers: uploadHeaders, body: form },
      );
      const json = await res.json();

      console.log("Avatar upload response:", json);

      if (json.success && json.url) {
        console.log("Upload successful, URL:", json.url);

        const timestamp = Date.now();
        const cacheBustedUrl = json.url.includes("?")
          ? `${json.url}&t=${timestamp}`
          : `${json.url}?t=${timestamp}`;

        // Immediately update the local state with the new profile picture URL
        setData((prev) => ({
          ...prev,
          profile_picture: cacheBustedUrl,
          avatar_url: cacheBustedUrl,
        }));

        onUpdate?.({
          id: person.id,
          account_id: person.id,
          profile_picture: cacheBustedUrl,
          avatar_url: cacheBustedUrl,
        });

        // Also refresh from backend to get any other updated data
        const refreshRes = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}`,
          {
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
          },
        );
        const refreshData = await refreshRes.json();
        console.log("Refresh response:", refreshData);

        if (refreshData.data) {
          const freshBackendAvatar =
            refreshData.data.avatar_url ||
            refreshData.data.profile_picture ||
            json.url;
          const freshBackendUrl = freshBackendAvatar
            ? freshBackendAvatar.includes("?")
              ? `${freshBackendAvatar}&t=${timestamp}`
              : `${freshBackendAvatar}?t=${timestamp}`
            : cacheBustedUrl;

          // Merge backend data with our profile picture URL and map residency
          const freshData = refreshData.data;
          let mappedResidencyStatus = freshData.residency_status || freshData.residencyStatus || null;
          try {
            if (mappedResidencyStatus !== undefined && mappedResidencyStatus !== null) {
              mappedResidencyStatus = mapServerResidencyToUI(mappedResidencyStatus);
            }
          } catch (e) {
            console.warn("Residency mapping failed during avatar refresh", e);
          }

          setData((prev) => ({
            ...freshData,
            profile_picture: freshBackendUrl,
            avatar_url: freshBackendUrl,
            residency_status: mappedResidencyStatus ?? prev?.residency_status,
          }));

          onUpdate?.({
            ...freshData,
            id: person.id,
            account_id: person.id,
            profile_picture: freshBackendUrl,
            avatar_url: freshBackendUrl,
          });

          // schedule resolving any linked residency file for immediate UI preview
          (async () => {
            try {
              let fileId =
                freshData.work_permit_file_id ||
                freshData.study_permit_file_id ||
                freshData.refugee_file_id ||
                null;
              if (!fileId && typeof freshData.note === "string") {
                const mRef = freshData.note.match(
                  /refugee_file_id\s*:\s*(\d+)/i,
                );
                const mWork = freshData.note.match(
                  /work_permit_file_id\s*:\s*(\d+)/i,
                );
                const mStudy = freshData.note.match(
                  /study_permit_file_id\s*:\s*(\d+)/i,
                );
                if (mRef) fileId = mRef[1];
                else if (mWork) fileId = mWork[1];
                else if (mStudy) fileId = mStudy[1];
              }
              if (!fileId) return;
              const resp = await fetch(
                `${BASE_URL}/api/registration/file/${fileId}/url`,
                { headers: getAuthHeaders() },
              );
              const j = await resp.json().catch(() => null);
              if (!resp.ok || !j) return;
              const url = j.url || j.signedUrl || j.data?.url || "";
              const name =
                j.name ||
                j.filename ||
                (url ? url.split("/").pop() : "") ||
                "";
              if (url) {
                setData((prev) => ({
                  ...(prev || {}),
                  residency_document: fileId,
                  residency_document_url: url,
                  residency_document_name: name,
                }));
              }
            } catch (e) {
              console.warn(
                "Failed to resolve residency file during avatar refresh",
                e,
              );
            }
          })();
          console.log("Final data state updated");
        }
      } else {
        console.error("Avatar upload failed:", json);
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Reliability + Shifts Worked cards. Desktop shows them atop the right
  // column; on mobile that column stacks below the whole form, so they are
  // also rendered at the top of the page (side by side, like the old system).
  const renderStatCards = (className) => (
    <div className={className}>
      <div className="bg-red-600 text-white rounded-lg p-4 shadow-sm">
        <div className="text-sm">Reliability</div>
        <div className="text-2xl font-semibold">
          {(() => {
            const rawRel = getVal("reliability", person?.reliability);
            const rawShifts = getVal("shiftsWorked", person?.shiftsWorked);
            const relNum =
              typeof rawRel === "number"
                ? rawRel
                : rawRel != null &&
                    rawRel !== "" &&
                    !Number.isNaN(Number(rawRel))
                  ? Number(rawRel)
                  : null;
            if (relNum != null && Number.isFinite(relNum)) {
              return `${relNum}%`;
            }
            const shiftsNum =
              rawShifts === "" || rawShifts == null ? null : Number(rawShifts);
            if (shiftsNum === 0) return "0%";
            return "—";
          })()}
        </div>
      </div>
      <div className="bg-red-600 text-white rounded-lg p-4 shadow-sm">
        <div className="text-sm">Shifts Worked</div>
        <div className="text-2xl font-semibold">
          {getVal("shiftsWorked", person?.shiftsWorked) ?? "—"}
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {renderStatCards("lg:hidden grid grid-cols-2 gap-3")}
        <div className="flex items-center justify-start">
          <div className="flex gap-2">
            {!editing && (
              <button
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setEditing(true)}
                disabled={loading}
              >
                Edit
              </button>
            )}
            {editing && (
              <>
                <button
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  onClick={onCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-2 bg-red-600 text-white rounded"
                  onClick={onSave}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
        <Section title="General Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <input
                  id="doNotPlace"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={Boolean(Number(getVal("global_do_not_place", 0)))}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setField("global_do_not_place", 0);
                      setDnpReason("");
                    } else if (dnpSavedOn) {
                      // Re-ticking a profile that is already DNP: no change to save.
                      setField("global_do_not_place", 1);
                    } else {
                      setDnpReasonDraft("");
                      setDnpReasonOpen(true);
                    }
                  }}
                  disabled={!editing || dnpLocked}
                  title={dnpLocked ? "Only a Super Admin can remove Do Not Place" : undefined}
                />
                <label htmlFor="doNotPlace" className="text-sm text-gray-700">
                  Do not place
                </label>
              </div>
              {editing && dnpLocked ? (
                <div className="text-xs text-gray-500">
                  Only a Super Admin can remove Do Not Place.
                </div>
              ) : null}
              {editing && dnpReason ? (
                <div className="text-xs text-gray-500 break-words">
                  Reason: {dnpReason}
                </div>
              ) : null}
              {dnpReasonOpen && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) setDnpReasonOpen(false);
                  }}
                >
                  <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Do Not Place
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Why is this profile being set to Do Not Place? This reason
                      will be saved in the Notes tab.
                    </p>
                    <label
                      htmlFor="dnpReason"
                      className="text-xs font-medium text-gray-600"
                    >
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="dnpReason"
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      rows={4}
                      value={dnpReasonDraft}
                      onChange={(e) => setDnpReasonDraft(e.target.value)}
                      autoFocus
                    />
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setDnpReasonOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        onClick={confirmDnpReason}
                        disabled={!dnpReasonDraft.trim()}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Type <span className="text-red-500">*</span>
              </div>
              <select
                className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
                value={getVal("type", person?.type) || ""}
                onChange={(e) => handleTypeChange(e.target.value)}
                disabled={!editing}
              >
                <option value="">Select type</option>
                <option value="candidate">Candidate</option>
                <option value="fieldstaff">Field Staff</option>
                <option value="outsourcedstaff">Outsourced Staff</option>
              </select>
            </div>
            <div />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="First Name"
                value={getVal("first_name", person?.firstName) || ""}
                onChange={(v) => setField("first_name", v)}
                disabled={!editing}
                required
              />
              <LabeledInput
                label="Last Name"
                value={getVal("last_name", person?.lastName) || ""}
                onChange={(v) => setField("last_name", v)}
                disabled={!editing}
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="Middle Name"
                value={getVal("middle_name", person?.middleName) || ""}
                onChange={(v) => setField("middle_name", v)}
                disabled={!editing}
              />
              <LabeledInput
                label="Preferred Name"
                value={getVal("preferred_name", person?.preferredName) || ""}
                onChange={(v) => setField("preferred_name", v)}
                hint="Only if different from First Name"
                disabled={!editing}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="Email"
                value={getVal("email", person?.email) || ""}
                onChange={(v) => setField("email", v)}
                disabled={!editing}
                required
              />
              <div className="flex flex-col gap-1">
                <div className="text-xs font-medium text-gray-600">Gender</div>
                <select
                  className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
                  value={getVal("gender", person?.gender) || ""}
                  onChange={(e) => setField("gender", e.target.value)}
                  disabled={!editing}
                >
                  <option value="">select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {/* <LabeledInput
                label="Gender"
                value={getVal("gender", person?.gender) || ""}
                onChange={(v) => setField("gender", v)}
                disabled={!editing}
              /> */}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="Date of Birth"
                type="date"
                value={getVal("date_of_birth", person?.dob) || ""}
                onChange={(v) => setField("date_of_birth", v)}
                disabled={!editing}
                min="1900-01-01"
              />
              <div></div>
            </div>
            <div className="space-y-3 sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700">
                Profile Picture
              </label>
              <div className="flex items-center gap-6">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-100 to-gray-200 flex items-center justify-center overflow-hidden shadow-lg border-4 border-white">
                    {(() => {
                      const avatarUrl =
                        getVal("profile_picture") ||
                        getVal("avatar_url") ||
                        person?.profile_picture ||
                        person?.avatar_url;
                      const resolvedAvatarUrl = resolveBackendUrl(avatarUrl);
                      return (
                        <ProfileAvatarDisplay
                          url={resolvedAvatarUrl}
                          firstName={getVal("first_name", "")}
                          lastName={getVal("last_name", "")}
                          isOffice={false}
                        />
                      );
                    })()}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-700 transition-all duration-200 disabled:opacity-30 hover:scale-110 z-50"
                    title="Change profile picture"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0118.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </button>
                  <ProfilePhotoInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onAvatarSelected}
                    className="hidden"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          </div>
        </Section>
        <Section title="Contact Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Mobile number on record with Talent <span className="text-red-500">*</span>
              </div>
              <div
                className={`border rounded px-3 py-2 bg-white text-gray-900 ${phoneError ? "border-red-500" : "border-gray-200"}`}
              >
                <PhoneInput
                  className="PhoneInputInput"
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  value={
                    getVal("phone_mobile", person?.phone || person?.mobile) ||
                    ""
                  }
                  onChange={(v) => {
                    setField("phone_mobile", v);
                    if (phoneError) setPhoneError("");
                  }}
                  disabled={!editing}
                />
              </div>
              {phoneError && (
                <div className="text-xs text-red-600">{phoneError}</div>
              )}
            </div>
            <div />
            <div className="sm:col-span-2 flex flex-col gap-2">
              <div className="text-xs font-medium text-gray-600">
                Address <span className="text-red-500">*</span>
              </div>
              <AddressAutocomplete
                value={buildAddressAutocompleteValue(data || {})}
                onChange={(v) => applyAddressToForm(setField, v)}
                disabled={!editing}
              />
            </div>
            <LabeledInput
              label="Emergency Contact Name"
              value={getVal("emergency_name", person?.emergencyName) || ""}
              onChange={(v) => setField("emergency_name", v)}
              disabled={!editing}
              required
            />
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Emergency Contact Number <span className="text-red-500">*</span>
              </div>
              <div className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900">
                <PhoneInput
                  className="PhoneInputInput"
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  value={
                    getVal("emergency_phone", person?.emergencyPhone) || ""
                  }
                  onChange={(v) => setField("emergency_phone", v)}
                  disabled={!editing}
                />
              </div>
            </div>
          </div>
        </Section>
        {/* Employment Information Section (restored) */}
        <Section title="Employment Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Divisions
              </label>
              <ModernMultiSelect
                options={divisionOptions}
                value={
                  getVal(
                    "division_ids",
                    person?.division_ids || person?.divisions,
                  ) || []
                }
                onChange={(selectedIds) =>
                  setField("division_ids", selectedIds)
                }
                onOpen={loadDivisions}
                disabled={!editing}
                placeholder="Select divisions..."
                displayKey="name"
                valueKey="division_id"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Office <span className="text-red-500">*</span>
              </label>
              <SingleSelectDropdown
                label="Employment Office"
                options={officeOptions.map((o) => ({
                  id: o.office_id || o.id,
                  label: o.name || o.label || String(o),
                }))}
                value={getVal(
                  "employment_office_id",
                  (getVal("office_ids", []) || [])[0] ||
                    person?.employment_office_id ||
                    person?.office_id ||
                    (person?.office_ids && person.office_ids[0]) ||
                    "",
                )}
                onChange={(v) => {
                  setField("employment_office_id", v);
                  setField("office_ids", [v]);
                  // Sync with division_ids so it shows up in list view and persists to employee_divisions table
                  const currentDivs = getVal("division_ids") || [];
                  if (v && !currentDivs.includes(v)) {
                    setField("division_ids", [...currentDivs, v]);
                  }
                }}
                disabled={!editing}
                onOpen={loadOffices}
              />
            </div>
            <LabeledInput
              label="Max Hours per Week"
              type="number"
              value={getVal("max_hours", person?.max_hours) || ""}
              onChange={(v) => setField("max_hours", v)}
              disabled={!editing}
            />
            <LabeledInput
              label="Start Date"
              type="date"
              value={getVal("start_date", person?.start_date) || ""}
              onChange={(v) => setField("start_date", v)}
              disabled={!editing}
            />
            <LabeledInput
              label="Schedule Note"
              value={getVal("schedule_note", person?.schedule_note) || ""}
              onChange={(v) => setField("schedule_note", v)}
              disabled={!editing}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recruiter
              </label>
              <SingleSelectDropdown
                label="Recruiter"
                options={recruiterOptions}
                value={getVal(
                  "recruiter_account_id",
                  data?.recruiter_account_id ||
                    person?.recruiter_account_id ||
                    person?.recruiterId ||
                    "",
                )}
                onChange={(v) => setField("recruiter_account_id", v)}
                disabled={!editing}
                onOpen={loadRecruiters}
              />
            </div>
          </div>
        </Section>
        <Section title="Payroll Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Residency Status <span className="text-red-500">*</span>
              </label>
              <select
                value={
                  getVal("residency_status", person?.residencyStatus) || ""
                }
                onChange={(e) => setField("residency_status", e.target.value)}
                disabled={!editing}
                className={`w-full border rounded px-3 py-2 ${!editing ? "opacity-80" : ""}`}
              >
                <option value="">Select</option>
                <option value="canadian">
                  Canadian Citizen / Permanent Resident
                </option>
                <option value="work_permit">Work permit</option>
                <option value="study_permit">Study permit</option>
                <option value="refugee">Refugee</option>
              </select>
              {/* Residency document upload for Work / Study Permit and note for Refugee */}
              {editing &&
                ["work_permit", "study_permit", "refugee"].includes(
                  (
                    getVal("residency_status", person?.residency_status) || ""
                  ).trim(),
                ) && (
                  <div className="mt-3">
                    <div
                      className="residency-upload-box border border-dashed rounded p-4 text-center"
                      onDrop={(e) => {
                        e.preventDefault();
                        const f =
                          e.dataTransfer.files && e.dataTransfer.files[0];
                        if (f) handleResidencyFile(f);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      style={{
                        minHeight: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 8,
                        zIndex: 11000,
                        pointerEvents: "auto",
                      }}
                    >
                      <div className="text-sm text-gray-600">
                        Drag & Drop your file or use the button below
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        {residencyUpload.name && (
                          <div className="text-amber-700">
                            Selected: {residencyUpload.name}
                            {residencyUpload.uploading
                              ? " (uploading...)"
                              : ""}
                          </div>
                        )}
                        {residencyUpload.error && (
                          <div className="text-red-600">
                            {residencyUpload.error}
                          </div>
                        )}
                        {data?.residency_document_url ||
                        data?.residency_document ? (
                          <div className="inline-flex items-center gap-2">
                            {data?.residency_document_url ? (
                              <a
                                href={data?.residency_document_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 underline break-words max-w-xs"
                              >
                                {data?.residency_document_name ||
                                  "Open document"}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-700 break-words max-w-xs">
                                {data?.residency_document_name ||
                                  "Uploaded document"}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={handleDownloadResidencyFile}
                              className="text-sm text-blue-600 underline"
                              title="Download document"
                            >
                              Download
                            </button>
                            {data?.residency_document && (
                              <button
                                type="button"
                                onClick={removeResidencyFile}
                                title="Remove file"
                                className="text-gray-500 hover:text-red-600 ml-1 px-2 py-1 text-xs rounded"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : !residencyUpload.name ? (
                          "No file selected"
                        ) : null}
                      </div>
                      <input
                        id="personnelResidencyFile"
                        type="file"
                        accept={STAFF_DOCUMENT_ACCEPT}
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) handleResidencyFile(f);
                        }}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <label
                          htmlFor="personnelResidencyFile"
                          onClick={(ev) => {
                            ev.preventDefault();
                            try {
                              fileInputRef?.current?.click();
                            } catch (__) {}
                          }}
                          className="inline-block px-4 py-2 bg-red-600 text-white rounded-3xl cursor-pointer"
                        >
                          Upload
                        </label>
                      </div>
                    </div>
                  </div>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payroll Payer <span className="text-red-500">*</span>
              </label>
              <select
                value={String(
                  getVal("payer_id", person?.payer || person?.payrollPayer) ||
                    "",
                )}
                onChange={(e) => setField("payer_id", e.target.value)}
                disabled={!editing}
                className={`w-full border rounded px-3 py-2 ${!editing ? "opacity-80" : ""}`}
              >
                <option value="">Select payer</option>
                {payerOptions.map((p) => (
                  <option
                    key={String(p.payer_id || p.id)}
                    value={String(p.payer_id || p.id)}
                  >
                    {p.name || p.label || String(p)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payout Staff
              </label>
              <SingleSelectDropdown
                label="Payout Staff"
                options={payoutOptions.map((a) => ({
                  id: a.account_id || a.id,
                  label:
                    a.name ||
                    `${a.first_name || ""} ${a.last_name || ""}`.trim() ||
                    `#${a.account_id || a.id}`,
                }))}
                value={String(
                  getVal(
                    "payout_account_id",
                    person?.payout_account_id ||
                      person?.payoutStaff ||
                      person?.payoutStaffName,
                  ) || "",
                )}
                onChange={(v) => setField("payout_account_id", v)}
                disabled={!editing}
                onOpen={loadPayoutStaff}
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Payment Delivery Method
              </div>
              <div className="flex items-center gap-4">
                {["Pickup", "Mail"].map((opt) => (
                  <label
                    key={opt}
                    className={`inline-flex items-center gap-2 ${
                      !editing ? "opacity-60" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_delivery_method"
                      className="h-4 w-4"
                      checked={
                        (
                          getVal(
                            "payment_delivery_method",
                            person?.payment_delivery_method ||
                              person?.cheque_method,
                          ) || ""
                        ).toLowerCase() === opt.toLowerCase()
                      }
                      onChange={() => setField("payment_delivery_method", opt)}
                      disabled={!editing}
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            {editing ? (
              <LabeledInput
                label="Social Insurance Number"
                value={getVal("sin", person?.sin) || ""}
                onChange={(v) => setField("sin", v)}
                required
              />
            ) : (
              <Field
                label="Social Insurance Number *"
                value={getVal("sin", person?.sin) || ""}
              />
            )}
            {editing ? (
              <LabeledInput
                label="HST Number"
                value={getVal("hst_number", person?.hstNumber) || ""}
                onChange={(v) => setField("hst_number", v)}
              />
            ) : (
              <Field
                label="HST Number"
                value={getVal("hst_number", person?.hstNumber) || ""}
              />
            )}
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Applicable Tax Form
              </div>
              <div className="flex items-center gap-4">
                {["T4", "T4A", "None"].map((opt) => (
                  <label
                    key={opt}
                    className={`inline-flex items-center gap-2 ${
                      !editing ? "opacity-60" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={
                        (getVal("applicable_tax_form", person?.taxForm) ||
                          "") === opt
                      }
                      onChange={() => setField("applicable_tax_form", opt)}
                      disabled={!editing}
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      <div className="space-y-6 lg:sticky lg:top-0 self-start">
        {renderStatCards("hidden lg:block space-y-3")}
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-sm font-semibold text-gray-700">
            Account Status
          </div>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label="Created"
              value={
                formatAccountDateTime(
                  getVal("create_date", person?.created || person?.createdAt),
                ) || ""
              }
            />
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Account Status
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px]">
                  {(() => {
                    const state = (
                      getVal("state", person?.status) || ""
                    ).toLowerCase();
                    if (
                      state === "pending" ||
                      state === "pendingactivation" ||
                      state === "pending_activation" ||
                      state === "pending activation"
                    ) {
                      return "Pending Activation";
                    }
                    if (state === "active") {
                      return "Active";
                    }
                    if (state === "deleted") {
                      return "Deleted";
                    }
                    return getVal("state", person?.status) || "";
                  })()}
                </div>
                <div className="relative" ref={statusMenuRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusMenuOpen(!statusMenuOpen);
                    }}
                    className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                    title="Account Status Options"
                  >
                    <FaEllipsisV className="text-gray-600" />
                  </button>
                  {statusMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setStatusMenuOpen(false)}
                      />
                      <div
                        className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                        style={
                          Object.keys(dropdownStyle).length > 0
                            ? dropdownStyle
                            : {
                                position: "fixed",
                                top: statusMenuRef.current
                                  ? `${statusMenuRef.current.getBoundingClientRect().bottom + 4}px`
                                  : "auto",
                                right: statusMenuRef.current
                                  ? `${window.innerWidth - statusMenuRef.current.getBoundingClientRect().right}px`
                                  : "16px",
                              }
                        }
                      >
                        {(
                          getVal("state", person?.status) || ""
                        ).toLowerCase() === "active" ? (
                          <div
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                            onClick={async (e) => {
                              e.stopPropagation();
                              setStatusMenuOpen(false);
                              setDialog({
                                title: "Confirm",
                                message:
                                  "Are you sure you would like to delete this account?",
                                onConfirm: () => handleStatusChange("deleted"),
                                onCancel: () => setDialog(null),
                              });
                            }}
                          >
                            <div className="text-sm font-medium text-red-600">
                              Delete Account
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Remove the account from the system, the person
                              will not able to Sign-in
                            </div>
                          </div>
                        ) : (
                            getVal("state", person?.status) || ""
                          ).toLowerCase() === "deleted" ? (
                          <>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to activate this account?",
                                  onConfirm: () => handleStatusChange("active"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-green-600">
                                Active
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to set this account to pending activation?",
                                  onConfirm: () =>
                                    handleStatusChange("pending activation"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-blue-600">
                                Pending Activation
                              </div>
                            </div>
                          </>
                        ) : (
                            getVal("state", person?.status) || ""
                          ).toLowerCase() === "pending" ||
                          (
                            getVal("state", person?.status) || ""
                          ).toLowerCase() === "pendingactivation" ||
                          (
                            getVal("state", person?.status) || ""
                          ).toLowerCase() === "pending_activation" ||
                          (
                            getVal("state", person?.status) || ""
                          ).toLowerCase() === "pending activation" ? (
                          <>
                            <div
                              className={`px-4 py-3 border-b border-gray-100 ${
                                isSendingActivation
                                  ? "cursor-not-allowed bg-gray-50"
                                  : "hover:bg-gray-50 cursor-pointer"
                              }`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isSendingActivation) return;
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to send an activation email?",
                                  onConfirm: () => {
                                    setDialog(null);
                                    handleSendActivationEmail();
                                  },
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-blue-600">
                                {isSendingActivation
                                  ? "Sending Activation Email..."
                                  : activationEmailSentDate
                                    ? "Resend Activation Email"
                                    : "Send Activation Email"}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                An email will be sent to{" "}
                                {getVal("email", person?.email) || "the user"}{" "}
                                to setup their sign-in password
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to activate this account?",
                                  onConfirm: () => handleStatusChange("active"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-green-600">
                                Activate Account
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Account will be activated without notifying the
                                person
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to delete this account?",
                                  onConfirm: () =>
                                    handleStatusChange("deleted"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-red-600">
                                Delete Account
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Remove the account from the system, the person
                                will not able to Sign-in
                              </div>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {(getVal("state", person?.status) || "").toLowerCase() ===
                "pending" ||
              (getVal("state", person?.status) || "").toLowerCase() ===
                "pendingactivation" ||
              (getVal("state", person?.status) || "").toLowerCase() ===
                "pending_activation" ? (
                <div className="text-xs text-gray-500 mt-1">
                  {activationEmailSentDate
                    ? `Activation email sent ${formatAppDate(activationEmailSentDate, { emptyValue: "" })}`
                    : "Notification Not Sent"}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-sm font-semibold text-gray-700">
            Sign-in & Security
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">Email</div>
              <div className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px] overflow-x-auto whitespace-nowrap">
                {getVal("email", person?.email) || ""}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-3">
                <Field label="Password" value="••••••••" />
              </div>
              <button
                className="px-3 py-2 bg-red-600 text-white rounded"
                onClick={() => setResetModalOpen(true)}
              >
                Reset Password
              </button>
            </div>
            <Field
              label="Last Sign-In"
              value={formatAccountDateTime(
                getVal(
                  "last_sign_in",
                  person?.signin_date ?? person?.lastSignIn ?? null,
                ),
                { emptyValue: "Never" },
              )}
            />
          </div>
        </div>
      </div>
      {resetModalOpen && (
        <ResetPasswordModal
          person={person}
          email={getVal("email", person?.email)}
          isOpen={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
        />
      )}
      {dialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{dialog.title}</h3>
            <p className="text-gray-700 mb-6">{dialog.message}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  if (dialog.onCancel) dialog.onCancel();
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                CANCEL
              </button>
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResetPasswordModal({ person, isOpen, onClose, email }) {
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [generated, setGenerated] = useState("");
  const [custom, setCustom] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [forceChange, setForceChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSendEmail(true);
      setAutoGenerate(true);
      setForceChange(true);
      const pwd = generatePassword(12);
      setGenerated(pwd);
      setCustom("");
      setShowPassword(false);
      setError("");
    }
  }, [isOpen, person?.id]);

  // Regenerate password whenever autoGenerate is toggled on (per user request)
  useEffect(() => {
    if (isOpen && autoGenerate) {
      setGenerated(generatePassword(12));
    }
    // when switching from true->false->true repeatedly it will regenerate each time true
  }, [autoGenerate, isOpen]);

  function generatePassword(len = 12) {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const syms = "!@#$%^&*()-_=+[]{}<>?";
    const all = lower + upper + nums + syms;
    let out = "";
    // ensure at least one of each category
    out += lower[Math.floor(Math.random() * lower.length)];
    out += nums[Math.floor(Math.random() * nums.length)];
    out += upper[Math.floor(Math.random() * upper.length)];
    out += syms[Math.floor(Math.random() * syms.length)];
    for (let i = out.length; i < len; i++)
      out += all[Math.floor(Math.random() * all.length)];
    // shuffle
    return out
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  }

  function validatePassword(p) {
    const errs = [];
    if (!p || p.length < 8) errs.push("At least 8 characters");
    if (!/[a-z]/.test(p)) errs.push("One lowercase letter");
    if (!/[A-Z]/.test(p)) errs.push("One uppercase letter");
    if (!/[0-9]/.test(p)) errs.push("One number");
    if (!/[!@#$%^&*()\-_=+\[\]{}<>?]/.test(p))
      errs.push("One special character");
    return errs;
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // fallback
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError("");
    const password = autoGenerate ? generated : custom;
    if (!password) {
      setError("Password is required");
      setSaving(false);
      return;
    }
    if (!autoGenerate) {
      const errs = validatePassword(password);
      if (errs.length) {
        setError(errs.join(", "));
        setSaving(false);
        return;
      }
    }

    // Assumption: backend endpoint exists at /api/persons/:id/reset-password
    // If your API differs, update this URL accordingly.
    try {
      const body = {
        password,
        email: email || null,
        send_email: !!sendEmail,
        force_change_on_next_signin: !!forceChange,
        auto_generated: !!autoGenerate,
      };
      const res = await fetch(
        `${BASE_URL}/api/personnel/${person?.id}/reset-password`,
        {
          method: "put",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(body),
        },
      );

      const contentType = res.headers.get("content-type");
      let responseData;

      if (contentType && contentType.includes("application/json")) {
        responseData = await res.json();
      } else {
        const text = await res.text();
        responseData = { message: text || `Server returned ${res.status}` };
      }

      if (!res.ok) {
        throw new Error(
          responseData.message ||
            responseData.error ||
            `Server returned ${res.status}`,
        );
      }

      showSuccess(responseData.message || "Password reset successfully");

      //  Close after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err?.message || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold">Reset Password</div>
            <div className="text-sm text-gray-500 mt-1">
              Set a new password and optionally email it to staff.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✖
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="inline-flex items-center gap-2">
            <input
              style={{ accentColor: "#dc2626" }}
              type="checkbox"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
            />
            <span className="text-sm">Automatically generate new password</span>
          </label>

          {autoGenerate ? (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 mb-1">
                Generated Password
              </div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={generated}
                  type={showPassword ? "text" : "password"}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 bg-gray-50"
                />
                <button
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="p-2 rounded border"
                  title={showPassword ? "Hide" : "Show"}
                  style={{ color: "#000" }}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-7.5a10.94 10.94 0 0 1 1.64-3.02" />
                      <path d="M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(generated)}
                  aria-label="Copy generated password"
                  className="p-2 rounded border"
                  title="Copy"
                  style={{ color: "#000" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="2" width="6" height="4" rx="1" />
                    <path d="M9 8H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <input
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder="Customized Password"
                  className="flex-1 border border-gray-300 rounded px-3 py-2 bg-white"
                />
                <button
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="p-2 rounded border"
                  title={showPassword ? "Hide" : "Show"}
                  style={{ color: "#000" }}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-7.5a10.94 10.94 0 0 1 1.64-3.02" />
                      <path d="M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(custom)}
                  aria-label="Copy custom password"
                  className="p-2 rounded border"
                  title="Copy"
                  style={{ color: "#000" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="2" width="6" height="4" rx="1" />
                    <path d="M9 8H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2" />
                  </svg>
                </button>
              </div>
              <div className="text-xs text-red-600 mt-2">
                {custom
                  ? validatePassword(custom).map((e, i) => (
                      <div key={i}>• {e}</div>
                    ))
                  : null}
              </div>
            </div>
          )}

          <div>
            <label className="inline-flex items-center gap-2">
              <input
                style={{ accentColor: "#dc2626" }}
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              <span className="text-sm">
                Email {autoGenerate ? "newly generated" : "customized"} password
                to {email}
              </span>
            </label>
          </div>

          <label className="inline-flex items-center gap-2">
            <input
              style={{ accentColor: "#dc2626" }}
              type="checkbox"
              checked={forceChange}
              onChange={(e) => setForceChange(e.target.checked)}
            />
            <span className="text-sm">
              After sign-in force user to create new password
            </span>
          </label>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-red-600 text-white rounded"
            disabled={saving}
          >
            {saving ? "Resetting…" : "RESET PASSWORD"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  hint,
  disabled,
  type,
  required = false,
  min,
  max,
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-medium text-gray-600">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </div>
      {type === "date" ? (
        <DateInput
          className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 w-full"
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
        />
      ) : (
        <input
          type={type || "text"}
          className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      )}
      {hint ? <div className="text-xs text-gray-400">{hint}</div> : null}
    </div>
  );
}

function TempNotes({ person, active = false, actionMenuZClass }) {
  const contextZClass = useContext(PersonnelModalZContext);
  const effectiveMenuZClass = actionMenuZClass || contextZClass || "z-[10150]";
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null); // ID of note being edited, null if adding
  const [relatesQuery, setRelatesQuery] = useState("");
  const [relatesOptions, setRelatesOptions] = useState([]);
  const [selectedRelateds, setSelectedRelateds] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const notesFilterPanel = useEmbeddedFilterLayout();
  // FILTER STATES
  const [createdBy, setCreatedBy] = useState([]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [relatedTo, setRelatedTo] = useState([]); // MULTI SELECT

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [createdByDropdownOpen, setCreatedByDropdownOpen] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [authorOptions, setAuthorOptions] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortBy, setSortBy] = useState("create_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [notesLimit, setNotesLimit] = useState(25);

  const [filteredNotes, setFilteredNotes] = useState([]);
  const authorFilterOptions = useMemo(
    () =>
      (authorOptions || []).map((a) => ({
        value: String(a.account_id || a.id),
        label: `${a.first_name || ""} ${a.last_name || ""}`.trim() || String(a.account_id || a.id),
      })),
    [authorOptions],
  );
  const relatedFilterOptions = useMemo(
    () =>
      (typeOptions || []).map((opt) => ({
        value: opt.label,
        label: opt.label,
      })),
    [typeOptions],
  );
  const notesFilterChips = useMemo(
    () =>
      buildPersonnelNotesFilterChips(
        { createdBy, createdFrom, createdTo, relatedTo, showDeleted },
        { authorOptions, relatedTypeOptions: typeOptions },
      ),
    [createdBy, createdFrom, createdTo, relatedTo, showDeleted, authorOptions, typeOptions],
  );
  const handleNotesHeaderSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortOrder(key === "create_date" ? "desc" : "asc");
  };

  const relatesTimer = useRef(null);
  const dropdownRef = useRef(null);
  const relatesRef = useRef(null);

  const normalizeTypeName = (type) => {
    if (!type) return "—";
    const t = type.toLowerCase().trim();
    if (t === "officestaff" || t === "staff") return "Office Staff";
    if (t === "fieldstaff" || t === "temp") return "Field Staff";
    if (t === "client") return "Client";
    if (t === "candidate") return "Candidate";
    if (t === "outsourcedstaff") return "Outsourced Staff";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const toggleRelated = (value) => {
    setRelatedTo((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };
  const toggleCreatedBy = (value) => {
    setCreatedBy((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const removeTag = (value) => {
    setRelatedTo((prev) => prev.filter((v) => v !== value));
  };
  const removeCreatedByTag = (value) => {
    setCreatedBy((prev) => prev.filter((v) => v !== value));
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setCreatedByDropdownOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // fetch types for filter if no need convert into hard code options
  useEffect(() => {
    fetch(`${BASE_URL}/api/personnel/account-types`)
      .then((res) => res.json())
      .then((j) => {
        if (j.success) {
          setTypeOptions(filterAllowedNoteRelatedTypeOptions(j.data));
        }
      });
  }, []);

  const applyFilter = (
    searchValue = searchTerm,
    createdValue = createdBy,
    dateFromValue = createdFrom,
    dateToValue = createdTo,
    relatedValues = relatedTo,
    sortKey = sortBy,
    sortDir = sortOrder,
  ) => {
    let filtered = [...notes];

    // Search Term Filter
    const q = (searchValue || "").toString().trim().toLowerCase();

    if (q !== "") {
      filtered = filtered.filter(
        (n) =>
          (n.note && n.note.toLowerCase().includes(q)) ||
          (n.content && n.content.toLowerCase().includes(q)) ||
          // (n.relates_to && n.relates_to.toLowerCase().includes(q)) ||
          (n.relates_type && n.relates_type.toLowerCase().includes(q)) ||
          formatActor(n.created_by).toLowerCase().includes(q),
      );
    }
    // Created by filter
    if (Array.isArray(createdValue) && createdValue.length > 0) {
      const selectedAuthorNames = createdValue
        .map((id) =>
          authorOptions.find((a) => String(a.account_id || a.id) === String(id)),
        )
        .filter(Boolean)
        .map((a) => `${a.first_name} ${a.last_name}`.toLowerCase());

      filtered = filtered.filter((n) => {
        const actorStr = String(n.created_by || "").toLowerCase();
        const match = actorStr.match(/(\d+)/);
        const actorId = match ? match[1] : actorStr;
        const idMatch = createdValue.some((id) => String(actorId) === String(id));
        if (idMatch) return true;
        return selectedAuthorNames.some((name) => name && actorStr.includes(name));
      });
    }

    // Date range filter - Created from
    if (dateFromValue) {
      const fromDate = new Date(dateFromValue);
      fromDate.setHours(0, 0, 0, 0); // Start of day
      filtered = filtered.filter((n) => {
        if (!n.create_date) return false;
        const noteDate = new Date(n.create_date);
        return noteDate >= fromDate;
      });
    }

    // Date range filter - Created to
    if (dateToValue) {
      const toDate = new Date(dateToValue);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter((n) => {
        if (!n.create_date) return false;
        const noteDate = new Date(n.create_date);
        return noteDate <= toDate;
      });
    }

    //realted to
    if (Array.isArray(relatedValues) && relatedValues.length > 0) {
      const selectedTypeKeys = new Set(
        relatedValues.map((v) => normalizeNoteRelatedTypeKey(v)),
      );
      filtered = filtered.filter((n) => {
        const singleKey = normalizeNoteRelatedTypeKey(n.relates_type);
        if (singleKey && selectedTypeKeys.has(singleKey)) return true;
        if (Array.isArray(n.relates_types)) {
          return n.relates_types.some((t) =>
            selectedTypeKeys.has(normalizeNoteRelatedTypeKey(t)),
          );
        }
        return false;
      });
    }

    // Show Deleted Filter
    filtered = filtered.filter((n) => {
      const state = (n.state || "active").toString().toLowerCase().trim();
      return showDeleted ? state === "deleted" : state !== "deleted";
    });
    filtered.sort((a, b) => {
      let x = a[sortKey];
      let y = b[sortKey];

      if (typeof x === "string") x = x.toLowerCase();
      if (typeof y === "string") y = y.toLowerCase();

      return sortDir === "asc" ? (x > y ? 1 : -1) : x < y ? 1 : -1;
    });

    setFilteredNotes(filtered);
  };

  useEffect(() => {
    applyFilter();
  }, [
    notes,
    searchTerm,
    createdBy,
    createdFrom,
    createdTo,
    relatedTo,
    showDeleted,
    sortBy,
    sortOrder,
  ]);
  const visibleNotes = filteredNotes.slice(0, notesLimit);

  const getAuthorAccountId = () => {
    try {
      const explicit = localStorage.getItem("accountId");
      if (explicit && !Number.isNaN(parseInt(explicit, 10)))
        return parseInt(explicit, 10);
      const rawUser = localStorage.getItem("user");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u && (u.account_id || u.accountId || u.id))
          return u.account_id || u.accountId || u.id;
      }
    } catch (_) {}
    return undefined;
  };

  const loadNotes = async () => {
    const startTs = new Date().toISOString();
    const url = `${BASE_URL}/api/personnel/accounts/${person.id}/notes`;
    console.debug("[TempNotes] loadNotes start", {
      personId: person?.id,
      url,
      startTs,
    });
    if (!person?.id) {
      console.debug("[TempNotes] no person id, abort loadNotes");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(url, { credentials: "include" });
      const contentType =
        res.headers && res.headers.get
          ? res.headers.get("content-type")
          : undefined;
      const raw = await res.text().catch(() => null);
      let j = {};
      try {
        j = raw ? JSON.parse(raw) : {};
      } catch (parseErr) {
        j = { data: [] };
      }
      console.debug("[TempNotes] loadNotes response", {
        ok: res.ok,
        status: res.status,
        contentType,
        body: j,
        rawLength: raw ? raw.length : 0,
      });
      if (!res.ok || j.success === false) {
        const msg =
          (j && j.message) || `Failed to load notes (status ${res.status})`;
        throw new Error(msg);
      }
      const data = Array.isArray(j.data) ? j.data : Array.isArray(j) ? j : [];
      console.debug("[TempNotes] notes resolved count", (data || []).length);
      console.log(
        "[TempNotes] notes with states:",
        data.map((n) => ({ id: n.note_id, state: n.state })),
      );
      setNotes(data || []);
      console.log("loade note data :", data);
      // Don't set filteredNotes directly - let applyFilter handle it via useEffect
    } catch (e) {
      const errorMsg = e.message || "Failed to load notes";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Notes");
      console.error("[TempNotes] loadNotes error", e);
    } finally {
      setLoading(false);
    }
  };

  // Reload notes when the notes tab becomes active or the person changes
  useEffect(() => {
    console.debug("[TempNotes] useEffect active/person change", {
      personId: person?.id,
      active,
    });
    if (active) loadNotes();
  }, [person?.id, active]);

  // Mount/unmount trace
  useEffect(() => {
    console.debug("[TempNotes] mounted", { personId: person?.id, active });
    return () =>
      console.debug("[TempNotes] unmounted", { personId: person?.id });
  }, []);

  const searchRelates = async (q) => {
    if (!q || q.trim().length < 1) {
      setRelatesOptions([]);
      return;
    }
    try {
      const resp = await fetch(
        `${BASE_URL}/api/personnel/accounts/search?q=${encodeURIComponent(
          q,
        )}&limit=20`,
        { credentials: "include" },
      );
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setRelatesOptions([]);
        return;
      }
      // Expecting { data: [ { account_id, name, type } ] } or a plain array
      const list = j.data || j || [];
      const opts = Array.isArray(list)
        ? list.map((a) => ({
            uid: a.uid || `${a.type || "account"}-${a.account_id || a.id}`,
            id: a.account_id || a.id,
            name: a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim(),
            type: a.type || a.account_type || "",
          }))
        : [];
      setRelatesOptions(opts);
    } catch (err) {
      console.error("Relates search failed", err);
      setRelatesOptions([]);
    }
  };

  // Close relates-to dropdown on outside click or Escape (OfficeNotes)
  useEffect(() => {
    const onClick = (e) => {
      if (!relatesOptions || relatesOptions.length === 0) return;
      const node = relatesRef.current;
      if (node && !node.contains(e.target)) {
        setRelatesOptions([]);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setRelatesOptions([]);
      }
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
      if (relatesTimer.current) {
        clearTimeout(relatesTimer.current);
        relatesTimer.current = null;
      }
    };
  }, [relatesOptions]);

  // Close relates-to dropdown on outside click or Escape
  useEffect(() => {
    const onClick = (e) => {
      if (!relatesOptions || relatesOptions.length === 0) return;
      const node = relatesRef.current;
      if (node && !node.contains(e.target)) {
        setRelatesOptions([]);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setRelatesOptions([]);
      }
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
      if (relatesTimer.current) {
        clearTimeout(relatesTimer.current);
        relatesTimer.current = null;
      }
    };
  }, [relatesOptions]);

  const addNote = async () => {
    if (!newNote.trim()) {
      showError("Note is required");
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      // Send structured payload: note text + array of relations
      const payload = {
        note: newNote,
        state: "active",
        relates_to: selectedRelateds.map((r) => ({
          id: r.id,
          type: r.type,
          name: r.name,
        })),
      };

      const url = `${BASE_URL}/api/personnel/accounts/${person.id}/notes`;
      console.debug("[TempNotes] addNote payload", { url, payload });
      const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const raw = await res.text().catch(() => null);
      let j = {};
      try {
        j = raw ? JSON.parse(raw) : {};
      } catch (_) {
        j = {};
      }
      console.debug("[TempNotes] addNote response", {
        ok: res.ok,
        status: res.status,
        body: j,
        rawLength: raw ? raw.length : 0,
      });
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to add note");
      setNewNote("");
      setShowModal(false);
      setSelectedRelateds([]);
      setRelatesQuery("");
      loadNotes();
      showSuccess("Note added successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to add note";
      setError(errorMsg);
      showError(errorMsg, "Failed to Add Note");
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (note) => {
    setEditingNoteId(note.note_id);
    setNewNote(note.note || note.content || "");

    // Map existing relations to the format needed for selectedRelateds
    if (Array.isArray(note.relates_to) && note.relates_to.length > 0) {
      const mapped = note.relates_to.map((name, idx) => ({
        // We might not have the exact ID/uid from this view if the backend only returns names/types arrays
        // But let's try to construct a usable object.
        // If the backend provides relates_ids or similar, use that.
        // Based on previous code, we might only have names and types.
        // We'll construct a synthetic UID if needed, but ideally we need real IDs for updates.
        // *Assumption*: The backend returns `relates_ids` or similar to effectively re-link them.
        // If not, we might be limited. Let's inspect `note` object structure from `loadNotes`.
        // The `loadNotes` output shows keys like `relates_to` (array of strings), `relates_types`.
        // If we don't have IDs, we can't reliably "edit" relations effectively without clearing them.
        // However, let's assume we can map them back or they are just for display if we can't get IDs.

        // WAIT: The `loadNotes` debug output or `mapNotes` might show more.
        // If we look at `applyFilter`, it uses `n.relates_type`.
        // Let's assume for now we use what we have. If we lack IDs, we might need to fetch them or
        // the user will have to re-select relations if they want to change them.

        // Actually, let's check if `n` has `relates_ids`.
        // If not, we might show them as "Text only" or similar, but the user request implies full editing.
        // Let's try to use `relates_ids` if available, otherwise just use name/type.

        uid: `existing-${idx}`, // temporary UID
        id: note.relates_object_ids?.[idx] || null,
        name: name,
        type: note.relates_types?.[idx] || "",
      }));
      setSelectedRelateds(mapped);
    } else {
      setSelectedRelateds([]);
    }

    setShowModal(true);
  };

  const saveNote = async () => {
    if (!newNote.trim()) {
      showError("Note is required");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      // Filter out the "person" themselves from relations if we added them for display
      // The user request says: "when saving to db relates to.. ignore that" (referring to the temp staff user)
      // We will add the temp staff visually in the modal, but here we process `selectedRelateds`.
      // If `selectedRelateds` contains the current person (by ID), exclude it?
      // actually, the request says "shows related to data but top of that we should show tyhe temp staff user name".
      // We'll handle the visual part in the render.
      // `selectedRelateds` should only contain *extra* relations.

      const payload = {
        note: newNote,
        state: "active",
        relates_to: selectedRelateds
          .map((r) => ({
            id: r.id,
            type: r.type,
            name: r.name,
          }))
          .filter((r) => r.id), // Ensure we have an ID to relate to
      };

      let url = `${BASE_URL}/api/personnel/accounts/${person.id}/notes`;
      let method = "POST";

      if (editingNoteId) {
        url = `${url}/${editingNoteId}`;
        method = "PUT";
      }

      const res = await fetch(url, {
        method,
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const j = await res.json().catch(() => ({}));

      if (!res.ok || !j.success) {
        throw new Error(
          j.message || `Failed to ${editingNoteId ? "update" : "add"} note`,
        );
      }

      setNewNote("");
      setShowModal(false);
      setEditingNoteId(null);
      setSelectedRelateds([]);
      setRelatesQuery("");
      loadNotes();
      showSuccess(`Note ${editingNoteId ? "updated" : "added"} successfully!`);
    } catch (e) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const restoreNote = async (n) => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      const payload = {
        note: n.note || n.content,
        state: "active",
        relates_to: (n.relates_object_ids || []).map((id, idx) => ({
          id,
          type: n.relates_types?.[idx],
          name: n.relates_to?.[idx],
        })),
      };

      const url = `${BASE_URL}/api/personnel/accounts/${person.id}/notes/${n.note_id}`;
      const res = await fetch(url, {
        method: "PUT",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to restore note");

      loadNotes();
      showSuccess("Note restored successfully!");
    } catch (e) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!(await showConfirm("Are you sure you want to delete this note?")))
      return;
    try {
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/notes/${noteId}`,
        {
          method: "DELETE",
          headers,
          credentials: "include",
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to delete note");
      loadNotes();
      showSuccess("Note deleted successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to delete note";
      setError(errorMsg);
      showError(errorMsg, "Failed to Delete Note");
    }
  };

  const formatDateTime = (dateTime) =>
    formatAppDateTime(dateTime, { emptyValue: "—" });

  const fetchAuthors = async () => {
    const res = await axios.get(
      `${BASE_URL}/api/personnel/${person.id}/note/created-by?showDeleted=${showDeleted ? "true" : "false"}`,
    );
    setAuthorOptions(res.data.data);
  };

  useEffect(() => {
    if (notesFilterPanel.isOpen) {
      fetchAuthors();
    }
  }, [notesFilterPanel.isOpen, person?.id, showDeleted]);

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Notes for {person?.name || `#${person?.id}`}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <AtlasListToolbar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onFilterClick={notesFilterPanel.toggle}
        filterOpen={notesFilterPanel.isOpen}
        onRefresh={loadNotes}
        refreshDisabled={loading}
        refreshSpinning={loading}
        resultsMeta={{
          startRow: filteredNotes.length === 0 ? 0 : 1,
          endRow: Math.min(notesLimit, filteredNotes.length),
          totalCount: filteredNotes.length,
          pageSize: notesLimit,
          pageSizeOptions: [10, 25, 50, 100, 200],
          onPageSizeChange: setNotesLimit,
          sortBy,
          sortOrder,
          sortOptions: NOTES_SORT_OPTIONS,
          onSortByChange: setSortBy,
          onSortOrderToggle: () =>
            setSortOrder((o) => (o === "asc" ? "desc" : "asc")),
        }}
        actions={
          <button
            type="button"
            onClick={() => {
              setEditingNoteId(null);
              setNewNote("");
              setSelectedRelateds([]);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
          >
            + Add Note
          </button>
        }
        chipBar={
          notesFilterChips.length > 0 ? (
            <AtlasFilterChipBar
              chips={notesFilterChips}
              onChipClick={(chip) => notesFilterPanel.focusField(chip.fieldId)}
              onRemove={(chip) => {
                if (chip.key === "createdBy") setCreatedBy([]);
                if (chip.key === "createdFrom") setCreatedFrom("");
                if (chip.key === "createdTo") setCreatedTo("");
                if (chip.key === "relatedTo") setRelatedTo([]);
                if (chip.key === "showDeleted") setShowDeleted(false);
              }}
              onClearAll={() => {
                setCreatedBy([]);
                setCreatedFrom("");
                setCreatedTo("");
                setRelatedTo([]);
                setShowDeleted(false);
                setSortBy("create_date");
                setSortOrder("desc");
              }}
            />
          ) : null
        }
      />

      <EmbeddedFilterPanel
        isOpen={notesFilterPanel.isOpen}
        onClose={notesFilterPanel.close}
        onClear={() => {
          setCreatedBy([]);
          setCreatedFrom("");
          setCreatedTo("");
          setRelatedTo([]);
          setShowDeleted(false);
          setSortBy("create_date");
          setSortOrder("desc");
        }}
        footerMode="clearOnly"
      >
        <FilterMultiSelect
          id="notes-created-by"
          label="Created by"
          labelVariant="embedded"
          options={authorFilterOptions}
          value={createdBy}
          onChange={setCreatedBy}
        />
        <FilterField
          id="notes-created-from"
          label="Created from"
          labelVariant="embedded"
          focusedFieldId={notesFilterPanel.focusedFieldId}
          onFocusClear={notesFilterPanel.clearFocus}
        >
          <DateInput
            id="notes-created-from-control"
            value={createdFrom}
            onChange={setCreatedFrom}
            className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
        </FilterField>
        <FilterField
          id="notes-created-to"
          label="Created to"
          labelVariant="embedded"
          focusedFieldId={notesFilterPanel.focusedFieldId}
          onFocusClear={notesFilterPanel.clearFocus}
        >
          <DateInput
            id="notes-created-to-control"
            value={createdTo}
            onChange={setCreatedTo}
            className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
        </FilterField>
        <FilterMultiSelect
          id="notes-related-to"
          label="Related to"
          labelVariant="embedded"
          options={relatedFilterOptions}
          value={relatedTo}
          onChange={setRelatedTo}
        />
        <FilterToggle
          id="notes-show-deleted"
          label="Show Deleted"
          checked={showDeleted}
          onChange={setShowDeleted}
        />
        <FilterSelect
          id="notes-sort-by"
          label="Sort By"
          labelVariant="embedded"
          value={sortBy}
          onChange={setSortBy}
          options={NOTES_SORT_OPTIONS}
          placeholder={null}
        />
        <FilterSelect
          id="notes-sort-direction"
          label="Sort Direction"
          labelVariant="embedded"
          value={sortOrder}
          onChange={setSortOrder}
          options={[
            { value: "asc", label: "Ascending" },
            { value: "desc", label: "Descending" },
          ]}
          placeholder={null}
        />
      </EmbeddedFilterPanel>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No notes yet
        </div>
      ) : (
        <div className="w-full border border-gray-200 rounded overflow-hidden shadow-sm">
          {/* Mobile Card View — disabled per QA request; keep the real table at every
              width instead (below), scrollable via no-mobile-table-stack. */}
          <div className="hidden flex-col gap-0 divide-y divide-gray-100 bg-gray-50">
            {visibleNotes.map((n) => (
              <div key={`mob-${n.note_id}`} className="bg-white p-4 flex flex-col gap-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-center gap-2 text-gray-700">
                    <CreatorAvatar avatarUrl={n.created_by_avatar} name={n.created_by} />
                    <span className="font-semibold text-sm">{formatActor(n.created_by)}</span>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(n.create_date)}</span>
                </div>
                
                <div
                  className={`text-sm whitespace-pre-wrap break-words font-medium ${n.state === "deleted" ? "text-red-600/50" : "text-gray-800"}`}
                  onClick={() => n.state !== "deleted" && handleEditClick(n)}
                >
                  {(n.note ?? n.content) || "—"}
                  {n.state === "deleted" && (
                    <span className="ml-2 text-[9px] font-bold text-amber-500 uppercase tracking-wider">
                      Deleted
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2 pt-2 mt-1 border-t border-gray-50">
                  <div className="flex flex-wrap gap-1">
                    {n.object_owner && !n.relates_to?.some(r => String(r || "").trim().toLowerCase() === String(n.object_owner || "").trim().toLowerCase()) && (
                      <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-[10px] font-medium border border-red-100">
                        {n.object_owner}
                        {n.object_owner_type ? ` (${normalizeTypeName(n.object_owner_type)})` : ""}
                      </span>
                    )}
                    {Array.isArray(n.relates_to) && n.relates_to.length > 0 ? (
                      n.relates_to.map((relName, idx) => (
                        <span key={idx} className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-[10px] font-medium border border-red-100">
                          {relName}
                          {n.relates_types?.[idx] ? ` (${normalizeTypeName(n.relates_types[idx])})` : ""}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 text-xs italic">No relations</span>
                    )}
                  </div>
                  <div className="flex justify-end mt-1">
                    <ActionDropdown
                      menuZIndexClass={effectiveMenuZClass}
                      actions={
                        n.state === "deleted"
                          ? [ { label: "Restore", onClick: () => restoreNote(n) } ]
                          : [
                              { label: "Edit", onClick: () => handleEditClick(n) },
                              { label: "Delete", onClick: () => deleteNote(n.note_id) },
                            ]
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto min-w-0 bg-white no-mobile-table-stack">
            <table className="w-full min-w-[600px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <SortableTableHeader
                  label="Note"
                  sortKey="note"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleNotesHeaderSort}
                />
                <TableHeaderCell label="Created By" />
                <SortableTableHeader
                  label="Date"
                  sortKey="create_date"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleNotesHeaderSort}
                />
                <TableHeaderCell label="Relates to" />
                <TableHeaderCell label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visibleNotes.map((n) => (
                <tr key={n.note_id} className="border-t border-gray-200">
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <div
                        className={`whitespace-pre-wrap break-words font-medium ${n.state === "deleted" ? "text-red-600/50" : "text-red-600 hover:text-red-700 cursor-pointer"}`}
                        onClick={() =>
                          n.state !== "deleted" && handleEditClick(n)
                        }
                      >
                        {(n.note ?? n.content) || "—"}
                      </div>
                      {n.state === "deleted" && (
                        <span className="text-[9px] font-bold text-amber-500 uppercase tracking-wider">
                          Deleted
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 text-gray-700">
                      <CreatorAvatar
                        avatarUrl={n.created_by_avatar}
                        name={n.created_by}
                      />
                      <span className="whitespace-nowrap">
                        {formatActor(n.created_by)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{formatDateTime(n.create_date)}</td>
                  <td className="px-3 py-2">
                    {Array.isArray(n.relates_to) && n.relates_to.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {n.object_owner &&
                          !n.relates_to?.some(
                            (relName) =>
                              String(relName || "").trim().toLowerCase() ===
                              String(n.object_owner || "").trim().toLowerCase(),
                          ) && (
                            <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-[10px] font-medium border border-red-100">
                              {n.object_owner}
                              {n.object_owner_type
                                ? ` (${normalizeTypeName(n.object_owner_type)})`
                                : ""}
                            </span>
                          )}
                        {n.relates_to.map((relName, idx) => (
                          <span
                            key={idx}
                            className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-[10px] font-medium border border-red-100"
                          >
                            {relName}
                            {n.relates_types?.[idx]
                              ? ` (${normalizeTypeName(n.relates_types[idx])})`
                              : ""}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <ActionDropdown
                      menuZIndexClass={effectiveMenuZClass}
                      actions={
                        n.state === "deleted"
                          ? [
                              {
                                label: "Restore",
                                onClick: () => restoreNote(n),
                              },
                            ]
                          : [
                              {
                                label: "Edit",
                                onClick: () => handleEditClick(n),
                              },
                              {
                                label: "Delete",
                                onClick: () => deleteNote(n.note_id),
                              },
                            ]
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingNoteId ? "Edit Note" : "Add Note"}
            </h3>

            <div className="space-y-4">
              <div className="relative" ref={relatesRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relates to
                </label>

                {/* Selected Chips */}

                {/* Always show the Person themselves as the first "related" item (Read Only) */}
                <div className="flex flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-1.5 bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-[10px] font-bold border border-gray-200 shadow-sm cursor-default">
                    <span>{person?.name || "Temp Staff"}</span>
                    {/* No remove button for this one */}
                  </div>

                  {selectedRelateds.map((opt) => (
                    <div
                      key={opt.uid}
                      className="flex items-center gap-1.5 bg-red-50 text-red-700 px-2 py-1 rounded-md text-[10px] font-bold border border-red-100 shadow-sm"
                    >
                      <span>
                        {opt.name}{" "}
                        {opt.type ? `(${normalizeTypeName(opt.type)})` : ""}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRelateds((prev) =>
                            prev.filter((r) => r.uid !== opt.uid),
                          );
                        }}
                        className="hover:text-red-900 bg-red-100 rounded-full p-0.5"
                      >
                        <FaTimes size={8} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="relative">
                  <input
                    value={relatesQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRelatesQuery(v);
                      if (relatesTimer.current)
                        clearTimeout(relatesTimer.current);
                      relatesTimer.current = setTimeout(
                        () => searchRelates(v),
                        300,
                      );
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/10 focus:border-red-500"
                    placeholder="Search accounts to relate (Office Staff, Clients, etc)..."
                  />
                  {relatesOptions.length > 0 && (
                    <div className="absolute z-[60] left-0 right-0 mt-1 border border-gray-200 rounded-lg bg-white max-h-48 overflow-auto shadow-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-200">
                      {relatesOptions.map((opt) => {
                        const isAlreadySelected = selectedRelateds.some(
                          (r) => r.uid === opt.uid,
                        );
                        return (
                          <div
                            key={opt.uid}
                            className={`px-4 py-2.5 hover:bg-red-50 cursor-pointer flex items-center justify-between group ${isAlreadySelected ? "opacity-50 grayscale pointer-events-none" : ""}`}
                            onClick={() => {
                              if (!isAlreadySelected) {
                                setSelectedRelateds((prev) => [...prev, opt]);
                                setRelatesQuery("");
                                setRelatesOptions([]);
                              }
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-gray-800 group-hover:text-red-700">
                                {opt.name}
                              </span>
                              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                                {normalizeTypeName(opt.type)}
                              </span>
                            </div>
                            {isAlreadySelected && (
                              <FaTimes size={10} className="text-gray-300" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows={4}
                  placeholder="Enter your note here..."
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={saveNote}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={saving || !newNote.trim()}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TempSkills({ person }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allSkills, setAllSkills] = useState([]); // from master list
  const [accountSkills, setAccountSkills] = useState([]); // current assigned
  const [selectedSkillId, setSelectedSkillId] = useState("");
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const [skillsRes, acctRes] = await Promise.all([
        fetch(`${BASE_URL}/api/personnel/skills`, { credentials: "include" }),
        fetch(`${BASE_URL}/api/personnel/accounts/${person.id}/skills`, {
          credentials: "include",
        }),
      ]);
      const skillsJson = await skillsRes.json().catch(() => ({ data: [] }));
      const acctJson = await acctRes.json().catch(() => ({ data: [] }));
      setAllSkills(Array.isArray(skillsJson.data) ? skillsJson.data : []);
      setAccountSkills(Array.isArray(acctJson.data) ? acctJson.data : []);
    } catch (e) {
      const errorMsg = e.message || "Failed to load skills";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Skills");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [person?.id]);

  const availableToAdd = allSkills.filter(
    (s) => !accountSkills.some((a) => a.skill_id === (s.id ?? s.skill_id)),
  );

  const addSkill = async () => {
    if (!selectedSkillId) return;
    try {
      setSaving(true);
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/skills`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({ skillId: Number(selectedSkillId) }),
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false)
        throw new Error(j.message || "Failed");
      setSelectedSkillId("");
      await load();
      showSuccess("Skill added successfully!");
    } catch (e) {
      const errorMsg = e.message || "Add failed";
      setError(errorMsg);
      showError(errorMsg, "Failed to Add Skill");
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    if (!(await showConfirm("Remove this skill?"))) return;
    try {
      setUpdating(row.skill_id);
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/skills/${row.skill_id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false)
        throw new Error(j.message || "Failed");
      setAccountSkills((prev) =>
        prev.filter((s) => s.skill_id !== row.skill_id),
      );
      showSuccess("Skill removed successfully!");
    } catch (e) {
      const errorMsg = e.message || "Delete failed";
      setError(errorMsg);
      showError(errorMsg, "Failed to Remove Skill");
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Skills for {person?.name || `#${person?.id}`}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4 p-1">
        <div className="flex-1 w-full min-w-0">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
            Select Skill
          </label>
          <div className="relative">
            <select
              className="w-full min-w-0 bg-white text-gray-900 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-shadow appearance-none"
              value={selectedSkillId}
              onChange={(e) => setSelectedSkillId(e.target.value)}
            >
              <option value="" className="text-gray-400">Choose a skill…</option>
              {availableToAdd.map((s) => (
                <option key={s.id ?? s.skill_id} value={s.id ?? s.skill_id} className="text-gray-900">
                  {s.name}
                </option>
              ))}
            </select>
            {/* Custom dropdown arrow for consistent mobile styling */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        <button
          className="w-full sm:w-auto px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center h-[46px]"
          onClick={addSkill}
          disabled={saving || !selectedSkillId}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Adding…
            </span>
          ) : (
            "Add Skill"
          )}
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : accountSkills.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No skills yet
        </div>
      ) : (
        <div className="w-full border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {/* Mobile View */}
          <div className="md:hidden flex flex-col gap-0 divide-y divide-gray-100 bg-gray-50">
            {accountSkills.map((row) => (
              <div key={row.skill_id} className="p-4 flex flex-col gap-3 bg-white">
                <div className="font-medium text-gray-900">{row.skill_name || row.name}</div>
                <div className="flex justify-end">
                  <button
                    className="px-3 py-1.5 border border-red-600 text-red-600 hover:bg-red-50 transition-colors rounded text-sm w-full sm:w-auto font-medium disabled:opacity-50"
                    onClick={() => removeRow(row)}
                    disabled={updating === row.skill_id}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto min-w-0 bg-white">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 font-medium">Skill</th>
                  <th className="px-4 py-3 font-medium text-right w-36">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {accountSkills.map((row) => (
                  <tr key={row.skill_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.skill_name || row.name}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="px-3 py-1.5 border border-red-600 text-red-600 hover:bg-red-50 transition-colors rounded text-sm font-medium disabled:opacity-50"
                        onClick={() => removeRow(row)}
                        disabled={updating === row.skill_id}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TempCertifications({ person, actionMenuZClass }) {
  const contextZClass = useContext(PersonnelModalZContext);
  const effectiveMenuZClass = actionMenuZClass || contextZClass || "z-[10150]";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [defs, setDefs] = useState([]); // certification definitions
  const [items, setItems] = useState([]); // assigned certs
  const [fileTypes, setFileTypes] = useState([]);
  const [selectedCertId, setSelectedCertId] = useState("");
  const [selectedCertExpiryDate, setSelectedCertExpiryDate] = useState("");
  const [selectedCertFile, setSelectedCertFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editExpiryDate, setEditExpiryDate] = useState("");
  const [previewCertRow, setPreviewCertRow] = useState(null);
  // Profile province: Smart Serve is shown under that province's program name (e.g. ProServe in AB)
  const [province, setProvince] = useState("");

  useEffect(() => {
    if (!person?.id) return;
    let cancelled = false;
    fetch(`${BASE_URL}/api/personnel/accounts/${person.id}`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled) setProvince(json?.data?.province || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [person?.id]);

  const defsById = useMemo(() => {
    const map = new Map();
    for (const row of defs || []) {
      const id = Number(row?.certification_id ?? row?.id);
      if (Number.isFinite(id)) map.set(id, row);
    }
    return map;
  }, [defs]);

  const normalizeRequiredFlag = (value) =>
    value === true ||
    value === 1 ||
    String(value || "").toLowerCase() === "true" ||
    String(value || "") === "1";

  const getRequirements = (rowOrId) => {
    const certificationId =
      typeof rowOrId === "object"
        ? Number(rowOrId?.certification_id ?? rowOrId?.id)
        : Number(rowOrId);
    const def = defsById.get(certificationId);

    const rowRequiresExpiry =
      typeof rowOrId === "object" ? rowOrId?.requires_expiry_date : undefined;
    const rowRequiresUpload =
      typeof rowOrId === "object" ? rowOrId?.requires_uploaded_file : undefined;

    const requiresExpiryDate = normalizeRequiredFlag(
      rowRequiresExpiry ?? def?.requires_expiry_date ?? def?.expirydate,
    );
    const requiresUploadedFile = normalizeRequiredFlag(
      rowRequiresUpload ?? def?.requires_uploaded_file ?? def?.file,
    );

    return { requiresExpiryDate, requiresUploadedFile };
  };

  const toInputDate = (value) => {
    if (!value) return "";
    // Use the calendar date part as is; local-time conversion shows the previous day in Canada
    const ymd = String(value).match(/^\d{4}-\d{2}-\d{2}/);
    if (ymd) return ymd[0];
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getExpiryDisplay = (value) => {
    const input = String(value || "").trim();
    if (!input) return "—";

    // Expiry is a calendar date ("YYYY-MM-DD" or "YYYY-MM-DDT00:00:00.000Z"): show that date as is.
    // Building it at local midnight and formatting in the app timezone shifted it a day
    // (e.g. 2027-10-29 showed as Oct 28 in a browser ahead of Canada).
    const ymdMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      const [, year, month, day] = ymdMatch.map(Number);
      if (ymdMatch[0] < getAppTodayIso()) return "Expired";
      return formatAppDate(new Date(Date.UTC(year, month - 1, day)), { emptyValue: "—", timeZone: "UTC" });
    }

    const parsed = new Date(input);
    if (!Number.isFinite(parsed.getTime())) return "—";

    const expiryIso = formatAppDate(parsed, { emptyValue: "" });
    if (expiryIso && expiryIso < getAppTodayIso()) return "Expired";
    return formatAppDate(parsed, { emptyValue: "—" });
  };

  const getSupportingFileDisplay = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "—";

    try {
      const withoutQuery = raw.split("?")[0].split("#")[0];
      const parts = withoutQuery.split(/[\\/]/).filter(Boolean);
      const baseName = parts.length ? parts[parts.length - 1] : withoutQuery;
      const decodedName = decodeURIComponent(baseName || raw);
      const cleanedName = decodedName.replace(/^\d{10,}[_-]+/, "");
      return cleanedName || decodedName;
    } catch {
      return raw;
    }
  };

  const loadAll = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const [defsRes, listRes] = await Promise.all([
        fetch(`${BASE_URL}/api/personnel/certifications`, {
          credentials: "include",
        }),
        fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}/certifications`,
          { credentials: "include" },
        ),
      ]);
      const defsJson = await defsRes.json().catch(() => ({ data: [] }));
      const listJson = await listRes.json().catch(() => ({ data: [] }));
      console.log("defsJson", defsJson);
      const nextDefs = Array.isArray(defsJson.data) ? defsJson.data : [];
      const nextItems = Array.isArray(listJson.data) ? listJson.data : [];
      setDefs(nextDefs);
      setItems(nextItems);
      return nextItems;
    } catch (e) {
      const errorMsg = e.message || "Failed to load certifications";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Certifications");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadFileTypes = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/file-types`, {
        credentials: "include",
      });
      const j = await res.json().catch(() => ({}));
      const rows = j?.success && Array.isArray(j.data) ? j.data : [];
      setFileTypes(rows);
      return rows;
    } catch (e) {
      console.error("Failed to load file types for certification upload", e);
      return [];
    }
  };

  const resolveCertificationFileTypeId = (typesInput = fileTypes) => {
    const types = Array.isArray(typesInput) ? typesInput : [];
    if (!types.length) return null;

    const normalize = (value) => String(value || "").toLowerCase();
    const preferredKeywords = [
      "certification",
      "certificate",
      "credential",
      "license",
      "licence",
      "training",
    ];

    const preferredType = types.find((typeRow) =>
      preferredKeywords.some((kw) => normalize(typeRow?.name).includes(kw)),
    );

    return preferredType?.type_id || types[0]?.type_id || null;
  };

  useEffect(() => {
    loadAll();
    loadFileTypes();
  }, [person?.id]);

  const availableToAdd = defs.filter(
    (d) => !items.some((i) => i.certification_id === d.certification_id),
  );

  const selectedRequirements = getRequirements(selectedCertId);

  const addCert = async () => {
    if (!selectedCertId) return;
    try {
      setSaving(true);
      // Ensure we have a token (try to obtain one in dev if missing)
      if (!getAuthToken()) await loginAndGetToken();

      const { requiresExpiryDate, requiresUploadedFile } =
        getRequirements(selectedCertId);

      if (requiresExpiryDate && !selectedCertExpiryDate) {
        throw new Error("Expiry date is required for this certification");
      }

      let uploadedFileId = null;
      if (selectedCertFile) {
        uploadedFileId = await uploadCertificationFile(selectedCertFile);
      }

      if (requiresUploadedFile && !uploadedFileId) {
        throw new Error("Supporting file is required for this certification");
      }

      const payload = {
        certification_id: Number(selectedCertId),
      };

      if (selectedCertExpiryDate) payload.expiry_date = selectedCertExpiryDate;
      if (uploadedFileId) payload.file_id = uploadedFileId;

      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/certifications`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false)
        throw new Error(j.message || "Failed");

      await loadAll();
      showSuccess("Certification added successfully!");

      setSelectedCertId("");
      setSelectedCertExpiryDate("");
      setSelectedCertFile(null);
    } catch (e) {
      const errorMsg = e.message || "Add failed";
      setError(errorMsg);
      showError(errorMsg, "Failed to Add Certification");
    } finally {
      setSaving(false);
    }
  };

  const removeCert = async (row) => {
    if (!(await showConfirm("Remove this certification?"))) return;
    try {
      if (!getAuthToken()) await loginAndGetToken();
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/certifications/${row.employee_certification_id}`,
        { method: "DELETE", credentials: "include", headers: getAuthHeaders() },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false)
        throw new Error(j.message || "Failed");
      setItems((prev) =>
        prev.filter(
          (x) => x.employee_certification_id !== row.employee_certification_id,
        ),
      );
      showSuccess("Certification removed successfully!");
    } catch (e) {
      const errorMsg = e.message || "Delete failed";
      setError(errorMsg);
      showError(errorMsg, "Failed to Remove Certification");
    }
  };

  const handleSaveEdit = async () => {
    if (!editRow) return;

    try {
      const { requiresExpiryDate, requiresUploadedFile } =
        getRequirements(editRow);

      if (requiresExpiryDate && !editExpiryDate) {
        return showError("Expiry date is required for this certification");
      }

      let uploadedFileId = null;
      if (editFile) {
        uploadedFileId = await uploadCertificationFile(editFile);
      }

      if (requiresUploadedFile && !uploadedFileId && !editRow.file_id) {
        return showError("Supporting file is required for this certification");
      }

      await linkFileToCertification(
        editRow.employee_certification_id,
        uploadedFileId,
        editExpiryDate || null,
      );

      showSuccess("Certification file uploaded successfully");
      setShowEdit(false);
      setEditFile(null);
      setEditExpiryDate("");
      loadAll(); // reload table
    } catch (e) {
      showError(e.message || "Upload failed");
    }
  };

  const openCertificationFile = async (row) => {
    const ecId = Number(row?.employee_certification_id);
    if (!Number.isFinite(ecId)) {
      return showError("No file attached");
    }

    try {
      if (!getAuthToken()) await loginAndGetToken();
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/certifications/${ecId}/file/download`,
        {
          method: "GET",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "No file attached");
      }

      // Backend streams the file directly — download as blob
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cd = res.headers.get("content-disposition");
      link.download = cd
        ? decodeURIComponent(
            (cd.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i) || [])[1] || "",
          ) || "certification-file"
        : "certification-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError(e.message || "Failed to open file");
    }
  };

  const handleDownload = async (row) => {
    await openCertificationFile(row);
  };

  const loadCertificationPreview = async (row) => {
    const ecId = Number(row?.employee_certification_id);
    if (!Number.isFinite(ecId)) throw new Error("No file attached");
    if (!getAuthToken()) await loginAndGetToken();

    const res = await fetch(
      `${BASE_URL}/api/personnel/accounts/${person.id}/certifications/${ecId}/file/download`,
      {
        method: "GET",
        credentials: "include",
        headers: { ...getAuthHeaders() },
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to preview file");
    }
    return res.blob();
  };

  const uploadCertificationFile = async (file) => {
    if (!getAuthToken()) await loginAndGetToken();

    let resolvedTypeId = resolveCertificationFileTypeId();
    if (!resolvedTypeId) {
      const latestTypes = await loadFileTypes();
      resolvedTypeId = resolveCertificationFileTypeId(latestTypes);
    }

    if (!resolvedTypeId) {
      throw new Error("No active file type is configured for uploads");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("state", "active");
    formData.append("type_id", String(resolvedTypeId));

    const res = await fetch(
      `${BASE_URL}/api/personnel/accounts/${person.id}/files`,
      {
        method: "POST",
        headers: { ...getAuthHeaders() },
        credentials: "include",
        body: formData,
      },
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "S3 Upload failed");
    }

    return data.fileId;
  };

  const linkFileToCertification = async (
    certificationId,
    fileId,
    expiryDate,
  ) => {
    const payload = {};
    if (fileId !== undefined && fileId !== null) payload.file_id = fileId;
    if (expiryDate !== undefined && expiryDate !== null)
      payload.expiry_date = expiryDate;

    const res = await fetch(
      `${BASE_URL}/api/personnel/accounts/${person.id}/certifications/${certificationId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          ...payload,
        }),
      },
    );

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Failed to link file to certification");
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Certifications for {person?.name || `#${person?.id}`}
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <div className="text-xs font-medium text-gray-600">Certification</div>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2"
            value={selectedCertId}
            onChange={(e) => setSelectedCertId(e.target.value)}
          >
            <option value="">Select certification…</option>
            {availableToAdd.map((c) => (
              <option key={c.certification_id} value={c.certification_id}>
                {certDisplayName(c.name, province)}
              </option>
            ))}
          </select>
        </div>

        <button
          className="px-3 py-2 bg-red-600 text-white rounded"
          onClick={addCert}
          disabled={saving || !selectedCertId}
        >
          {saving ? "Adding…" : "Add Certification"}
        </button>
      </div>

      {selectedCertId &&
        (selectedRequirements.requiresExpiryDate ||
          selectedRequirements.requiresUploadedFile) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedRequirements.requiresExpiryDate && (
              <div>
                <div className="text-xs font-medium text-gray-600">
                  Expiry Date
                </div>
                <DateInput
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  value={selectedCertExpiryDate}
                  onChange={setSelectedCertExpiryDate}
                />
              </div>
            )}

            {selectedRequirements.requiresUploadedFile && (
              <div>
                <div className="text-xs font-medium text-gray-600">
                  Supporting File
                </div>
                <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const validationMessage = file && validateStaffDocumentFile(file);
                    if (validationMessage) {
                      showError(validationMessage);
                      e.target.value = "";
                      return;
                    }
                    setSelectedCertFile(file);
                  }}
                />
              </div>
            )}
          </div>
        )}

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No certifications
        </div>
      ) : (
        <div className="border border-gray-200 rounded overflow-x-auto min-w-0 no-mobile-table-stack">
          <table className="w-full min-w-[600px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Certification</th>
                <th className="text-left px-3 py-2">Supporting File</th>
                <th className="text-left px-3 py-2">Expiry</th>
                <th className="text-right px-3 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.employee_certification_id}
                  className="border-t border-gray-200"
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-left text-red-600 hover:text-red-700"
                      onClick={() => {
                        setEditRow(row);
                        setEditExpiryDate(toInputDate(row.expiry_date));
                        setEditFile(null);
                        setShowEdit(true);
                      }}
                    >
                      {certDisplayName(row.certification_name, province)}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {getSupportingFileDisplay(row.file_name)}
                  </td>
                  <td className="px-3 py-2">
                    {getExpiryDisplay(row.expiry_date)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <ActionDropdown
                      menuZIndexClass={effectiveMenuZClass}
                      actions={[
                        ...(row.file_id || row.file_name
                          ? [
                              {
                                label: "Preview",
                                onClick: () => setPreviewCertRow(row),
                              },
                              {
                                label: "Download",
                                onClick: () => handleDownload(row),
                              },
                            ]
                          : []),
                        {
                          label: "Delete",
                          onClick: () => removeCert(row),
                          className: "text-red-600",
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-[420px] rounded shadow-lg p-5">
            <h2 className="text-lg font-semibold mb-4">Edit Certification</h2>

            {/* Certification Name */}
            <div className="mb-3">
              <label className="text-xs text-gray-600">Certification</label>
              <input
                type="text"
                value={certDisplayName(editRow?.certification_name, province) || ""}
                disabled
                className="w-full border rounded px-3 py-2 bg-gray-100"
              />
            </div>

            {/* Drag & Drop File Upload */}
            {editRow && getRequirements(editRow).requiresExpiryDate && (
              <div className="mb-3">
                <label className="text-xs text-gray-600">Expiry Date</label>
                <DateInput
                  value={editExpiryDate}
                  onChange={setEditExpiryDate}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            )}

            {editRow && getRequirements(editRow).requiresUploadedFile && (
              <div className="mb-4">
                <label className="text-xs text-gray-600">Supporting File</label>

                <div
                  className="border border-dashed border-gray-400 rounded p-5 text-center cursor-pointer bg-gray-50"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    const validationMessage = file && validateStaffDocumentFile(file);
                    if (validationMessage) {
                      showError(validationMessage);
                      return;
                    }
                    setEditFile(file);
                  }}
                >
                  <input
                    type="file"
                    accept={STAFF_DOCUMENT_ACCEPT}
                    hidden
                    id="certUpload"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const validationMessage = file && validateStaffDocumentFile(file);
                      if (validationMessage) {
                        showError(validationMessage);
                        e.target.value = "";
                        return;
                      }
                      setEditFile(file);
                    }}
                  />

                  <label htmlFor="certUpload" className="cursor-pointer">
                    {editFile ? (
                      <div className="text-sm text-gray-700">
                        {editFile.name}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        Drag & drop file here or{" "}
                        <span className="text-red-600">click to upload</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-gray-600"
                onClick={() => {
                  setShowEdit(false);
                  setEditFile(null);
                  setEditExpiryDate("");
                }}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 border border-gray-600"
                onClick={() => handleDownload(editRow)}
              >
                Download
              </button>

              <button
                className="px-4 py-2 bg-red-600 text-white"
                onClick={handleSaveEdit}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      <DocumentPreviewModal
        isOpen={Boolean(previewCertRow)}
        file={previewCertRow}
        loadPreview={loadCertificationPreview}
        onClose={() => setPreviewCertRow(null)}
      />
    </div>
  );
}

function formatReliabilityScheduled(ev) {
  const start = ev?.shift_start_at ? resolveAppInstant(ev.shift_start_at) : null;
  const end = ev?.shift_end_at ? resolveAppInstant(ev.shift_end_at) : null;
  if (start) {
    const datePart = formatAppIntlLabel(start, { month: "short", day: "numeric" });
    const startTime = formatAppTime(start);
    if (end) {
      const endTime = formatAppTime(end);
      return `${datePart} · ${startTime} - ${endTime}`;
    }
    return `${datePart} · ${startTime}`;
  }
  return ev?.scheduled || ev?.shift_date || "—";
}

function TempReliability({ person }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [filteredHistory, setFilteredHistory] = useState([]);

  const reliabilityFilterPanel = useEmbeddedFilterLayout();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterReason, setFilterReason] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterReason,
  );
  const [filterNoticePeriod, setFilterNoticePeriod] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterNoticePeriod,
  );
  const [filterAffectsReliability, setFilterAffectsReliability] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterAffectsReliability,
  );
  const [filterModifyDateFrom, setFilterModifyDateFrom] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterModifyDateFrom,
  );
  const [filterModifyDateTo, setFilterModifyDateTo] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterModifyDateTo,
  );
  const [filterShiftDateFrom, setFilterShiftDateFrom] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterShiftDateFrom,
  );
  const [filterShiftDateTo, setFilterShiftDateTo] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterShiftDateTo,
  );
  const [filterStatus, setFilterStatus] = useState(
    RELIABILITY_DEFAULT_FILTERS.filterStatus,
  );
  const [sortBy, setSortBy] = useState(RELIABILITY_DEFAULT_FILTERS.sortBy);
  const [sortOrder, setSortOrder] = useState(RELIABILITY_DEFAULT_FILTERS.sortOrder);
  const [editingEvent, setEditingEvent] = useState(null);
  const [reliabilityPageSize, setReliabilityPageSize] = useState(25);
  const [reliabilityPage, setReliabilityPage] = useState(1);

  const reliabilityFilterChips = useMemo(
    () =>
      buildPersonnelReliabilityFilterChips({
        filterReason,
        filterNoticePeriod,
        filterAffectsReliability,
        filterStatus,
        filterModifyDateFrom,
        filterModifyDateTo,
        filterShiftDateFrom,
        filterShiftDateTo,
      }),
    [
      filterReason,
      filterNoticePeriod,
      filterAffectsReliability,
      filterStatus,
      filterModifyDateFrom,
      filterModifyDateTo,
      filterShiftDateFrom,
      filterShiftDateTo,
    ],
  );

  const resetReliabilityFilters = () => {
    setSearchTerm("");
    setFilterReason(RELIABILITY_DEFAULT_FILTERS.filterReason);
    setFilterNoticePeriod(RELIABILITY_DEFAULT_FILTERS.filterNoticePeriod);
    setFilterAffectsReliability(RELIABILITY_DEFAULT_FILTERS.filterAffectsReliability);
    setFilterModifyDateFrom(RELIABILITY_DEFAULT_FILTERS.filterModifyDateFrom);
    setFilterModifyDateTo(RELIABILITY_DEFAULT_FILTERS.filterModifyDateTo);
    setFilterShiftDateFrom(RELIABILITY_DEFAULT_FILTERS.filterShiftDateFrom);
    setFilterShiftDateTo(RELIABILITY_DEFAULT_FILTERS.filterShiftDateTo);
    setFilterStatus([...RELIABILITY_DEFAULT_FILTERS.filterStatus]);
    setSortBy(RELIABILITY_DEFAULT_FILTERS.sortBy);
    setSortOrder(RELIABILITY_DEFAULT_FILTERS.sortOrder);
  };

  const handleReliabilityHeaderSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortOrder(key === "reason" ? "asc" : "desc");
  };

  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/reliability`,
        { credentials: "include" },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to load reliability");
      setData(j.data || null);
    } catch (e) {
      const errorMsg = e.message || "Failed to load reliability";
      setError(errorMsg);
      setData(null);
      showError(errorMsg, "Failed to Load Reliability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [person?.id]);

  const score = data?.reliability_score ?? 0;
  const details = data?.reliability_details || {};
  const history = Array.isArray(data?.history) ? data.history : [];
  const reliabilitySummary = data?.reliability || {};
  const unreliableCount =
    details.unreliableCount ?? reliabilitySummary.unreliableCount ?? 0;
  const reasonBuckets = {
    backedOut: details.unreliableUnder24hCount ?? reliabilitySummary.unreliableUnder24hCount ?? 0,
    mia: details.miaCount ?? reliabilitySummary.miaCount ?? 0,
    sick: details.sickCount ?? reliabilitySummary.sickCount ?? 0,
  };
  // Format percent helper
  const fmtPct = (val) =>
    typeof val === "number" ? `${val.toFixed(2)}%` : "—";

  // Apply filters
  const applyFilters = () => {
    let list = [...history];

    // Search term
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (h) =>
          (h.shift_id?.toString() || "").includes(q) ||
          (h.client_name || "").toLowerCase().includes(q) ||
          (h.position_name || "").toLowerCase().includes(q) ||
          (h.state || "").toLowerCase().includes(q) ||
          (h.reason || "").toLowerCase().includes(q) ||
          (h.note || "").toLowerCase().includes(q) ||
          (h.last_modified_by_name || "").toLowerCase().includes(q),
      );
    }

    // Reason filter
    if (filterReason !== "all") {
      list = list.filter((h) =>
        (h.reason || "").toLowerCase().includes(filterReason.toLowerCase()),
      );
    }

    // Notice Period filter
    if (filterNoticePeriod === "less24") {
      list = list.filter((h) => h.notice_period === false);
    } else if (filterNoticePeriod === "more24") {
      list = list.filter((h) => h.notice_period === true);
    }

    // Affects Reliability filter
    if (filterAffectsReliability === "yes") {
      list = list.filter((h) => h.affects_reliability === true);
    } else if (filterAffectsReliability === "no") {
      list = list.filter((h) => h.affects_reliability === false);
    }

    // Modify Date Range
    if (filterModifyDateFrom) {
      const fromTime = new Date(filterModifyDateFrom).getTime();
      list = list.filter((h) => {
        const t = new Date(h.modified_date || h.last_modified).getTime();
        return !isNaN(t) && t >= fromTime;
      });
    }
    if (filterModifyDateTo) {
      const toTime = new Date(filterModifyDateTo).getTime();
      list = list.filter((h) => {
        const t = new Date(h.modified_date || h.last_modified).getTime();
        return !isNaN(t) && t <= toTime;
      });
    }

    // Shift Date Range
    if (filterShiftDateFrom) {
      const fromTime = new Date(filterShiftDateFrom).getTime();
      list = list.filter((h) => {
        const t = new Date(h.shift_date).getTime();
        return !isNaN(t) && t >= fromTime;
      });
    }
    if (filterShiftDateTo) {
      const toTime = new Date(filterShiftDateTo).getTime();
      list = list.filter((h) => {
        const t = new Date(h.shift_date).getTime();
        return !isNaN(t) && t <= toTime;
      });
    }

    // Status filter
    if (filterStatus.length > 0 && filterStatus.length < 2) {
      list = list.filter((h) => {
        const status =
          String(h.state || "").toLowerCase() === "unassigned"
            ? "Unassigned"
            : "Assigned";
        return filterStatus.includes(status);
      });
    }

    // Sorting
    list.sort((a, b) => {
      const dir = sortOrder === "desc" ? -1 : 1;

      if (sortBy === "reason") {
        const valA = String(a.reason || "").trim().toLowerCase();
        const valB = String(b.reason || "").trim().toLowerCase();
        const blankA = !valA;
        const blankB = !valB;
        if (blankA && blankB) return 0;
        if (blankA) return 1;
        if (blankB) return -1;
        return valA.localeCompare(valB) * dir;
      }

      const valueForDate = (row) =>
        sortBy === "scheduled_start"
          ? row.shift_start_at || row.shift_date
          : row.modified_date || row.last_modified;
      const valA = new Date(valueForDate(a)).getTime();
      const valB = new Date(valueForDate(b)).getTime();
      const safeA = Number.isNaN(valA) ? 0 : valA;
      const safeB = Number.isNaN(valB) ? 0 : valB;
      return (safeA - safeB) * dir;
    });

    setFilteredHistory(list);
  };

  const reliabilityTotalPages = Math.max(
    1,
    Math.ceil(filteredHistory.length / reliabilityPageSize),
  );
  const displayedReliabilityRows = filteredHistory.slice(
    (reliabilityPage - 1) * reliabilityPageSize,
    reliabilityPage * reliabilityPageSize,
  );

  useEffect(() => {
    setReliabilityPage(1);
  }, [
    searchTerm,
    filterReason,
    filterNoticePeriod,
    filterAffectsReliability,
    filterModifyDateFrom,
    filterModifyDateTo,
    filterShiftDateFrom,
    filterShiftDateTo,
    filterStatus,
    sortBy,
    sortOrder,
    reliabilityPageSize,
  ]);

  useEffect(() => {
    if (reliabilityPage > reliabilityTotalPages) {
      setReliabilityPage(reliabilityTotalPages);
    }
  }, [reliabilityPage, reliabilityTotalPages]);

  const renderReliabilityPageButtons = () => {
    if (reliabilityTotalPages <= 1) return null;
    const pages = [];
    const start = Math.max(1, reliabilityPage - 1);
    const end = Math.min(reliabilityTotalPages, reliabilityPage + 1);
    if (start > 1) pages.push(1);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < reliabilityTotalPages - 1) pages.push("...");
    if (end < reliabilityTotalPages) pages.push(reliabilityTotalPages);

    return pages.map((p, idx) =>
      p === "..." ? (
        <span key={`rel-ellipsis-${idx}`} className="px-2">
          …
        </span>
      ) : (
        <button
          key={`rel-page-${p}`}
          type="button"
          onClick={() => setReliabilityPage(Number(p))}
          className={`px-3 py-1 rounded border text-sm ${
            p === reliabilityPage
              ? "bg-sidebar-active text-white border-sidebar-active"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
          }`}
          disabled={p === reliabilityPage}
        >
          {p}
        </button>
      ),
    );
  };

  useEffect(() => {
    applyFilters();
  }, [
    history,
    searchTerm,
    filterReason,
    filterNoticePeriod,
    filterAffectsReliability,
    filterModifyDateFrom,
    filterModifyDateTo,
    filterShiftDateFrom,
    filterShiftDateTo,
    filterStatus,
    sortBy,
    sortOrder,
  ]);

  const eventToDialogInitialValues = (ev) => ({
    countTowardsReliability: !!ev?.affects_reliability,
    affectsReliability: !!ev?.affects_reliability,
    reason: ev?.dialog_reason || ev?.reason || "Other",
    notice24h: ev?.notice24h === false || ev?.notice_period === false ? "no" : "yes",
    availability: ev?.availability || "available",
    note: ev?.note || "",
  });

  const editingDialogInitialValues = useMemo(
    () => (editingEvent ? eventToDialogInitialValues(editingEvent) : null),
    [editingEvent],
  );

  const saveReliabilityEvent = async (values) => {
    if (!editingEvent?.shift_account_id || !person?.id || !editingEvent?.editable_unassign) return;
    const reasonCode =
      values.reason === "Sick"
        ? "sick"
        : values.reason === "MIA/No Show"
          ? "mia"
          : "other";
    const res = await fetch(
      `${BASE_URL}/api/personnel/accounts/${person.id}/reliability/${editingEvent.shift_account_id}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          reason: values.reason,
          reasonCode,
          note: values.note || null,
          notice24h: values.notice24h === "yes" ? 1 : 0,
          affectsReliability: !!values.countTowardsReliability,
          availability: values.availability || "available",
        }),
      },
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.success) {
      throw new Error(json.message || "Failed to update reliability event");
    }
    showSuccess("Reliability event updated");
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Reliability History
        </h3>

        {error && <div className="text-sm text-red-600 mb-4">{error}</div>}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active mx-auto mb-3"></div>
            <div className="text-gray-500">Loading reliability data...</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg bg-[#dc1f00] text-white p-4 flex flex-col justify-between min-h-[92px]">
                <div className="text-3xl font-bold leading-none">{score}% Reliable</div>
                <div className="text-xs mt-2 opacity-90">
                  Based on {details.total_assigned ?? history.length ?? 0} shifts
                </div>
              </div>
              <div className="rounded-lg bg-[#dc1f00] text-white p-4 flex flex-col justify-between min-h-[92px]">
                <div className="text-3xl font-bold leading-none">
                  {reasonBuckets.backedOut} of {unreliableCount}
                </div>
                <div className="text-xs mt-2 opacity-90">
                  Backed out less than 24h notice
                </div>
              </div>
              <div className="rounded-lg bg-[#dc1f00] text-white p-4 flex flex-col justify-between min-h-[92px]">
                <div className="text-3xl font-bold leading-none">{reasonBuckets.mia}</div>
                <div className="text-xs mt-2 opacity-90">
                  MIA affecting reliability
                </div>
              </div>
              <div className="rounded-lg bg-[#dc1f00] text-white p-4 flex flex-col justify-between min-h-[92px]">
                <div className="text-3xl font-bold leading-none">{reasonBuckets.sick}</div>
                <div className="text-xs mt-2 opacity-90">
                  Sick affecting reliability
                </div>
              </div>
            </div>

            <AtlasListToolbar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search reliability events..."
              onFilterClick={reliabilityFilterPanel.toggle}
              filterOpen={reliabilityFilterPanel.isOpen}
              onRefresh={load}
              refreshDisabled={loading}
              refreshSpinning={loading}
              resultsMeta={{
                startRow:
                  filteredHistory.length === 0
                    ? 0
                    : (reliabilityPage - 1) * reliabilityPageSize + 1,
                endRow: Math.min(
                  reliabilityPage * reliabilityPageSize,
                  filteredHistory.length,
                ),
                totalCount: filteredHistory.length,
                pageSize: reliabilityPageSize,
                pageSizeOptions: [10, 25, 50, 100, 200],
                onPageSizeChange: (size) => {
                  setReliabilityPageSize(size);
                  setReliabilityPage(1);
                },
                sortBy,
                sortOrder,
                sortOptions: RELIABILITY_SORT_OPTIONS,
                onSortByChange: setSortBy,
                onSortOrderToggle: () =>
                  setSortOrder((o) => (o === "asc" ? "desc" : "asc")),
              }}
              chipBar={
                reliabilityFilterChips.length > 0 ? (
                  <AtlasFilterChipBar
                    chips={reliabilityFilterChips}
                    onChipClick={(chip) =>
                      reliabilityFilterPanel.focusField(chip.fieldId)
                    }
                    onRemove={(chip) => {
                      if (chip.key === "filterReason") setFilterReason("all");
                      if (chip.key === "filterNoticePeriod")
                        setFilterNoticePeriod("");
                      if (chip.key === "filterAffectsReliability")
                        setFilterAffectsReliability("");
                      if (chip.key === "filterStatus") setFilterStatus([]);
                      if (chip.key === "modifyDate") {
                        setFilterModifyDateFrom("");
                        setFilterModifyDateTo("");
                      }
                      if (chip.key === "shiftDate") {
                        setFilterShiftDateFrom("");
                        setFilterShiftDateTo("");
                      }
                    }}
                    onClearAll={resetReliabilityFilters}
                  />
                ) : null
              }
            />

            <EmbeddedFilterPanel
              isOpen={reliabilityFilterPanel.isOpen}
              onClose={reliabilityFilterPanel.close}
              onClear={resetReliabilityFilters}
              footerMode="clearOnly"
            >
              <FilterSelect
                id="reliability-reason"
                label="Reason"
                labelVariant="embedded"
                value={filterReason}
                onChange={setFilterReason}
                options={RELIABILITY_REASON_OPTIONS}
                placeholder={null}
              />
              <FilterSelect
                id="reliability-notice"
                label="Notice Period"
                labelVariant="embedded"
                value={filterNoticePeriod}
                onChange={setFilterNoticePeriod}
                options={RELIABILITY_NOTICE_OPTIONS}
                placeholder={null}
              />
              <FilterSelect
                id="reliability-affects"
                label="Affects Reliability"
                labelVariant="embedded"
                value={filterAffectsReliability}
                onChange={setFilterAffectsReliability}
                options={RELIABILITY_AFFECTS_OPTIONS}
                placeholder={null}
              />
              <FilterField
                id="reliability-modify-from"
                label="From Modify Date"
                labelVariant="embedded"
                focusedFieldId={reliabilityFilterPanel.focusedFieldId}
                onFocusClear={reliabilityFilterPanel.clearFocus}
              >
                <DateInput
                  id="reliability-modify-from-control"
                  value={filterModifyDateFrom}
                  onChange={setFilterModifyDateFrom}
                  className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </FilterField>
              <FilterField
                id="reliability-modify-to"
                label="To Modify Date"
                labelVariant="embedded"
                focusedFieldId={reliabilityFilterPanel.focusedFieldId}
                onFocusClear={reliabilityFilterPanel.clearFocus}
              >
                <DateInput
                  id="reliability-modify-to-control"
                  value={filterModifyDateTo}
                  onChange={setFilterModifyDateTo}
                  className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </FilterField>
              <FilterField
                id="reliability-shift-from"
                label="From Shift Date"
                labelVariant="embedded"
                focusedFieldId={reliabilityFilterPanel.focusedFieldId}
                onFocusClear={reliabilityFilterPanel.clearFocus}
              >
                <DateInput
                  id="reliability-shift-from-control"
                  value={filterShiftDateFrom}
                  onChange={setFilterShiftDateFrom}
                  className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </FilterField>
              <FilterField
                id="reliability-shift-to"
                label="To Shift Date"
                labelVariant="embedded"
                focusedFieldId={reliabilityFilterPanel.focusedFieldId}
                onFocusClear={reliabilityFilterPanel.clearFocus}
              >
                <DateInput
                  id="reliability-shift-to-control"
                  value={filterShiftDateTo}
                  onChange={setFilterShiftDateTo}
                  className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </FilterField>
              <FilterMultiSelect
                id="reliability-status"
                label="Status"
                labelVariant="embedded"
                options={RELIABILITY_STATUS_OPTIONS}
                value={filterStatus}
                onChange={setFilterStatus}
              />
              <FilterSelect
                id="reliability-sort-by"
                label="Sort By"
                labelVariant="embedded"
                value={sortBy}
                onChange={setSortBy}
                options={RELIABILITY_SORT_OPTIONS}
                placeholder={null}
              />
              <FilterSelect
                id="reliability-sort-direction"
                label="Sort Direction"
                labelVariant="embedded"
                value={sortOrder}
                onChange={setSortOrder}
                options={[
                  { value: "asc", label: "Ascending" },
                  { value: "desc", label: "Descending" },
                ]}
                placeholder={null}
              />
            </EmbeddedFilterPanel>

            <div className="border border-gray-200 rounded overflow-x-auto min-w-0 bg-white shadow-sm no-mobile-table-stack">
              <table className="w-full min-w-[600px] text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <SortableTableHeader
                      label="Reason"
                      sortKey="reason"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleReliabilityHeaderSort}
                    />
                    <SortableTableHeader
                      label="Scheduled"
                      sortKey="scheduled_start"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleReliabilityHeaderSort}
                    />
                    <TableHeaderCell label="Affects Reliability" />
                    <SortableTableHeader
                      label="Last Modified"
                      sortKey="last_modified"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleReliabilityHeaderSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayedReliabilityRows.length === 0 ? (
                    <tr>
                      <td
                        className="px-4 py-6 text-center text-gray-400"
                        colSpan={4}
                      >
                        No reliability events found
                      </td>
                    </tr>
                  ) : (
                    displayedReliabilityRows.map((ev, idx) => (
                      <tr
                        key={ev.shift_account_id || `${ev.shift_id}-${ev.shift_date}-${idx}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-xs text-gray-900">
                          {ev.reason ? (
                            <>
                              {ev.editable_unassign ? (
                                <button
                                  type="button"
                                  className="text-left text-red-600 font-medium hover:underline"
                                  onClick={() => setEditingEvent(ev)}
                                >
                                  {ev.reason}
                                </button>
                              ) : (
                                <div className="text-red-600 font-medium">
                                  {ev.reason}
                                </div>
                              )}
                              {ev.notice_label ? (
                                <div className="text-gray-500">{ev.notice_label}</div>
                              ) : null}
                            </>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-900">
                          <div>{formatReliabilityScheduled(ev)}</div>
                          {ev.position_name ? (
                            <div className="text-amber-700">{ev.position_name}</div>
                          ) : null}
                          <div className="text-gray-500">
                            {ev.client_name || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-900">
                          {ev.affects_reliability ? "Yes" : "No"}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-900">
                          <div>
                            {formatAccountDateTime(ev.last_modified || ev.modified_date || ev.shift_date, { emptyValue: "—" })}
                          </div>
                          {ev.last_modified_by_name ? (
                            <div className="text-gray-500">
                              {ev.last_modified_by_name}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {filteredHistory.length > reliabilityPageSize && (
              <div className="flex items-center justify-end mt-4">
                <nav
                  className="flex items-center mt-auto text-sm text-muted-foreground gap-2"
                  aria-label="Pagination"
                >
                  <button
                    type="button"
                    onClick={() => setReliabilityPage((p) => Math.max(1, p - 1))}
                    disabled={reliabilityPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  {renderReliabilityPageButtons()}
                  <button
                    type="button"
                    onClick={() =>
                      setReliabilityPage((p) =>
                        Math.min(reliabilityTotalPages, p + 1),
                      )
                    }
                    disabled={reliabilityPage >= reliabilityTotalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      <UnassignShiftDialog
        open={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        title="Edit Reliability Event"
        staffDisplayName={person?.name || person?.fullName || ""}
        shiftId={editingEvent?.shift_id}
        assignedAccountId={person?.id}
        shiftStartAt={editingEvent?.shift_start_at || null}
        shiftEndAt={editingEvent?.shift_end_at || null}
        initialValues={editingDialogInitialValues}
        onSubmitOverride={saveReliabilityEvent}
        onSuccess={() => setEditingEvent(null)}
        successMessage="Reliability event updated"
      />
    </div>
  );
}

// Custom pay rate position value for "ALL positions" (Global level only; saved as no position)
const PAY_RATE_ALL_POSITIONS = "ALL";

function TempPay({ person }) {
  const MAX_PAY_RATE = 9999.99;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPayRateId, setEditingPayRateId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [positions, setPositions] = useState([]);
  const [clientPositions, setClientPositions] = useState([]);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    level: "Global",
    position_id: "",
    client_id: "",
    pay_rate: "",
  });

  const payFilterPanel = useEmbeddedFilterLayout();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("position");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showDeleted, setShowDeleted] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [payRateMenuOpen, setPayRateMenuOpen] = useState(null); // Will store the pay_rate_id when menu is open
  const payRateMenuRef = useRef(null);
  const [editPositionFallback, setEditPositionFallback] = useState(null);
  const uniqPositionsById = (items = []) => {
    const seen = new Set();
    const unique = [];
    for (const item of items) {
      const id = item.position_id || item.id;
      const rawName = String(item.name || "").trim();
      if (!id || !rawName) continue;
      const idKey = String(id);
      if (seen.has(idKey)) continue;
      seen.add(idKey);
      unique.push({
        ...item,
        position_id: id,
        name: rawName,
        label: item.label || rawName,
      });
    }
    return unique;
  };
  const buildClientPositionLabel = (name, division) => {
    const cleanName = String(name || "").trim();
    const cleanDivision = String(division || "").trim();
    if (!cleanName) return "";
    return cleanDivision ? `${cleanName} — ${cleanDivision}` : cleanName;
  };

  const payFilterChips = useMemo(
    () => buildPersonnelPayFilterChips({ showDeleted }),
    [showDeleted],
  );
  const handleHeaderSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortOrder("asc");
  };

  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/pay${showDeleted ? "?showDeleted=1" : ""}`,
        { credentials: "include" },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to load pay");
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      const errorMsg = e.message || "Failed to load pay";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Pay Rates");
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [positionsRes, clientsRes] = await Promise.all([
        fetch(`${BASE_URL}/api/clients/positions/system-options`, {
          credentials: "include",
        }),
        fetch(`${BASE_URL}/api/personnel/clients`, { credentials: "include" }),
      ]);

      const [positionsData, clientsData] = await Promise.all([
        positionsRes.json().catch(() => ({})),
        clientsRes.json().catch(() => ({})),
      ]);

      const rows = Array.isArray(positionsData?.data) ? positionsData.data : [];
      setPositions(
        rows
          .map((row) => ({
            position_id: row.id,
            name: row.name || row.position_name || "",
            label: row.name || row.position_name || "",
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      );
      if (clientsData.success) setClients(clientsData.data || []);
    } catch (e) {
      console.error("Failed to load dropdowns:", e);
    }
  };

  const loadClientPositions = async (clientId) => {
    if (!clientId) {
      setClientPositions([]);
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/api/clients/${clientId}/positions`, {
        credentials: "include",
      });
      const j = await res.json().catch(() => ({}));
      const rows = Array.isArray(j?.data) ? j.data : [];
      setClientPositions(
        uniqPositionsById(
          rows.map((row) => {
            const name = row.name || "";
            const division = row.division || row.divisionName || "";
            return {
              position_id: row.position_id || row.id,
              name,
              division,
              label: buildClientPositionLabel(name, division),
            };
          }),
        ).sort((a, b) => (a.label || a.name || "").localeCompare(b.label || b.name || "")),
      );
    } catch (e) {
      console.error("Failed to load client positions:", e);
      setClientPositions([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.position_id || !formData.pay_rate) return;
    if (formData.level === "Client" && !formData.client_id) return;
    const numericPayRate = Number(formData.pay_rate);
    if (!Number.isFinite(numericPayRate) || numericPayRate < 0 || numericPayRate > MAX_PAY_RATE) {
      const msg = `Pay rate must be between 0 and ${MAX_PAY_RATE.toFixed(2)}`;
      setError(msg);
      showError(msg, "Invalid Pay Rate");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        position_id: formData.position_id,
        client_id: formData.level === "Client" ? formData.client_id : null,
        pay_rate: numericPayRate,
      };
      let res;
      let j;
      if (editingPayRateId) {
        // Prefer direct update. If backend returns 500, fallback to replace flow.
        res = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}/pay/${editingPayRateId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...getAuthHeaders() },
            credentials: "include",
            body: JSON.stringify(payload),
          },
        );
        j = await res.json().catch(() => ({}));

        if (!res.ok || !j.success) {
          const createRes = await fetch(
            `${BASE_URL}/api/personnel/accounts/${person.id}/pay`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", ...getAuthHeaders() },
              credentials: "include",
              body: JSON.stringify(payload),
            },
          );
          const createJson = await createRes.json().catch(() => ({}));
          if (!createRes.ok || !createJson.success) {
            throw new Error(createJson.message || "Failed to update pay rate");
          }

          const deleteRes = await fetch(
            `${BASE_URL}/api/personnel/accounts/${person.id}/pay/${editingPayRateId}`,
            {
              method: "DELETE",
              credentials: "include",
              headers: { ...getAuthHeaders() },
            },
          );
          const deleteJson = await deleteRes.json().catch(() => ({}));
          if (!deleteRes.ok || !deleteJson.success) {
            throw new Error(deleteJson.message || "Failed to finalize pay rate update");
          }
        }
      } else {
        res = await fetch(`${BASE_URL}/api/personnel/accounts/${person.id}/pay`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        j = await res.json().catch(() => ({}));
        if (!res.ok || !j.success) {
          throw new Error(j.message || "Failed to add pay rate");
        }
      }

      setShowModal(false);
      setEditingPayRateId(null);
      setEditPositionFallback(null);
      setFormData({
        level: "Global",
        position_id: "",
        client_id: "",
        pay_rate: "",
      });
      load(); // Reload the list
      showSuccess(
        editingPayRateId
          ? "Pay rate updated successfully!"
          : "Pay rate added successfully!",
      );
    } catch (e) {
      const errorMsg =
        e.message ||
        (editingPayRateId
          ? "Failed to update pay rate"
          : "Failed to add pay rate");
      setError(errorMsg);
      showError(
        errorMsg,
        editingPayRateId ? "Failed to Update Pay Rate" : "Failed to Add Pay Rate",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingPayRateId(null);
    setEditPositionFallback(null);
    setFormData({
      level: "Global",
      position_id: "",
      client_id: "",
      pay_rate: "",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (row) => {
    setEditingPayRateId(row.pay_rate_id);
    setEditPositionFallback(
      row.position_id
        ? {
            position_id: row.position_id,
            name: row.position_name || "",
            label: row.position_name || "",
          }
        : null,
    );
    setFormData({
      level: row.client_id ? "Client" : "Global",
      position_id: row.position_id
        ? String(row.position_id)
        : row.client_id
          ? ""
          : PAY_RATE_ALL_POSITIONS,
      client_id: row.client_id ? String(row.client_id) : "",
      pay_rate:
        row.custom_pay_rate === null || row.custom_pay_rate === undefined
          ? ""
          : String(row.custom_pay_rate),
    });
    setShowModal(true);
  };

  const handleDelete = async (payRateId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/pay/${payRateId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to delete pay rate");

      load(); // Reload the list
      showSuccess("Pay rate deleted successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to delete pay rate";
      setError(errorMsg);
      showError(errorMsg, "Failed to Delete Pay Rate");
    }
  };

  const handleRestore = async (payRateId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/pay/${payRateId}/restore`,
        {
          method: "PUT",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to restore pay rate");

      load();
      showSuccess("Pay rate restored successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to restore pay rate";
      setError(errorMsg);
      showError(errorMsg, "Failed to Restore Pay Rate");
    }
  };

  // Filter and sort rows
  const filteredAndSortedRows = useMemo(() => {
    let filtered = [...rows];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.position_name || "").toLowerCase().includes(search) ||
          (r.client_name || "").toLowerCase().includes(search) ||
          (r.level || "").toLowerCase().includes(search) ||
          (r.custom_pay_rate?.toString() || "").includes(search),
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "position") {
        aVal = (a.position_name || "").toLowerCase();
        bVal = (b.position_name || "").toLowerCase();
      } else if (sortBy === "client") {
        aVal = (a.client_name || "").toLowerCase();
        bVal = (b.client_name || "").toLowerCase();
      } else if (sortBy === "pay_rate") {
        aVal = parseFloat(a.custom_pay_rate || 0);
        bVal = parseFloat(b.custom_pay_rate || 0);
      } else {
        return 0;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [rows, searchTerm, sortBy, sortOrder]);

  const paginatedRows = filteredAndSortedRows.slice(0, pageSize);

  useEffect(() => {
    load();
    loadDropdowns();
  }, [person?.id]);

  useEffect(() => {
    load();
  }, [showDeleted]);

  useEffect(() => {
    if (formData.level !== "Client") {
      setClientPositions([]);
      return;
    }
    loadClientPositions(formData.client_id);
  }, [formData.level, formData.client_id]);

  const positionOptions = useMemo(() => {
    const source = formData.level === "Client" ? clientPositions : positions;
    const sorted = [...source].sort((a, b) =>
      (a.label || a.name || "").localeCompare(b.label || b.name || ""),
    );

    if (
      editPositionFallback?.position_id &&
      !sorted.some(
        (p) => String(p.position_id) === String(editPositionFallback.position_id),
      )
    ) {
      return uniqPositionsById([...sorted, editPositionFallback]);
    }

    return sorted;
  }, [formData.level, clientPositions, positions, editPositionFallback]);

  const clientDropdownOptions = useMemo(
    () =>
      [...clients]
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((client) => ({
          value: String(client.id || client.client_id),
          label: client.name || "",
        })),
    [clients],
  );

  const positionDropdownOptions = useMemo(() => {
    const options = positionOptions.map((p) => ({
      value: String(p.position_id),
      label: p.label || p.name,
    }));
    // Global rates can apply to ALL positions (saved with no position)
    return formData.level === "Global"
      ? [{ value: PAY_RATE_ALL_POSITIONS, label: "All Positions" }, ...options]
      : options;
  }, [positionOptions, formData.level]);

  return (
    <div className="space-y-4 relative">
      <div className="text-sm font-semibold text-gray-700">
        Custom Pay Rates
      </div>

      {/* Description Box with bullet points */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-sm text-red-800 space-y-1">
          <div>
            • The greater between Temp Staff custom pay rates and client pay
            rates will be used when shifts are assigned.
          </div>
          <div>
            • Whenever a staff is unassigned from a shift, the shift's pay rate
            will revert to the amount set prior to assignment.
          </div>
          <div>
            • If the Temp Staff has both a global position and client position
            custom pay rate, the client position custom pay rate will be used.
          </div>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <AtlasListToolbar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search"
        onFilterClick={payFilterPanel.toggle}
        filterOpen={payFilterPanel.isOpen}
        onRefresh={load}
        refreshDisabled={loading}
        refreshSpinning={loading}
        resultsMeta={{
          startRow: filteredAndSortedRows.length === 0 ? 0 : 1,
          endRow: Math.min(pageSize, filteredAndSortedRows.length),
          totalCount: filteredAndSortedRows.length,
          pageSize,
          pageSizeOptions: [10, 25, 50, 100, 200],
          onPageSizeChange: setPageSize,
          sortBy,
          sortOrder,
          sortOptions: PAY_SORT_OPTIONS,
          onSortByChange: setSortBy,
          onSortOrderToggle: () =>
            setSortOrder((o) => (o === "asc" ? "desc" : "asc")),
        }}
        actions={
          <button
            type="button"
            onClick={handleOpenAdd}
            className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
          >
            CREATE
          </button>
        }
        chipBar={
          payFilterChips.length > 0 ? (
            <AtlasFilterChipBar
              chips={payFilterChips}
              onChipClick={(chip) => payFilterPanel.focusField(chip.fieldId)}
              onRemove={(chip) => {
                if (chip.key === "showDeleted") setShowDeleted(false);
              }}
              onClearAll={() => {
                setShowDeleted(false);
                setSearchTerm("");
                setSortBy("position");
                setSortOrder("asc");
              }}
            />
          ) : null
        }
      />

      <EmbeddedFilterPanel
        isOpen={payFilterPanel.isOpen}
        onClose={payFilterPanel.close}
        onClear={() => {
          setShowDeleted(false);
          setSearchTerm("");
          setSortBy("position");
          setSortOrder("asc");
        }}
        footerMode="clearOnly"
      >
        <FilterToggle
          id="pay-show-deleted"
          label="Show Deleted"
          checked={showDeleted}
          onChange={setShowDeleted}
        />
        <FilterSelect
          id="pay-sort-by"
          label="Sort By"
          labelVariant="embedded"
          value={sortBy}
          onChange={setSortBy}
          options={PAY_SORT_OPTIONS}
          placeholder={null}
        />
        <FilterSelect
          id="pay-sort-direction"
          label="Sort Direction"
          labelVariant="embedded"
          value={sortOrder}
          onChange={setSortOrder}
          options={[
            { value: "asc", label: "Ascending" },
            { value: "desc", label: "Descending" },
          ]}
          placeholder={null}
        />
      </EmbeddedFilterPanel>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : filteredAndSortedRows.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No pay rates configured
        </div>
      ) : (
        <>
          <div className="border border-gray-200 rounded bg-white shadow-sm overflow-x-auto relative no-mobile-table-stack">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <SortableTableHeader
                    label="Position"
                    sortKey="position"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <SortableTableHeader
                    label="Client"
                    sortKey="client"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <SortableTableHeader
                    label="Pay Rate"
                    sortKey="pay_rate"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <TableHeaderCell label="Actions" />
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((r) => (
                  <tr
                    key={r.pay_rate_id}
                    className="border-t border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-3 py-2">
                      {showDeleted ? (
                        <span>{r.position_name || "�"}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(r)}
                          className="text-red-600 hover:text-red-700 hover:underline"
                          title="Edit pay rate"
                        >
                          {r.position_name || "�"}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">{r.client_name || ""}</td>
                    <td className="px-3 py-2">${r.custom_pay_rate ?? "�"}</td>
                    <td className="px-3 py-2">
                      <div className="relative" ref={payRateMenuRef}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPayRateMenuOpen(
                              payRateMenuOpen === r.pay_rate_id
                                ? null
                                : r.pay_rate_id,
                            );
                          }}
                          className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                          title="Pay Rate Options"
                        >
                          <FaEllipsisV className="text-gray-600" />
                        </button>
                        {payRateMenuOpen === r.pay_rate_id && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setPayRateMenuOpen(null)}
                            />
                            <div
                              className="fixed right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                              style={{
                                top: payRateMenuRef.current
                                  ? `${payRateMenuRef.current.getBoundingClientRect().bottom + 4}px`
                                  : "auto",
                                right: payRateMenuRef.current
                                  ? `${window.innerWidth - payRateMenuRef.current.getBoundingClientRect().right}px`
                                  : "16px",
                              }}
                            >
                              <div
                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-red-600"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setPayRateMenuOpen(null);
                                  if (showDeleted) {
                                    if (
                                      await showConfirm(
                                        "Are you sure you want to restore this pay rate?",
                                      )
                                    ) {
                                      handleRestore(r.pay_rate_id);
                                    }
                                  } else if (
                                    await showConfirm(
                                      "Are you sure you want to delete this pay rate?",
                                    )
                                  ) {
                                    handleDelete(r.pay_rate_id);
                                  }
                                }}
                              >
                                <div className="text-sm font-medium">
                                  {showDeleted ? "Restore" : "Delete"}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {showDeleted
                                    ? "Restore this custom pay rate"
                                    : "Remove this custom pay rate"}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add Pay Rate Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
            <h3 className="text-lg font-semibold mb-4">
              {editingPayRateId ? "Edit Custom Pay Rate" : "Add Custom Pay Rate"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position Level
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="level"
                      value="Global"
                      checked={formData.level === "Global"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          level: e.target.value,
                          client_id: "",
                          position_id: "",
                        })
                      }
                      className="mr-2"
                    />
                    Global
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="level"
                      value="Client"
                      checked={formData.level === "Client"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          level: e.target.value,
                          position_id: "",
                        })
                      }
                      className="mr-2"
                    />
                    Client
                  </label>
                </div>
              </div>

              {formData.level === "Client" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client <span className="text-red-500">*</span>
                  </label>
                  <ClickableDropdown
                    value={formData.client_id ? String(formData.client_id) : ""}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        client_id: value,
                        position_id: "",
                      })
                    }
                    options={clientDropdownOptions}
                    placeholder="Search client..."
                    searchable
                    clearable={false}
                    className="rounded px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position <span className="text-red-500">*</span>
                </label>
                <ClickableDropdown
                  value={formData.position_id ? String(formData.position_id) : ""}
                  onChange={(value) =>
                    setFormData({ ...formData, position_id: value })
                  }
                  options={positionDropdownOptions}
                  placeholder={
                    formData.level === "Client" && !formData.client_id
                      ? "Select client first..."
                      : "Search position..."
                  }
                  searchable
                  clearable={false}
                  disabled={formData.level === "Client" && !formData.client_id}
                  className="rounded px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Pay Rate ($) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={MAX_PAY_RATE}
                  value={formData.pay_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, pay_rate: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 no-number-spinner"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPayRateId(null);
                    setEditPositionFallback(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Saving..." : editingPayRateId ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TempFiles({ person, actionMenuZClass }) {
  const contextZClass = useContext(PersonnelModalZContext);
  const effectiveMenuZClass = actionMenuZClass || contextZClass || "z-[10150]";
  const ALLOWED_FILE_TYPE_NAMES = new Set([
    "Object PDF",
    "SIN Document",
    "Identity Document",
    "Resume and Other",
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  // SIN Document expiry: optional for Canadian Citizen / PR (server "citizen"), required for
  // Work Permit, Study Permit and Refugee (same rule as registration Documents)
  const [residency, setResidency] = useState("");
  const sinExpiryRequired = !["citizen", "canadian"].includes(String(residency || "").toLowerCase());

  useEffect(() => {
    if (!person?.id) return;
    let cancelled = false;
    fetch(`${BASE_URL}/api/personnel/accounts/${person.id}`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled) setResidency(json?.data?.residency_status || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [person?.id]);
  const [formData, setFormData] = useState({
    state: "active",
    type_id: "",
    note: "",
    file: null,
    expiry_date: "",
  });
  const [filterData, setFilterData] = useState({
    states: [],
    type_ids: [],
    showDeleted: false,
  }); // Separate state for filters (empty = show all)
  const [rawSearch, setRawSearch] = useState(""); // input value (user types anything)
  const [searchTerm, setSearchTerm] = useState(""); // cleaned value for filtering

  const filesFilterPanel = useEmbeddedFilterLayout();
  const [sortBy, setSortBy] = useState("file_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [pageSize, setPageSize] = useState(25);

  const handleHeaderSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortOrder("asc");
  };

  // Edit modal state
  const [editingFile, setEditingFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [editFormData, setEditFormData] = useState({
    state: "",
    type_id: "",
    note: "",
    file: null,
    expiry_date: "",
  });
  const [editFormOriginal, setEditFormOriginal] = useState({
    state: "",
    type_id: "",
    note: "",
    file: null,
    expiry_date: "",
  });
  const allowedFileTypes = useMemo(
    () =>
      (fileTypes || []).filter((t) =>
        ALLOWED_FILE_TYPE_NAMES.has((t.name || "").trim()),
      ),
    [fileTypes],
  );
  const filesFilterChips = useMemo(
    () => buildPersonnelFilesFilterChips(filterData, { fileTypes: allowedFileTypes }),
    [filterData, allowedFileTypes],
  );
  const fileTypeFilterOptions = useMemo(
    () =>
      allowedFileTypes.map((t) => ({
        value: String(t.type_id),
        label: t.name,
      })),
    [allowedFileTypes],
  );
  const editFileTypeOptions = useMemo(() => {
    const base = Array.isArray(allowedFileTypes) ? [...allowedFileTypes] : [];
    if (
      editingFile?.type_id &&
      !base.some((t) => String(t.type_id) === String(editingFile.type_id))
    ) {
      base.unshift({
        type_id: editingFile.type_id,
        name: editingFile.type_name || `Type ${editingFile.type_id}`,
      });
    }
    return base;
  }, [allowedFileTypes, editingFile]);

  const fileTypeNameById = (id) =>
    (fileTypes || []).find((t) => String(t.type_id) === String(id))?.name?.trim() ||
    "";

  const renderExpiryCell = (row) => {
    if ((row.type_name || "").trim() !== "SIN Document") return "—";
    if (!row.expiry_date) return "—";
    const ymd = String(row.expiry_date).slice(0, 10);
    if (row.is_expired) {
      return (
        <span>
          {ymd}{" "}
          <span className="text-red-600 font-semibold text-xs ml-1">Expired</span>
        </span>
      );
    }
    return ymd;
  };

  // const types = [
  //   "Object PDF",
  //   "SIN Document",
  //   "Identity Document",
  //   "Resume",
  //   "Other"
  // ];

  const cleanSearchText = (text = "") => {
    return text.toLowerCase().replace(/[^a-z0-9]/gi, ""); // keep only letters + numbers
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;

    // ✅ keep the input as user typed
    setRawSearch(value);

    // ✅ use cleaned version for searching
    setSearchTerm(cleanSearchText(value));
  };

  const filteredRows = rows.filter((r) => {
    let matches = true;

    // Filter by status (multi-select)
    if ((filterData.states || []).length > 0) {
      let fileState = r.state;
      if (fileState === "pendingReview" || fileState === "pending_review") {
        fileState = "Pending Review";
      }
      matches = matches && filterData.states.includes(fileState);
    }

    // Filter by type (multi-select)
    if ((filterData.type_ids || []).length > 0) {
      matches =
        matches && filterData.type_ids.includes(String(r.type_id || ""));
    }

    // Filter by search box
    if (searchTerm.trim() !== "") {
      const s = cleanSearchText(searchTerm);

      const fileName = cleanSearchText(r.file_name || "");
      const note = cleanSearchText(r.note || "");
      const typeClass = cleanSearchText(r.type_class || "");

      matches =
        matches &&
        (fileName.includes(s) || note.includes(s) || typeClass.includes(s));
    }

    return matches;
  });

  const sortedRows = [...filteredRows].sort((a, b) => {
    let x = a[sortBy];
    let y = b[sortBy];

    if (typeof x === "string") x = x.toLowerCase();
    if (typeof y === "string") y = y.toLowerCase();

    if (x < y) return sortOrder === "asc" ? -1 : 1;
    if (x > y) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const pagedRows = sortedRows.slice(0, pageSize);

  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const query = filterData.showDeleted ? "?showDeleted=true" : "";
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files${query}`,
        { credentials: "include" },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to load files");
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      const errorMsg = e.message || "Failed to load files";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Files");
    } finally {
      setLoading(false);
    }
  };

  const loadFileTypes = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/file-types`, {
        credentials: "include",
      });
      const j = await res.json().catch(() => ({}));
      if (j.success) setFileTypes(j.data || []);
    } catch (e) {
      console.error("Failed to load file types:", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.type_id || !formData.file) return;
    if (fileTypeNameById(formData.type_id) === "SIN Document" && sinExpiryRequired && !formData.expiry_date) {
      showError("Expiry date is required for the SIN Document of this residency status.", "Expiry Date Required");
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("state", formData.state);
      formDataToSend.append("type_id", formData.type_id);
      formDataToSend.append("note", formData.note || "");
      if (fileTypeNameById(formData.type_id) === "SIN Document") {
        formDataToSend.append("expiry_date", formData.expiry_date || "");
      }
      formDataToSend.append("file", formData.file); // Add the actual file

      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files`,
        {
          method: "POST",
          // When sending FormData do not set Content-Type; let the browser set multipart boundary
          headers: { ...getAuthHeaders() },
          credentials: "include",
          body: formDataToSend,
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to add file");

      setShowModal(false);
      setFormData({ state: "active", type_id: "", note: "", file: null, expiry_date: "" });
      load(); // Reload the list
      showSuccess("File uploaded successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to add file";
      setError(errorMsg);
      showError(errorMsg, "Failed to Upload File");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!(await showConfirm("Are you sure you want to delete this file?")))
      return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${fileId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to delete file");

      load(); // Reload the list
      showSuccess("File deleted successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to delete file";
      setError(errorMsg);
      showError(errorMsg, "Failed to Delete File");
    }
  };

  const handleRestore = async (fileId) => {
    if (!(await showConfirm("Are you sure you want to restore this file?")))
      return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${fileId}/restore`,
        {
          method: "PUT",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to restore file");

      load();
      showSuccess("File restored successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to restore file";
      setError(errorMsg);
      showError(errorMsg, "Failed to Restore File");
    }
  };

  const formatState = (state) => {
    if (!state) return "—";
    // Capitalize first letter of each word
    if (state === "active") return "Active";
    if (state === "inactive") return "Inactive";
    if (
      state === "Pending Review" ||
      state === "pendingReview" ||
      state === "pending_review"
    )
      return "Pending Review";
    // Fallback: capitalize first letter
    return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
  };

  const handleEdit = (file) => {
    const rawState = String(file.state || "").trim();
    const normalizedStateKey = rawState
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .toLowerCase();
    const normalizedState =
      normalizedStateKey === "pending review" ||
      normalizedStateKey === "pending approval"
        ? "Pending Review"
        : rawState || "active";
    setEditingFile(file);
    const expYmd =
      file.expiry_date != null && file.expiry_date !== ""
        ? String(file.expiry_date).slice(0, 10)
        : "";
    const initialData = {
      state: normalizedState,
      type_id: String(file.type_id || ""),
      note: file.note || "",
      file: null,
      expiry_date: expYmd,
    };
    setEditFormData(initialData);
    setEditFormOriginal(initialData);
  };

  const handleEditCancel = () => {
    setEditingFile(null);

    setEditFormData({ state: "", type_id: "", note: "", file: null, expiry_date: "" });
    setEditFormOriginal({ state: "", type_id: "", note: "", file: null, expiry_date: "" });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingFile) return;
    if (fileTypeNameById(editFormData.type_id) === "SIN Document" && sinExpiryRequired && !editFormData.expiry_date) {
      showError("Expiry date is required for the SIN Document of this residency status.", "Expiry Date Required");
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("state", editFormData.state);
      formDataToSend.append("type_id", editFormData.type_id);
      formDataToSend.append("note", editFormData.note || "");
      if (fileTypeNameById(editFormData.type_id) === "SIN Document") {
        formDataToSend.append("expiry_date", editFormData.expiry_date || "");
      }
      if (editFormData.file) {
        formDataToSend.append("file", editFormData.file);
      }

      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${editingFile.file_id}`,
        {
          method: "PUT",
          headers: { ...getAuthHeaders() },
          credentials: "include",
          body: formDataToSend,
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to update file");

      handleEditCancel();
      load(); // Reload the list
      showSuccess("File updated successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to update file";
      setError(errorMsg);
      showError(errorMsg, "Failed to Update File");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadFile = async (file) => {
    if (!file || !person?.id) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${file.file_id}/download`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );

      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: "Failed to download file" }));
        throw new Error(error.message || "Failed to download file");
      }

      // Backend streams the file directly — read as blob, no S3 URL involved
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cd = res.headers.get("content-disposition");
      link.download = cd
        ? decodeURIComponent(
            (cd.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i) || [])[1] || "",
          ) ||
          file.name ||
          file.file_name ||
          "file"
        : file.name || file.file_name || "file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError(e.message || "Failed to download file", "Download Error");
    }
  };

  const loadFilePreview = async (file) => {
    if (!file || !person?.id) throw new Error("File not found");
    const res = await fetch(
      `${BASE_URL}/api/personnel/accounts/${person.id}/files/${file.file_id}/download`,
      { credentials: "include", headers: { ...getAuthHeaders() } },
    );
    if (!res.ok) {
      const error = await res
        .json()
        .catch(() => ({ message: "Failed to preview file" }));
      throw new Error(error.message || "Failed to preview file");
    }
    return res.blob();
  };

  const handleDownload = async () => {
    if (!editingFile) return;
    await handleDownloadFile(editingFile);
  };

  // Check if edit form has changes
  const hasEditChanges =
    editingFile &&
    (editFormData.state !== editFormOriginal.state ||
      editFormData.type_id !== editFormOriginal.type_id ||
      editFormData.note !== editFormOriginal.note ||
      editFormData.expiry_date !== editFormOriginal.expiry_date ||
      editFormData.file !== null);

  useEffect(() => {
    load();
    loadFileTypes();
  }, [person?.id, filterData.showDeleted]);

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Files for {person?.name || `#${person?.id}`}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <AtlasListToolbar
        searchValue={rawSearch}
        onSearchChange={(v) => {
          setRawSearch(v);
          setSearchTerm(cleanSearchText(v));
        }}
        searchPlaceholder="Search"
        onFilterClick={filesFilterPanel.toggle}
        filterOpen={filesFilterPanel.isOpen}
        onRefresh={load}
        refreshDisabled={loading}
        refreshSpinning={loading}
        resultsMeta={{
          startRow: filteredRows.length === 0 ? 0 : 1,
          endRow: Math.min(pageSize, filteredRows.length),
          totalCount: filteredRows.length,
          pageSize,
          pageSizeOptions: [10, 25, 50, 100, 200],
          onPageSizeChange: setPageSize,
          sortBy,
          sortOrder,
          sortOptions: FILE_SORT_OPTIONS,
          onSortByChange: setSortBy,
          onSortOrderToggle: () =>
            setSortOrder((o) => (o === "asc" ? "desc" : "asc")),
        }}
        chipBar={
          filesFilterChips.length > 0 ? (
            <AtlasFilterChipBar
              chips={filesFilterChips}
              onChipClick={(chip) => filesFilterPanel.focusField(chip.fieldId)}
              onRemove={(chip) => {
                if (chip.key === "type_ids") {
                  setFilterData((prev) => ({ ...prev, type_ids: [] }));
                } else if (chip.key === "states") {
                  setFilterData((prev) => ({ ...prev, states: [] }));
                } else if (chip.key === "showDeleted") {
                  setFilterData((prev) => ({ ...prev, showDeleted: false }));
                }
              }}
              onClearAll={() => {
                setFilterData({ type_ids: [], states: [], showDeleted: false });
                setSortBy("file_name");
                setSortOrder("asc");
                setRawSearch("");
                setSearchTerm("");
              }}
            />
          ) : null
        }
      />

      <EmbeddedFilterPanel
        isOpen={filesFilterPanel.isOpen}
        onClose={filesFilterPanel.close}
        onClear={() => {
          setFilterData({ type_ids: [], states: [], showDeleted: false });
          setSortBy("file_name");
          setSortOrder("asc");
          setRawSearch("");
          setSearchTerm("");
        }}
        footerMode="clearOnly"
      >
        <FilterMultiSelect
          id="file-types"
          label="Type"
          labelVariant="embedded"
          options={fileTypeFilterOptions}
          value={filterData.type_ids || []}
          onChange={(type_ids) => setFilterData((prev) => ({ ...prev, type_ids }))}
        />
        <FilterMultiSelect
          id="file-states"
          label="Status"
          labelVariant="embedded"
          options={FILE_STATE_OPTIONS}
          value={filterData.states || []}
          onChange={(states) => setFilterData((prev) => ({ ...prev, states }))}
          formatLabel={(v) => formatState(v)}
        />
        <FilterToggle
          id="file-show-deleted"
          label="Show Deleted"
          checked={!!filterData.showDeleted}
          onChange={(showDeleted) =>
            setFilterData((prev) => ({ ...prev, showDeleted }))
          }
        />
        <FilterSelect
          id="file-sort-by"
          label="Sort By"
          labelVariant="embedded"
          value={sortBy}
          onChange={setSortBy}
          options={FILE_SORT_OPTIONS}
          placeholder={null}
        />
        <FilterSelect
          id="file-sort-direction"
          label="Sort Direction"
          labelVariant="embedded"
          value={sortOrder}
          onChange={setSortOrder}
          options={[
            { value: "asc", label: "Ascending" },
            { value: "desc", label: "Descending" },
          ]}
          placeholder={null}
        />
      </EmbeddedFilterPanel>

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {filteredRows.length} file{filteredRows.length !== 1 ? "s" : ""}{" "}
          uploaded
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
        Add File
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No files uploaded
        </div>
      ) : (
        <div className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
          {/* Mobile Compact Grid View */}
          <div className="md:hidden flex flex-col w-full bg-white">
            {/* Header */}
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1.5fr_28px] gap-1.5 px-2 py-2 border-b-2 border-gray-200 text-[11px] font-semibold text-gray-500">
              <div className="flex items-center gap-1">
                Name
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
              </div>
              <div className="flex items-center">Note</div>
              <div className="flex items-center">State</div>
              <div className="flex items-center">Type</div>
              <div></div>
            </div>
            
            {/* Body */}
            <div className="flex flex-col divide-y divide-gray-100">
              {pagedRows.map((r) => (
                <div key={`mob-${r.file_id}`} className="grid grid-cols-[1.5fr_1fr_1fr_1.5fr_28px] gap-1.5 px-2 py-3 items-center text-[11px] sm:text-xs">
                  {/* Name */}
                  <div className="leading-tight break-words pr-1">
                    <button
                      onClick={() => (r.state || "").toLowerCase() !== "deleted" && handleEdit(r)}
                      className={`text-left font-medium ${
                        (r.state || "").toLowerCase() === "deleted"
                          ? "text-red-500/50 cursor-not-allowed"
                          : "text-red-500 hover:text-red-600 cursor-pointer"
                      }`}
                      disabled={(r.state || "").toLowerCase() === "deleted"}
                    >
                      {r.file_name || "—"}
                    </button>
                  </div>
                  {/* Note */}
                  <div className="leading-tight text-gray-600 break-words">
                    {r.note || ""}
                  </div>
                  {/* State */}
                  <div className="leading-tight text-gray-800 break-words font-medium">
                    {formatState(r.state)}
                  </div>
                  {/* Type */}
                  <div className="leading-tight text-gray-800 break-words">
                    {r.type_name || "—"}
                  </div>
                  {/* Actions */}
                  <div className="flex justify-end items-center">
                    <ActionDropdown
                      menuZIndexClass={effectiveMenuZClass}
                      actions={
                        (r.state || "").toLowerCase() === "deleted"
                          ? [
                              {
                                label: "Restore",
                                onClick: () => handleRestore(r.file_id),
                              },
                            ]
                          : [
                              {
                                label: "Preview",
                                onClick: () => setPreviewFile(r),
                              },
                              {
                                label: "Download",
                                onClick: () => handleDownloadFile(r),
                              },
                              {
                                label: "Delete",
                                onClick: () => handleDelete(r.file_id),
                              },
                            ]
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto min-w-0">
            <table className="w-full min-w-[600px] text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                <tr>
                  <SortableTableHeader
                    label="Name"
                    sortKey="file_name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <TableHeaderCell label="Note" />
                  <SortableTableHeader
                    label="State"
                    sortKey="state"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <SortableTableHeader
                    label="Type"
                    sortKey="type_name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <SortableTableHeader
                    label="Expiry"
                    sortKey="expiry_date"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <TableHeaderCell label="Created" />
                  <TableHeaderCell label="Actions" className="text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagedRows.map((r) => (
                  <tr key={r.file_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium">
                      <button
                        onClick={() => (r.state || "").toLowerCase() !== "deleted" && handleEdit(r)}
                        className={
                          (r.state || "").toLowerCase() === "deleted"
                            ? "text-red-600/50 cursor-not-allowed text-left"
                            : "text-red-600 hover:text-red-700 cursor-pointer text-left"
                        }
                        disabled={(r.state || "").toLowerCase() === "deleted"}
                      >
                        {r.file_name || "—"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.note || "—"}</td>
                    <td className="px-4 py-3">{formatState(r.state)}</td>
                    <td className="px-4 py-3 text-gray-600">{r.type_name || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{renderExpiryCell(r)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatAccountDateTime(r.create_date, { emptyValue: "—" })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ActionDropdown
                        menuZIndexClass={effectiveMenuZClass}
                        actions={
                          (r.state || "").toLowerCase() === "deleted"
                            ? [
                                {
                                  label: "Restore",
                                  onClick: () => handleRestore(r.file_id),
                                },
                              ]
                          : [
                              {
                                label: "Preview",
                                onClick: () => setPreviewFile(r),
                              },
                              {
                                label: "Download",
                                  onClick: () => handleDownloadFile(r),
                                },
                                {
                                  label: "Delete",
                                  onClick: () => handleDelete(r.file_id),
                                },
                              ]
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add File Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add File</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="Pending Review">Pending Review</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type_id}
                  onChange={(e) =>
                    setFormData({ ...formData, type_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="">Select file type...</option>
                  {allowedFileTypes.map((t) => (
                    <option key={t.type_id} value={t.type_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {fileTypeNameById(formData.type_id) === "SIN Document" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {sinExpiryRequired ? (
                      <>Expiry date <span className="text-red-500">*</span></>
                    ) : (
                      "Expiry date (optional)"
                    )}
                  </label>
                  <DateInput
                    value={formData.expiry_date}
                    onChange={(value) =>
                      setFormData({ ...formData, expiry_date: value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) =>
                    setFormData({ ...formData, note: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows={3}
                  placeholder="Optional note about this file..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  onChange={(e) =>
                    (() => {
                      const file = e.target.files[0];
                      const validationMessage = file && validateStaffDocumentFile(file);
                      if (validationMessage) {
                        showError(validationMessage);
                        e.target.value = "";
                        return;
                      }
                      setFormData({ ...formData, file });
                    })()
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit File Modal */}
      {editingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit File</h3>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={editFormData.state}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, state: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="Pending Review">Pending Review</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={editFormData.type_id}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      type_id: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="">Select file type...</option>
                  {allowedFileTypes.map((t) => (
                    <option key={t.type_id} value={t.type_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {fileTypeNameById(editFormData.type_id) === "SIN Document" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {sinExpiryRequired ? (
                      <>Expiry date <span className="text-red-500">*</span></>
                    ) : (
                      "Expiry date (optional)"
                    )}
                  </label>
                  <DateInput
                    value={editFormData.expiry_date}
                    onChange={(value) =>
                      setEditFormData({
                        ...editFormData,
                        expiry_date: value,
                      })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={editFormData.note}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, note: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows={3}
                  placeholder="Optional note about this file..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File
                </label>
                <div className="border border-gray-300 rounded-lg p-4 bg-white relative">
                  {editFormData.file ? (
                    // Show newly selected file
                    <div className="flex flex-col items-center justify-center py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-700 font-medium">
                        {editFormData.file.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(editFormData.file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  ) : editingFile ? (
                    // Show current uploaded file
                    <div className="flex flex-col items-center justify-center py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-2 relative">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        {editingFile.extension && (
                          <span className="absolute bottom-1 text-xs font-semibold text-gray-600">
                            {editingFile.extension.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 font-medium text-center">
                        {(() => {
                          // Extract filename from S3 URL
                          const url = editingFile.file_url || "";
                          if (url) {
                            const parts = url.split("/");
                            const filename = parts[parts.length - 1];
                            const cleanFilename = filename.includes("_")
                              ? filename.substring(filename.indexOf("_") + 1)
                              : filename;
                            return cleanFilename || url;
                          }
                          return editingFile.file_name || "No file";
                        })()}
                      </p>
                    </div>
                  ) : (
                    // Show empty state
                    <div className="flex flex-col items-center justify-center py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500">No file uploaded</p>
                    </div>
                  )}

                  {/* Upload button in bottom right */}
                  <label className="absolute bottom-3 right-3 w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors">
                    <input
                      type="file"
                      accept={STAFF_DOCUMENT_ACCEPT}
                      onChange={(e) =>
                        (() => {
                          const file = e.target.files[0];
                          const validationMessage = file && validateStaffDocumentFile(file);
                          if (validationMessage) {
                            showError(validationMessage);
                            e.target.value = "";
                            return;
                          }
                          setEditFormData({ ...editFormData, file });
                        })()
                      }
                      className="hidden"
                    />
                    <FaUpload className="w-5 h-5 text-white" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewFile(editingFile)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Preview
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={saving || !hasEditChanges}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DocumentPreviewModal
        isOpen={Boolean(previewFile)}
        file={previewFile}
        loadPreview={loadFilePreview}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
}
// form section
function TempForms({ person, actionMenuZClass }) {
  const contextZClass = useContext(PersonnelModalZContext);
  const effectiveMenuZClass = actionMenuZClass || contextZClass || "z-[10150]";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formTypes, setFormTypes] = useState([]);
  const [formData, setFormData] = useState({
    state: "active",
    form_id: "",
    data: "",
  });

  const formsFilterPanel = useEmbeddedFilterLayout();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [stateFilter, setStateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("form_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showDeleted, setShowDeleted] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [formsMenuOpen, setFormsMenuOpen] = useState(null);
  const pagedRows = filteredRows.slice(0, pageSize);
  const formsFilterChips = useMemo(
    () => buildPersonnelFormsFilterChips({ stateFilter, showDeleted }),
    [stateFilter, showDeleted],
  );
  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/forms`,
        { credentials: "include" },
      );
      const j = await res.json().catch(() => ({}));

      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to load forms");

      const data = Array.isArray(j.data) ? j.data : [];

      setRows(data); // original list; filtered list is derived via applyFilters
    } catch (e) {
      const errorMsg = e.message || "Failed to load forms";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Forms");
    } finally {
      setLoading(false);
    }
  };

  const loadFormTypes = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/forms`, {
        credentials: "include",
      });
      const j = await res.json().catch(() => ({}));
      if (j.success) setFormTypes(j.data || []);
    } catch (e) {
      console.error("Failed to load form types:", e);
    }
  };
  const applyFilters = (
    searchValue,
    stateValue,
    sortKey,
    sortDir,
    showDeletedValue = showDeleted,
  ) => {
    let filtered = [...rows];

    // 1. SEARCH (form_name, form_state, submit_date)
    if (searchValue.trim() !== "") {
      const q = searchValue.toLowerCase();

      filtered = filtered.filter(
        (r) =>
          (r.form_name && r.form_name.toLowerCase().includes(q)) ||
          (r.form_state && r.form_state.toLowerCase().includes(q)) ||
          (r.submit_date && r.submit_date.toLowerCase().includes(q)),
      );
    }

    // 2. FILTER BY STATE
    if (stateValue !== "all") {
      filtered = filtered.filter((r) => r.form_state === stateValue);
    }

    // 3. SHOW DELETED toggle: either only deleted or only non-deleted
    filtered = filtered.filter((r) => {
      const state = String(r.form_state || "").toLowerCase().trim();
      const isDeleted = state === "deleted";
      return showDeletedValue ? isDeleted : !isDeleted;
    });

    // 4. SORTING
    filtered.sort((a, b) => {
      const x = a[sortKey]?.toString().toLowerCase() || "";
      const y = b[sortKey]?.toString().toLowerCase() || "";

      return sortDir === "asc" ? x.localeCompare(y) : y.localeCompare(x);
    });

    // 5. UPDATE UI LIST
    setFilteredRows(filtered);
  };

  const handleHeaderSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortOrder("asc");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.form_id) return;

    setSaving(true);
    try {
      let parsedData = null;
      if (formData.data && formData.data.trim()) {
        try {
          parsedData = JSON.parse(formData.data);
        } catch {
          parsedData = formData.data;
        }
      }

      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/forms`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({
            form_id: formData.form_id,
            state: formData.state,
            data: parsedData,
          }),
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to submit form");

      setShowModal(false);
      setFormData({ state: "active", form_id: "", data: "" });
      load(); // Reload the list
      showSuccess("Form submitted successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to submit form";
      setError(errorMsg);
      showError(errorMsg, "Failed to Submit Form");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (submissionId) => {
    if (!person?.id || !submissionId) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/forms/${submissionId}/download`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );

      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: "Failed to download form" }));
        throw new Error(error.message || "Failed to download form");
      }

      // If API returns JSON, treat it as an error/info payload (never redirect to S3 URL).
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Data no longer exists");
      } else {
        // Direct file download stream (pdf or other file types)
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = res.headers.get("content-disposition");
        let filename = "form.pdf";
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename="([^"]+)"/);
          if (matches) filename = matches[1];
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showError(e.message || "Failed to download form", "Download Error");
    }
  };

  const handleView = async (submissionId) => {
    if (!person?.id || !submissionId) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/forms/${submissionId}/download?view=true`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );

      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: "Failed to view form" }));
        throw new Error(error.message || "Failed to view form");
      }

      // If API returns JSON, treat it as an error/info payload (never redirect to S3 URL).
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Data no longer exists");
      } else {
        // Direct streamed file view (pdf or other file types)
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        // Clean up the object URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      showError(e.message || "Failed to view form", "View Error");
    }
  };

  const handleDelete = async (submissionId) => {
    if (!(await showConfirm("Are you sure you want to delete this form?")))
      return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/forms/${submissionId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to delete form submission");

      load(); // Reload the list
      showSuccess("Form submission deleted successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to delete form submission";
      setError(errorMsg);
      showError(errorMsg, "Failed to Delete Form Submission");
    }
  };

  const handleRestore = async (submissionId) => {
    if (!(await showConfirm("Are you sure you want to restore this form?")))
      return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/forms/${submissionId}/restore`,
        {
          method: "PUT",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to restore form submission");

      load();
      showSuccess("Form submission restored successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to restore form submission";
      setError(errorMsg);
      showError(errorMsg, "Failed to Restore Form Submission");
    }
  };

  useEffect(() => {
    load();
    loadFormTypes();
  }, [person?.id]);

  useEffect(() => {
    applyFilters(searchTerm, stateFilter, sortBy, sortOrder, showDeleted);
  }, [rows, searchTerm, stateFilter, sortBy, sortOrder, showDeleted]);

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Forms for {person?.name || `#${person?.id}`}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <AtlasListToolbar
        searchValue={searchTerm}
        onSearchChange={(v) => setSearchTerm(v.toLowerCase())}
        searchPlaceholder="Search forms..."
        onFilterClick={formsFilterPanel.toggle}
        filterOpen={formsFilterPanel.isOpen}
        onRefresh={load}
        refreshDisabled={loading}
        refreshSpinning={loading}
        resultsMeta={{
          startRow: filteredRows.length === 0 ? 0 : 1,
          endRow: Math.min(pageSize, filteredRows.length),
          totalCount: filteredRows.length,
          pageSize,
          pageSizeOptions: [10, 25, 50, 100, 200],
          onPageSizeChange: setPageSize,
          sortBy,
          sortOrder,
          sortOptions: FORM_SORT_OPTIONS,
          onSortByChange: setSortBy,
          onSortOrderToggle: () =>
            setSortOrder((o) => (o === "asc" ? "desc" : "asc")),
        }}
        chipBar={
          formsFilterChips.length > 0 ? (
            <AtlasFilterChipBar
              chips={formsFilterChips}
              onChipClick={(chip) => formsFilterPanel.focusField(chip.fieldId)}
              onRemove={(chip) => {
                if (chip.key === "stateFilter") setStateFilter("all");
                if (chip.key === "showDeleted") setShowDeleted(false);
              }}
              onClearAll={() => {
                setStateFilter("all");
                setShowDeleted(false);
                setSearchTerm("");
                setSortBy("form_name");
                setSortOrder("asc");
              }}
            />
          ) : null
        }
      />

      <EmbeddedFilterPanel
        isOpen={formsFilterPanel.isOpen}
        onClose={formsFilterPanel.close}
        onClear={() => {
          setStateFilter("all");
          setShowDeleted(false);
          setSearchTerm("");
          setSortBy("form_name");
          setSortOrder("asc");
        }}
        footerMode="clearOnly"
      >
        <FilterToggle
          id="forms-show-deleted"
          label="Show Deleted"
          checked={showDeleted}
          onChange={setShowDeleted}
        />
        <FilterSelect
          id="forms-sort-by"
          label="Sort By"
          labelVariant="embedded"
          value={sortBy}
          onChange={setSortBy}
          options={FORM_SORT_OPTIONS}
          placeholder={null}
        />
        <FilterSelect
          id="forms-sort-direction"
          label="Sort Direction"
          labelVariant="embedded"
          value={sortOrder}
          onChange={setSortOrder}
          options={[
            { value: "asc", label: "Ascending" },
            { value: "desc", label: "Descending" },
          ]}
          placeholder={null}
        />
      </EmbeddedFilterPanel>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No forms submitted
        </div>
      ) : (
        <div className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm relative">
          {/* Mobile Compact Grid View */}
          <div className="md:hidden flex flex-col w-full bg-white">
            {/* Header */}
            <div className="grid grid-cols-[1.5fr_1fr_1fr_28px] gap-1.5 px-2 py-2 border-b-2 border-gray-200 text-[11px] font-semibold text-gray-500">
              <div className="flex items-center gap-1">
                Form
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
              </div>
              <div className="flex items-center">State</div>
              <div className="flex items-center">Submit Date</div>
              <div></div>
            </div>
            
            {/* Body */}
            <div className="flex flex-col divide-y divide-gray-100">
              {pagedRows.map((r) => (
                <div key={`mob-${r.form_id}-${r.submission_id || "none"}`} className="grid grid-cols-[1.5fr_1fr_1fr_28px] gap-1.5 px-2 py-3 items-center text-[11px] sm:text-xs">
                  {/* Form */}
                  <div className="leading-tight break-words pr-1 text-gray-900 font-medium">
                    {r.form_name || "—"}
                  </div>
                  {/* State */}
                  <div className="leading-tight text-gray-800 break-words font-medium">
                    {r.form_state || "—"}
                  </div>
                  {/* Submit Date */}
                  <div className="leading-tight text-gray-600 break-words">
                    {formatAccountDateTime(r.submit_date, { emptyValue: "—" })}
                  </div>
                  {/* Actions */}
                  <div className="flex justify-end items-center">
                    {r.submission_id ? (
                      <ActionDropdown
                        menuZIndexClass={effectiveMenuZClass}
                        actions={
                          showDeleted
                            ? [
                                { label: "Restore", onClick: () => handleRestore(r.submission_id) },
                              ]
                            : [
                                { label: "Download", onClick: () => handleDownload(r.submission_id) },
                                { label: "Delete", onClick: () => handleDelete(r.submission_id) },
                              ]
                        }
                      />
                    ) : (
                      <span className="text-gray-400 text-[10px]">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto min-w-0">
            <table className="w-full min-w-[500px] text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                <tr>
                  <SortableTableHeader
                    label="Form"
                    sortKey="form_name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <SortableTableHeader
                    label="State"
                    sortKey="form_state"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <SortableTableHeader
                    label="Submit Date"
                    sortKey="submit_date"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleHeaderSort}
                  />
                  <TableHeaderCell label="Actions" className="text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagedRows.map((r) => (
                  <tr
                    key={`${r.form_id}-${r.submission_id || "none"}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{r.form_name || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{r.form_state || "—"}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatAccountDateTime(r.submit_date, { emptyValue: "—" })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.submission_id ? (
                        <ActionDropdown
                          menuZIndexClass={effectiveMenuZClass}
                          actions={
                            showDeleted
                              ? [
                                  {
                                    label: "Restore",
                                    onClick: () => handleRestore(r.submission_id),
                                  },
                                ]
                              : [
                                  {
                                    label: "Download",
                                    onClick: () => handleDownload(r.submission_id),
                                  },
                                  {
                                    label: "Delete",
                                    onClick: () => handleDelete(r.submission_id),
                                  },
                                ]
                          }
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Submit Form</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form
                </label>
                <select
                  value={formData.form_id}
                  onChange={(e) =>
                    setFormData({ ...formData, form_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="">Select form...</option>
                  {formTypes.map((f) => (
                    <option key={f.form_id} value={f.form_id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="Pending Review">Pending Review</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Submission Data (JSON)
                </label>
                <textarea
                  value={formData.data}
                  onChange={(e) =>
                    setFormData({ ...formData, data: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows={6}
                  placeholder='{"field":"value"}'
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

function TempPreferences({ person }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editType, setEditType] = useState("");
  const [editRating, setEditRating] = useState(0);
  const [editNote, setEditNote] = useState("");
  const [sortBy, setSortBy] = useState("client_name");
  const [sortDirection, setSortDirection] = useState("desc");

  const typeOptions = [
    { value: "good_fit", label: "Good Fit" },
    { value: "like", label: "Like" },
    { value: "neutral", label: "Neutral" },
    { value: "do_not_place", label: "Do Not Place" },
  ];

  const normalizeTypeValue = (value) => {
    const raw = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    if (raw === "goodfit") return "good_fit";
    if (raw === "donotplace") return "do_not_place";
    return raw;
  };

  const getTypeLabel = (value) => {
    const normalized = normalizeTypeValue(value);
    if (normalized === "none") return "";
    const found = typeOptions.find((opt) => opt.value === normalized);
    if (found) return found.label;
    if (!normalized) return "";
    return normalized
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const getTypeFromRating = (rating) => {
    const numeric = getRatingValue(rating);
    if (numeric >= 5) return "like";
    if (numeric >= 4) return "good_fit";
    if (numeric >= 3) return "neutral";
    if (numeric > 0) return "do_not_place";
    return "none";
  };

  const getRatingValue = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(5, numeric));
  };

  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const url = `${BASE_URL}/api/personnel/accounts/${person.id}/preferences`;
      const [listRes] = await Promise.all([
        fetch(url, { credentials: "include" }),
      ]);
      const listJson = await listRes.json().catch(() => ({}));

      if (!listRes.ok) {
        throw new Error(listJson?.message || "Failed to load preferences");
      }

      const nextItems =
        (Array.isArray(listJson.preferences) && listJson.preferences) ||
        (Array.isArray(listJson.data) && listJson.data) ||
        (Array.isArray(listJson.rows) && listJson.rows) ||
        [];

      setItems(nextItems);
    } catch (e) {
      const errorMsg = e.message || "Failed to load preferences";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Preferences");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row) => {
    const rowRating = getRatingValue(row?.rating);
    const mappedType = getTypeFromRating(rowRating);
    setEditingRow(row);
    setEditType(mappedType);
    setEditRating(rowRating);
    setEditNote(String(row?.note || ""));
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditingRow(null);
    setEditType("");
    setEditRating(0);
    setEditNote("");
  };

  const saveEdit = async () => {
    if (!editingRow?.preference_id) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/preferences/${editingRow.preference_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({
            type:
              (editType || getTypeFromRating(editRating)) &&
              (editType || getTypeFromRating(editRating)) !== "none"
                ? editType || getTypeFromRating(editRating)
                : null,
            rating: editRating || null,
            note: editNote.trim() || null,
          }),
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) {
        throw new Error(j.message || "Failed to update preference");
      }

      showSuccess("Preference updated successfully");
      closeEdit();
      await load();
    } catch (e) {
      showError(e.message || "Failed to update preference", "Update Error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, [person?.id]);

  const sortedItems = useMemo(() => {
    const rows = [...items];
    rows.sort((a, b) => {
      if (sortBy === "client_name") {
        const aName = String(a?.client_name || "").toLowerCase();
        const bName = String(b?.client_name || "").toLowerCase();
        return sortDirection === "asc"
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      }
      return 0;
    });
    return rows;
  }, [items, sortBy, sortDirection]);

  return (
    <div className="space-y-4">
      {error && <div className="text-sm text-red-600">{error}</div>}

      {/* List */}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="border border-gray-200 rounded overflow-x-auto min-w-0 no-mobile-table-stack">
          <table className="w-full min-w-[600px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th
                  className="text-left px-3 py-2 cursor-pointer select-none"
                  onClick={() => {
                    if (sortBy === "client_name") {
                      setSortDirection(
                        sortDirection === "asc" ? "desc" : "asc",
                      );
                    } else {
                      setSortBy("client_name");
                      setSortDirection("asc");
                    }
                  }}
                >
                  <div className="flex items-center">
                    Client
                    <SortArrow
                      field="client_name"
                      currentSort={sortBy}
                      currentDirection={sortDirection}
                    />
                  </div>
                </th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Rating</th>
                <th className="text-left px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td
                    className="px-3 py-4 text-center text-gray-400"
                    colSpan={4}
                  >
                    No preferences
                  </td>
                </tr>
              ) : (
                sortedItems.map((row) => (
                  <tr
                    key={row.preference_id}
                    className="border-t border-gray-200"
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => openEdit(row)}
                      >
                        {row.client_name || ""}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      {getTypeLabel(
                        getTypeFromRating(row.rating) || row.preference_type,
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {getRatingValue(row.rating) > 0
                        ? `${getRatingValue(row.rating)}/5`
                        : ""}
                    </td>
                    <td className="px-3 py-2">{row.note || ""}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showEdit && editingRow && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-xl rounded-md shadow-2xl p-7">
            <h3 className="text-[15px] leading-none font-semibold text-gray-800 mb-6">
              Edit Preference
            </h3>

            <div className="mb-6">
              <label className="block text-xs text-gray-500 mb-1">Staff*</label>
              <div className="flex items-center justify-between border-b border-gray-400 pb-2">
                <span className="text-[15px] text-gray-800">
                  {person?.name || ""}
                </span>
                <button
                  type="button"
                  className="text-[15px] leading-none text-gray-700"
                  onClick={closeEdit}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="mb-6 border-b border-gray-400 pb-2">
              <div className="text-[15px] text-gray-600">
                Contact Provided Feedback
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs text-gray-500 mb-2">Type</label>
              <div className="w-full border border-gray-300 rounded px-3 py-2 text-[15px] bg-gray-50">
                {getTypeLabel(editType)}
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs text-gray-500 mb-2">Rating</label>
              <div className="flex items-center gap-2 text-5xl leading-none">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      const nextRating = editRating === n ? 0 : n;
                      setEditRating(nextRating);
                      setEditType(getTypeFromRating(nextRating));
                    }}
                    className={
                      n <= editRating ? "text-red-600" : "text-gray-300"
                    }
                    aria-label={`Set rating ${n}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-xs text-red-600 mb-1">Note</label>
              <textarea
                className="w-full border-b border-red-600 outline-none resize-none text-[15px] py-1"
                rows={2}
                maxLength={400}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
              />
              <div className="text-xs text-gray-400 mt-1">
                {editNote.length}/400
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-[15px] text-red-600 uppercase"
                onClick={saveEdit}
                disabled={saving}
              >
                {saving ? "Saving" : "Save"}
              </button>
              <button
                type="button"
                className="text-[15px] text-gray-700 uppercase"
                onClick={closeEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-[15px] text-gray-700 uppercase"
                onClick={closeEdit}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Placeholder({ title, subtitle }) {
  return (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-gray-500">{subtitle}</p>
      <div className="h-40 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400">
        Placeholder
      </div>
    </div>
  );
}

function OfficeNotes({ person, active = false }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // Relates-to (autocomplete) state for Office Notes
  const [relatesQuery, setRelatesQuery] = useState("");
  const [relatesOptions, setRelatesOptions] = useState([]);
  const [selectedRelated, setSelectedRelated] = useState(null);

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // FILTER STATES
  const [createdBy, setCreatedBy] = useState([]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [relatedTo, setRelatedTo] = useState([]); // MULTI SELECT
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortBy, setSortBy] = useState("create_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [notesLimit, setNotesLimit] = useState(25);
  const [notesLimitOpen, setNotesLimitOpen] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [createdByDropdownOpen, setCreatedByDropdownOpen] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [authorOptions, setAuthorOptions] = useState([]);

  const relatesTimer = useRef(null);
  const relatesRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter helper functions
  const toggleRelated = (value) => {
    setRelatedTo((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };
  const toggleCreatedBy = (value) => {
    setCreatedBy((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const removeTag = (value) => {
    setRelatedTo((prev) => prev.filter((v) => v !== value));
  };
  const removeCreatedByTag = (value) => {
    setCreatedBy((prev) => prev.filter((v) => v !== value));
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setCreatedByDropdownOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch types for filter
  useEffect(() => {
    fetch(`${BASE_URL}/api/personnel/account-types`)
      .then((res) => res.json())
      .then((j) => {
        if (j.success) {
          setTypeOptions(filterAllowedNoteRelatedTypeOptions(j.data));
        }
      });
  }, []);

  // Apply filter function
  const applyFilter = (
    searchValue = searchTerm,
    createdValue = createdBy,
    dateFromValue = createdFrom,
    dateToValue = createdTo,
    relatedValues = relatedTo,
    showDeletedValue = showDeleted,
    sortKey = sortBy,
    sortDir = sortDirection,
  ) => {
    let filtered = [...notes];

    // Search Term Filter
    const q = (searchValue || "").toString().trim().toLowerCase();

    if (q !== "") {
      filtered = filtered.filter(
        (n) =>
          (n.note && n.note.toLowerCase().includes(q)) ||
          (n.content && n.content.toLowerCase().includes(q)) ||
          (n.relates_to && n.relates_to.toLowerCase().includes(q)) ||
          (n.relates_type && n.relates_type.toLowerCase().includes(q)) ||
          formatActor(n.created_by).toLowerCase().includes(q),
      );
    }

    // Created by filter
    if (Array.isArray(createdValue) && createdValue.length > 0) {
      const selectedAuthorNames = createdValue
        .map((id) =>
          authorOptions.find((a) => String(a.account_id || a.id) === String(id)),
        )
        .filter(Boolean)
        .map((a) => `${a.first_name} ${a.last_name}`.toLowerCase());

      filtered = filtered.filter((n) => {
        const actorStr = String(n.created_by || "").toLowerCase();
        const match = actorStr.match(/(\d+)/);
        const actorId = match ? match[1] : actorStr;
        const idMatch = createdValue.some((id) => String(actorId) === String(id));
        if (idMatch) return true;
        return selectedAuthorNames.some((name) => name && actorStr.includes(name));
      });
    }

    // Date range filter - Created from
    if (dateFromValue) {
      const fromDate = new Date(dateFromValue);
      fromDate.setHours(0, 0, 0, 0); // Start of day
      filtered = filtered.filter((n) => {
        if (!n.create_date) return false;
        const noteDate = new Date(n.create_date);
        return noteDate >= fromDate;
      });
    }

    // Date range filter - Created to
    if (dateToValue) {
      const toDate = new Date(dateToValue);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter((n) => {
        if (!n.create_date) return false;
        const noteDate = new Date(n.create_date);
        return noteDate <= toDate;
      });
    }

    // Related to filter
    if (Array.isArray(relatedValues) && relatedValues.length > 0) {
      const selectedTypeKeys = new Set(
        relatedValues.map((v) => normalizeNoteRelatedTypeKey(v)),
      );
      filtered = filtered.filter((n) => {
        const singleKey = normalizeNoteRelatedTypeKey(n.relates_type);
        if (singleKey && selectedTypeKeys.has(singleKey)) return true;
        if (Array.isArray(n.relates_types)) {
          return n.relates_types.some((t) =>
            selectedTypeKeys.has(normalizeNoteRelatedTypeKey(t)),
          );
        }
        return false;
      });
    }

    // Show Deleted Filter
    filtered = filtered.filter((n) => {
      const state = (n.state || "active").toString().toLowerCase().trim();
      return showDeletedValue ? state === "deleted" : state !== "deleted";
    });

    // Sort
    filtered.sort((a, b) => {
      let x = a[sortKey];
      let y = b[sortKey];

      if (typeof x === "string") x = x.toLowerCase();
      if (typeof y === "string") y = y.toLowerCase();

      return sortDir === "asc" ? (x > y ? 1 : -1) : x < y ? 1 : -1;
    });

    setFilteredNotes(filtered);
  };

  useEffect(() => {
    applyFilter();
  }, [
    notes,
    searchTerm,
    createdBy,
    createdFrom,
    createdTo,
    relatedTo,
    showDeleted,
    sortBy,
    sortDirection,
  ]);
  const visibleNotes = filteredNotes.slice(0, notesLimit);

  // Fetch authors for filter dropdown
  const fetchAuthors = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/office/${person.id}/note/created-by?showDeleted=${showDeleted ? "true" : "false"}`,
        {
          credentials: "include",
        },
      );
      const j = await res.json().catch(() => ({}));
      if (j.success && j.data) {
        setAuthorOptions(j.data);
      } else {
        // Fallback: try the temp staff endpoint format
        const res2 = await fetch(
          `${BASE_URL}/api/personnel/${person.id}/note/created-by?showDeleted=${showDeleted ? "true" : "false"}`,
          {
            credentials: "include",
          },
        );
        const j2 = await res2.json().catch(() => ({}));
        if (j2.success && j2.data) {
          setAuthorOptions(j2.data);
        }
      }
    } catch (e) {
      console.error("Failed to fetch authors:", e);
    }
  };

  useEffect(() => {
    if (showFilterPanel && person?.id) {
      fetchAuthors();
    }
  }, [showFilterPanel, person?.id, showDeleted]);

  const searchRelates = async (q) => {
    if (!q || q.trim().length < 1) {
      setRelatesOptions([]);
      return;
    }
    try {
      const resp = await fetch(
        `${BASE_URL}/api/personnel/accounts/search?q=${encodeURIComponent(
          q,
        )}&limit=20`,
        { credentials: "include" },
      );
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setRelatesOptions([]);
        return;
      }
      const list = j.data || j || [];
      const opts = Array.isArray(list)
        ? list.map((a) => ({
            id: a.account_id || a.id,
            name: a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim(),
            type: a.type || a.account_type || "",
          }))
        : [];
      setRelatesOptions(opts);
    } catch (err) {
      console.error("Relates search failed", err);
      setRelatesOptions([]);
    }
  };

  // Close relates-to dropdown on outside click or Escape (OfficeNotes)
  useEffect(() => {
    const onClick = (e) => {
      if (!relatesOptions || relatesOptions.length === 0) return;
      const node = relatesRef.current;
      if (node && !node.contains(e.target)) {
        setRelatesOptions([]);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setRelatesOptions([]);
      }
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
      if (relatesTimer.current) {
        clearTimeout(relatesTimer.current);
        relatesTimer.current = null;
      }
    };
  }, [relatesOptions]);

  const getAuthorAccountId = () => {
    try {
      const explicit = localStorage.getItem("accountId");
      if (explicit && !Number.isNaN(parseInt(explicit, 10)))
        return parseInt(explicit, 10);
      const rawUser = localStorage.getItem("user");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u && (u.account_id || u.accountId || u.id))
          return u.account_id || u.accountId || u.id;
      }
    } catch (_) {}
    return undefined;
  };

  const loadNotes = async () => {
    const startTs = new Date().toISOString();
    const url = `${BASE_URL}/api/personnel/office/${person.id}/notes`;
    console.debug("[OfficeNotes] loadNotes start", {
      personId: person?.id,
      url,
      startTs,
    });
    if (!person?.id) {
      console.debug("[OfficeNotes] no person id, abort loadNotes");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(url, { credentials: "include" });
      const contentType =
        res.headers && res.headers.get
          ? res.headers.get("content-type")
          : undefined;
      const raw = await res.text().catch(() => null);
      let j = {};
      try {
        j = raw ? JSON.parse(raw) : {};
      } catch (parseErr) {
        j = { data: [] };
      }
      console.debug("[OfficeNotes] loadNotes response", {
        ok: res.ok,
        status: res.status,
        contentType,
        body: j,
        rawLength: raw ? raw.length : 0,
      });
      if (!res.ok || j.success === false) {
        const msg =
          (j && j.message) || `Failed to load notes (status ${res.status})`;
        throw new Error(msg);
      }
      const data = Array.isArray(j.data) ? j.data : Array.isArray(j) ? j : [];
      console.debug("[OfficeNotes] notes resolved count", (data || []).length);
      setNotes(data || []);
      // Don't set filteredNotes directly - let applyFilter handle it via useEffect
    } catch (e) {
      const errorMsg = e.message || "Failed to load notes";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Notes");
      console.error("[OfficeNotes] loadNotes error", e);
    } finally {
      setLoading(false);
    }
  };

  // Mount/unmount trace for OfficeNotes
  useEffect(() => {
    console.debug("[OfficeNotes] mounted", { personId: person?.id, active });
    return () =>
      console.debug("[OfficeNotes] unmounted", { personId: person?.id });
  }, []);

  // Load notes when person changes or when the Notes tab becomes active
  useEffect(() => {
    if (active) loadNotes();
  }, [person?.id, active]);

  const addNote = async () => {
    if (!newNote.trim()) {
      showError("Note is required");
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;
      const payload = { note: newNote, state: "active" };
      if (selectedRelated) {
        payload.relates_type = selectedRelated.type || null;
        payload.relates_object_id = selectedRelated.id || null;
      }
      const url = `${BASE_URL}/api/personnel/office/${person.id}/notes`;
      console.debug("[OfficeNotes] addNote payload", { url, payload });
      const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const raw = await res.text().catch(() => null);
      let j = {};
      try {
        j = raw ? JSON.parse(raw) : {};
      } catch (_) {
        j = {};
      }
      console.debug("[OfficeNotes] addNote response", {
        ok: res.ok,
        status: res.status,
        body: j,
        rawLength: raw ? raw.length : 0,
      });
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to add note");
      setNewNote("");
      setShowModal(false);
      setSelectedRelated(null);
      setRelatesQuery("");
      loadNotes();
      showSuccess("Note added successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to add note";
      setError(errorMsg);
      showError(errorMsg, "Failed to Add Note");
    } finally {
      setSaving(false);
    }
  };

  const updateNote = async (noteId, noteContent) => {
    try {
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;
      const res = await fetch(
        `${BASE_URL}/api/personnel/office/${person.id}/notes/${noteId}`,
        {
          method: "PUT",
          headers,
          credentials: "include",
          body: JSON.stringify({ note: noteContent, state: "active" }),
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to update note");
      setEditingNote(null);
      loadNotes();
      showSuccess("Note updated successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to update note";
      setError(errorMsg);
      showError(errorMsg, "Failed to Update Note");
    }
  };

  const deleteNote = async (noteId) => {
    if (!(await showConfirm("Are you sure you want to delete this note?")))
      return;
    try {
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;
      const res = await fetch(
        `${BASE_URL}/api/personnel/office/${person.id}/notes/${noteId}`,
        {
          method: "DELETE",
          headers,
          credentials: "include",
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to delete note");
      loadNotes();
      showSuccess("Note deleted successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to delete note";
      setError(errorMsg);
      showError(errorMsg, "Failed to Delete Note");
    }
  };

  const formatDateTime = (dateTime) =>
    formatAppDateTime(dateTime, { emptyValue: "—" });

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        Notes for {person?.name || `#${person?.id}`}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="flex flex-wrap items-center justify-start gap-3 bg-gray-50/50 p-4 rounded-xl border border-gray-100 mt-4">
        <div className="relative w-full sm:w-64">
          <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg bg-white text-xs placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/10 focus:border-red-500 w-full transition-all font-medium"
          />
        </div>

        <button
          onClick={() => setShowFilterPanel(true)}
          className={`p-2 rounded-lg border flex items-center justify-center transition-all ${
            (createdBy && createdBy.length > 0) ||
            createdFrom ||
            createdTo ||
            (relatedTo && relatedTo.length > 0) ||
            showDeleted
              ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
              : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
          }`}
          title="Filter"
        >
          <FaSlidersH size={12} />
        </button>

        <AtlasRefreshIconButton
          onClick={() => loadNotes()}
          spinning={loading}
          useToolbarButton={false}
          buttonClassName="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
          iconSize={12}
        />

        <button
          onClick={() => {
            setEditingNote(null);
            setNewNote("");
            setSelectedRelated(null);
            setShowModal(true);
          }}
          className="px-4 py-1.5 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 transition-all shadow-sm active:scale-95 inline-flex items-center gap-1.5"
        >
          <span>➕ Add Note</span>
        </button>
      </div>

      {/* ACTIVE FILTERS - DISPLAYED AS TAGS */}
      {((createdBy && createdBy.length > 0) ||
        createdFrom ||
        createdTo ||
        (relatedTo && relatedTo.length > 0) ||
        showDeleted) && (
        <div className="flex flex-wrap items-center gap-2 px-1 py-1 group">
          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mr-1">
            Filters:
          </span>
          {createdBy.length > 0 && (
            <div
              onClick={() => setShowFilterPanel(true)}
              className="flex items-center gap-1.5 bg-white text-gray-700 px-2.5 py-1 rounded-md text-[11px] font-bold border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50 transition-all font-sans"
            >
              <span className="text-[12px]">👤</span>
              <span className="truncate max-w-[120px]">
                {(() => {
                  const labels = createdBy
                    .map((id) =>
                      authorOptions.find(
                        (a) => String(a.account_id || a.id) === String(id),
                      ),
                    )
                    .filter(Boolean)
                    .map((a) => `${a.first_name} ${a.last_name}`);
                  if (labels.length === 0) return "User";
                  if (labels.length <= 2) return labels.join(", ");
                  return `${labels[0]}, ${labels[1]}..`;
                })()}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatedBy([]);
                }}
                className="hover:text-red-700 p-0.5 ml-1 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </div>
          )}

          {createdFrom && (
            <div
              onClick={() => setShowFilterPanel(true)}
              className="flex items-center gap-1.5 bg-white text-gray-700 px-2.5 py-1 rounded-md text-[11px] font-bold border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50 transition-all font-sans"
            >
              <span className="text-gray-400 text-[9px] uppercase">From</span>
              {createdFrom}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatedFrom("");
                }}
                className="hover:text-red-700 p-0.5 ml-1 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </div>
          )}

          {createdTo && (
            <div
              onClick={() => setShowFilterPanel(true)}
              className="flex items-center gap-1.5 bg-white text-gray-700 px-2.5 py-1 rounded-md text-[11px] font-bold border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50 transition-all font-sans"
            >
              <span className="text-gray-400 text-[8px] uppercase">To</span>
              {createdTo}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatedTo("");
                }}
                className="hover:text-red-700 p-0.5 ml-1 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </div>
          )}

          {relatedTo &&
            relatedTo.map((rel) => (
              <div
                key={rel}
                onClick={() => setShowFilterPanel(true)}
                className="flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-md text-[11px] font-bold border border-red-100 shadow-sm cursor-pointer hover:bg-red-100 transition-all font-sans"
              >
                <span className="text-red-400 text-[8px] uppercase">Rel</span>
                {rel}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRelatedTo((prev) => prev.filter((p) => p !== rel));
                  }}
                  className="hover:text-red-900 p-0.5 ml-1 transition-colors"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            ))}

          {showDeleted && (
            <div
              onClick={() => setShowFilterPanel(true)}
              className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md text-[11px] font-bold border border-amber-100 shadow-sm cursor-pointer hover:bg-amber-100 transition-all font-sans"
            >
              <span className="text-amber-500 uppercase text-[8px]">
                Deleted
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleted(false);
                }}
                className="hover:text-amber-900 p-0.5 ml-1 transition-colors"
              >
                <FaTimes size={10} />
              </button>
            </div>
          )}

          <button
            onClick={() => {
              setCreatedBy([]);
              setCreatedFrom("");
              setCreatedTo("");
              setRelatedTo([]);
              setShowDeleted(false);
            }}
            className="text-[9px] font-bold text-gray-400 uppercase tracking-widest hover:text-red-600 transition-all ml-auto underline decoration-dotted"
          >
            Clear All
          </button>
        </div>
      )}

      {/* SORT SUMMARY */}
      <div className="px-1 py-1 text-[11px] text-gray-500 flex items-center gap-1.5 font-sans relative">
        Showing{" "}
        <button
          type="button"
          className="text-red-600 font-bold hover:underline"
          onClick={() => setNotesLimitOpen((v) => !v)}
        >
          {Math.min(filteredNotes.length, notesLimit)}
        </button>{" "}
        results sorted by
        <span
          className="text-red-600 cursor-pointer hover:underline font-bold"
          onClick={() => {
            const nextSort = sortBy === "create_date" ? "note" : "create_date";
            setSortBy(nextSort);
          }}
        >
          {sortBy === "create_date"
            ? "Created"
            : sortBy === "note"
              ? "Note"
              : sortBy}
        </span>
        <span>,</span>
        <span
          className="text-red-600 cursor-pointer hover:underline font-bold"
          onClick={() =>
            setSortDirection(sortDirection === "asc" ? "desc" : "asc")
          }
        >
          {sortDirection === "desc" ? "Descending" : "Ascending"}
        </span>
        {notesLimitOpen && (
          <div className="absolute top-full left-16 mt-1 bg-white border border-gray-200 rounded shadow z-20">
            {[10, 25, 50, 100, 200].map((n) => (
              <button
                key={n}
                type="button"
                className={`block w-full text-left px-3 py-1.5 hover:bg-gray-50 ${notesLimit === n ? "text-red-600 font-bold" : "text-gray-700"}`}
                onClick={() => {
                  setNotesLimit(n);
                  setNotesLimitOpen(false);
                }}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-8 text-center italic">
          Loading notes…
        </div>
      ) : notes.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-xl p-10 text-gray-400 text-center bg-gray-50/30">
          No notes have been recorded for this person yet.
        </div>
      ) : (
        <div className="w-full border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {/* Mobile Card View — disabled per QA request; keep the real table at every
              width instead (below), scrollable via no-mobile-table-stack. */}
          <div className="hidden flex-col gap-0 divide-y divide-gray-100 bg-gray-50">
            {visibleNotes.map((n) => (
              <div key={`mob-${n.note_id}`} className="bg-white p-4 flex flex-col gap-3 relative">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-semibold text-sm">{formatActor(n.created_by)}</span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(n.create_date)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      (n.state || "active") === "active"
                        ? "bg-green-50 text-green-600 border border-green-200"
                        : "bg-red-50 text-red-600 border border-red-200"
                    }`}>
                      {n.state || "active"}
                    </span>
                  </div>
                </div>
                
                {editingNote === n.note_id ? (
                  <div className="flex flex-col gap-2">
                    <textarea
                      id={`edit-note-mob-${n.note_id}`}
                      defaultValue={n.note ?? n.content}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none"
                      rows={4}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => {
                          const textarea = document.getElementById(`edit-note-mob-${n.note_id}`);
                          if (textarea && textarea.value) updateNote(n.note_id, textarea.value);
                        }}
                        className="text-[11px] font-bold text-white bg-green-600 px-3 py-1 rounded hover:bg-green-700 uppercase"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingNote(null)}
                        className="text-[11px] font-bold text-gray-500 hover:text-gray-700 px-3 py-1 rounded uppercase bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm whitespace-pre-wrap break-words leading-relaxed text-gray-800">
                    {n.note ?? n.content}
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-2 mt-1 border-t border-gray-50">
                  <div className="text-xs font-medium text-gray-600">
                    {n.relates_to ? `${n.relates_to}${n.relates_type ? ` (${n.relates_type})` : ""}` : <span className="text-gray-300 italic">No relations</span>}
                  </div>
                  <div className="flex justify-end gap-3 mt-1">
                    <button
                      onClick={() => setEditingNote(n.note_id)}
                      className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteNote(n.note_id)}
                      className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto min-w-0 bg-white no-mobile-table-stack">
            <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <TableHeaderCell label="Created By" className="px-4 py-3" />
                <SortableTableHeader
                  label="Date"
                  sortKey="create_date"
                  sortBy={sortBy}
                  sortOrder={sortDirection}
                  onSort={(key) => {
                    if (sortBy === key) {
                      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
                    } else {
                      setSortBy(key);
                      setSortDirection("desc");
                    }
                  }}
                  className="px-4 py-3"
                />
                <TableHeaderCell label="Relates to" className="px-4 py-3" />
                <SortableTableHeader
                  label="Note"
                  sortKey="note"
                  sortBy={sortBy}
                  sortOrder={sortDirection}
                  onSort={(key) => {
                    if (sortBy === key) {
                      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
                    } else {
                      setSortBy(key);
                      setSortDirection("asc");
                    }
                  }}
                  className="px-4 py-3"
                />
                <TableHeaderCell label="State" className="px-4 py-3" />
                <TableHeaderCell label="Actions" className="px-4 py-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {visibleNotes.map((n) => (
                <tr
                  key={n.note_id}
                  className="border-t border-gray-100 hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    {formatActor(n.created_by)}
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap text-gray-500">
                    {formatDateTime(n.create_date)}
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap font-medium text-gray-700">
                    {n.relates_to ? (
                      `${n.relates_to}${
                        n.relates_type ? ` (${n.relates_type})` : ""
                      }`
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {editingNote === n.note_id ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          id={`edit-note-${n.note_id}`}
                          defaultValue={n.note ?? n.content}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none"
                          rows={3}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && e.ctrlKey) {
                              if (e.target?.value) {
                                updateNote(n.note_id, e.target.value);
                              }
                            }
                            if (e.key === "Escape") setEditingNote(null);
                          }}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const textarea = document.getElementById(
                                `edit-note-${n.note_id}`,
                              );
                              if (textarea && textarea.value) {
                                updateNote(n.note_id, textarea.value);
                              }
                            }}
                            className="text-[11px] font-bold text-white bg-green-600 px-3 py-1 rounded hover:bg-green-700 uppercase"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingNote(null)}
                            className="text-[11px] font-bold text-gray-500 hover:text-gray-700 px-3 py-1 rounded uppercase bg-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap max-w-md text-gray-800 leading-relaxed">
                        {n.note ?? n.content}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top capitalize">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        (n.state || "active") === "active"
                          ? "bg-green-50 text-green-600 border border-green-200"
                          : "bg-red-50 text-red-600 border border-red-200"
                      }`}
                    >
                      {n.state || "active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => setEditingNote(n.note_id)}
                        className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                        title="Edit note"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteNote(n.note_id)}
                        className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                        title="Delete note"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 fade-in duration-200">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div className="text-lg font-bold text-gray-800">Add Note</div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="relative" ref={relatesRef}>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Relates to
                </label>
                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                  <input
                    value={
                      selectedRelated
                        ? `${selectedRelated.name} (${selectedRelated.type})`
                        : relatesQuery
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedRelated(null);
                      setRelatesQuery(v);
                      if (relatesTimer.current)
                        clearTimeout(relatesTimer.current);
                      relatesTimer.current = setTimeout(
                        () => searchRelates(v),
                        300,
                      );
                    }}
                    className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none transition-all placeholder:text-gray-400"
                    placeholder="Search accounts to link..."
                  />
                  {relatesOptions.length > 0 && !selectedRelated && (
                    <div className="absolute z-50 left-0 right-0 mt-2 border border-gray-100 rounded-xl bg-white max-h-56 overflow-auto shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
                      {relatesOptions.map((opt) => (
                        <div
                          key={opt.id}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between border-b last:border-b-0 border-gray-50 group"
                          onClick={() => {
                            setSelectedRelated(opt);
                            setRelatesQuery("");
                            setRelatesOptions([]);
                          }}
                        >
                          <span className="text-sm font-medium text-gray-700 group-hover:text-sidebar-active">
                            {opt.name}
                          </span>
                          <span className="text-[10px] font-bold uppercase text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                            {opt.type}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedRelated && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRelated(null);
                        setRelatesQuery("");
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Note Content
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="w-full border border-gray-300 rounded-2xl px-4 py-3 text-sm min-h-[150px] focus:ring-2 focus:ring-sidebar-active/20 outline-none transition-all"
                  placeholder="What would you like to record?..."
                />
              </div>
            </div>

            <div className="bg-gray-50 p-6 flex justify-end gap-3 border-t border-gray-100">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:text-gray-800 transition-colors uppercase tracking-wider"
              >
                Cancel
              </button>
              <button
                disabled={saving || !newNote.trim()}
                onClick={addNote}
                className="px-8 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-40 transition-all shadow-lg shadow-red-500/20 uppercase tracking-wider"
              >
                {saving ? "Saving…" : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Panel for Office Notes */}
      {showFilterPanel && (
        <>
          {/* OVERLAY - High Z-index with separate fixed positioning */}
          <div
            className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setShowFilterPanel(false)}
          />

          {/* PANEL - Forced high Z-index and absolute viewport positioning */}
          <div
            className="fixed top-0 right-0 z-[10001] w-full max-w-[320px] sm:max-w-sm h-screen bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER - Fixed to top */}
            <div className="flex justify-between items-center p-6 border-b border-gray-100 shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <FaSlidersH className="text-sidebar-active w-5 h-5" />
                </div>
                Filters
              </h3>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded-full transition-all"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* SCROLLABLE CONTENT - Guaranteed scroll with min-h-0 and flex-1 */}
            <div className="flex-1 overflow-y-scroll p-6 space-y-5 min-h-0">
              {/* CREATED BY USER (Single Select) */}
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                  Created by User
                </label>
                <div
                  className="border border-gray-200 rounded-xl px-4 py-2 min-h-[40px] flex items-center flex-wrap gap-2 cursor-pointer bg-gray-50/50 hover:border-gray-300 transition-all"
                  onClick={() =>
                    setCreatedByDropdownOpen(!createdByDropdownOpen)
                  }
                >
                  {createdBy.length > 0 ? (
                    createdBy.map((id) => {
                      const author = authorOptions.find(
                        (a) => String(a.account_id || a.id) === String(id),
                      );
                      if (!author) return null;
                      return (
                        <div
                          key={id}
                          className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1 rounded-full text-[11px] font-bold border border-red-100 animate-in zoom-in-95"
                        >
                          {author.first_name} {author.last_name}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCreatedByTag(String(id));
                            }}
                            className="hover:text-red-900"
                          >
                            <FaTimes size={10} />
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <span className="text-gray-400 text-sm">
                      Select users...
                    </span>
                  )}
                </div>
                {createdByDropdownOpen && (
                  <div className="mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl max-h-60 overflow-y-auto z-[10010] animate-in slide-in-from-top-2">
                    {authorOptions.map((a) => {
                      const id = String(a.account_id || a.id);
                      const label = `${a.first_name} ${a.last_name}`;
                      return (
                        <div
                          key={id}
                          onClick={() => toggleCreatedBy(id)}
                          className={`px-4 py-3 cursor-pointer text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                            createdBy.includes(id)
                              ? "bg-red-50 text-red-600 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          {label}
                          {createdBy.includes(id) && (
                            <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* DATE RANGE */}
              <div className="space-y-3">
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">
                  Date Range
                </label>
                <div className="space-y-2">
                  <div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 block ml-1">
                      From
                    </span>
                    <DateInput
                      value={createdFrom}
                      onChange={setCreatedFrom}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 block ml-1">
                      To
                    </span>
                    <DateInput
                      value={createdTo}
                      onChange={setCreatedTo}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>

              {/* RELATED TO (Multi Select) */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                  Related Account Type
                </label>
                <div
                  className="border border-gray-200 rounded-xl px-4 py-2 min-h-[40px] flex items-center flex-wrap gap-2 cursor-pointer bg-gray-50/50 hover:border-gray-300 transition-all"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {relatedTo.length > 0 ? (
                    relatedTo.map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1 rounded-full text-[11px] font-bold border border-red-100 animate-in zoom-in-95"
                      >
                        {item}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTag(item);
                          }}
                          className="hover:text-red-900"
                        >
                          <FaTimes size={10} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">
                      Select types...
                    </span>
                  )}
                </div>

                {dropdownOpen && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl max-h-60 overflow-y-auto z-[10010] animate-in slide-in-from-top-2">
                    {typeOptions.map((opt) => (
                      <div
                        key={opt.value}
                        onClick={() => toggleRelated(opt.label)}
                        className={`px-4 py-3 cursor-pointer text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          relatedTo.includes(opt.label)
                            ? "bg-red-50 text-red-600 font-bold"
                            : "text-gray-700"
                        }`}
                      >
                        {opt.label}
                        {relatedTo.includes(opt.label) && (
                          <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SORTING */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none bg-gray-50/50"
                  >
                    <option value="create_date">Date Created</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1">
                    Direction
                  </label>
                  <select
                    value={sortDirection === "asc" ? "Ascending" : "Descending"}
                    onChange={(e) =>
                      setSortDirection(
                        e.target.value === "Ascending" ? "asc" : "desc",
                      )
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-sidebar-active/20 outline-none bg-gray-50/50"
                  >
                    <option>Ascending</option>
                    <option>Descending</option>
                  </select>
                </div>
              </div>

              {/* TOGGLES */}
              <div className="pt-2">
                <label className="relative flex items-center p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-all border border-transparent hover:border-gray-100">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  <div className="ml-3">
                    <span className="block text-sm font-bold text-gray-700">
                      Show Deleted Notes
                    </span>
                    <span className="block text-[10px] text-gray-400 uppercase font-medium">
                      Include inactive records
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* FOOTER BUTTONS - Fixed to bottom */}
            <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex gap-4 shrink-0">
              <button
                onClick={() => {
                  setCreatedBy([]);
                  setRelatedTo([]);
                  setSearchTerm("");
                  setCreatedFrom("");
                  setCreatedTo("");
                  setShowDeleted(false);
                }}
                className="flex-1 px-4 py-3 text-xs font-bold text-gray-500 uppercase border border-gray-200 rounded-xl hover:bg-white hover:text-gray-700 transition-all tracking-widest"
              >
                Clear
              </button>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="flex-[1.5] px-4 py-3 text-xs font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-all uppercase tracking-widest shadow-lg shadow-red-500/20"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OfficeProfile({ person, confirmDialog, setConfirmDialog, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSendingActivation, setIsSendingActivation] = useState(false);
  const [data, setData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [residencyUpload, setResidencyUpload] = useState({
    name: "",
    uploading: false,
    error: "",
  });
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const residencyFileInputRef = useRef(null);

  const [divisionOptions, setDivisionOptions] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);

  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [localConfirmDialog, setLocalConfirmDialog] = useState(null);
  const statusMenuRef = useRef(null);
  const dialog =
    confirmDialog !== undefined ? confirmDialog : localConfirmDialog;
  const setDialog =
    setConfirmDialog !== undefined ? setConfirmDialog : setLocalConfirmDialog;
  const [activationEmailSentDate, setActivationEmailSentDate] = useState(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  async function handleStatusChange(newState) {
    if (!person?.id) return;
    try {
      setSaving(true);
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/state`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ state: newState }),
        },
      );
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.message || "Failed to update status");

      officeSetField("state", newState);
      setSnapshot((prev) => ({ ...(prev || {}), state: newState }));
      showSuccess(`Account status updated to ${newState}`);
    } catch (e) {
      showError(e.message);
    } finally {
      setSaving(false);
      if (typeof setDialog === "function") setDialog(null);
    }
  }

  async function handleSendActivationEmail() {
    if (!person?.id) return;
    if (isSendingActivation) return;
    try {
      setIsSendingActivation(true);
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/send-activation`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.message || "Failed to send email");

      setActivationEmailSentDate(new Date().toISOString());
      showSuccess("Activation email sent successfully");
    } catch (e) {
      showError(e.message);
    } finally {
      setIsSendingActivation(false);
      if (typeof setDialog === "function") setDialog(null);
    }
  }

  // Auto-scroll logic for status menu
  useEffect(() => {
    if (statusMenuOpen && statusMenuRef.current) {
      setTimeout(() => {
        if (!statusMenuRef.current) return;
        let scrollableParent = null;
        let element = statusMenuRef.current;
        while (
          element &&
          element !== document.body &&
          element !== document.documentElement
        ) {
          element = element.parentElement;
          if (!element) break;
          const style = window.getComputedStyle(element);
          const overflowY = style.overflowY;
          if (
            overflowY === "auto" ||
            overflowY === "scroll" ||
            (element.scrollHeight > element.clientHeight &&
              element.clientHeight > 0)
          ) {
            scrollableParent = element;
            break;
          }
        }
        if (scrollableParent) {
          const rect = statusMenuRef.current.getBoundingClientRect();
          const parentRect = scrollableParent.getBoundingClientRect();
          const dropdownHeight = 250;
          if (rect.bottom + dropdownHeight > window.innerHeight) {
            scrollableParent.scrollTo({
              top:
                scrollableParent.scrollTop +
                (rect.bottom + dropdownHeight - window.innerHeight) +
                20,
              behavior: "smooth",
            });
          }
        }
      }, 100);
    }
  }, [statusMenuOpen]);
  useEffect(() => {
    const load = async () => {
      if (!person?.id) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}`,
          {
            credentials: "include",
            headers: getAuthHeaders(),
          },
        );
        const json = await res.json();
        console.log("Initial data load:", json.data);

        const raw = json.data || {};
        const normalized = { ...raw };
        if (!String(normalized.line1 || "").trim()) {
          normalized.line1 = normalizeAddressLine1(raw);
        }
        const toIdArray = (value, keys = []) => {
          if (value == null) return [];
          const arr = Array.isArray(value) ? value : [value];
          const out = [];
          for (const item of arr) {
            let v = item;
            if (v && typeof v === "object") {
              for (const k of keys) {
                if (v[k] !== undefined && v[k] !== null && v[k] !== "") {
                  v = v[k];
                  break;
                }
              }
            }
            if (typeof v === "string" && v.includes(",")) {
              v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((part) => {
                  const n = Number(part);
                  out.push(Number.isNaN(n) ? part : n);
                });
              continue;
            }
            const n = Number(v);
            out.push(Number.isNaN(n) ? v : n);
          }
          return out
            .map((x) => (typeof x === "string" ? x.trim() : x))
            .filter((x) => x !== "" && x !== null && x !== undefined);
        };
        const parseOfficeMarkerIds = (note) => {
          const m = String(note || "").match(/office_ids:?\s*([^;\n]+)/i);
          if (!m || !m[1]) return [];
          return m[1]
            .split(",")
            .map((s) => Number(s.trim()))
            .filter((n) => !Number.isNaN(n));
        };

        // --- Divisions ---
        normalized.division_ids =
          toIdArray(raw.division_ids, ["division_id", "id", "value"]) ||
          toIdArray(raw.divisions, ["division_id", "id", "value"]) ||
          toIdArray(raw.division_id, ["division_id", "id", "value"]);
        if (!Array.isArray(normalized.division_ids))
          normalized.division_ids = [];

        // --- Offices ---
        normalized.office_ids =
          toIdArray(raw.office_ids, ["office_id", "id", "value"]) ||
          toIdArray(raw.offices, ["office_id", "id", "value"]) ||
          toIdArray(raw.office_id ?? raw.employment_office_id, [
            "office_id",
            "id",
            "value",
          ]);
        if (!Array.isArray(normalized.office_ids)) normalized.office_ids = [];
        if (normalized.office_ids.length === 0) {
          normalized.office_ids = parseOfficeMarkerIds(raw.note);
        }
        // --- Legacy Compatibility ---
        // Mobile: old rows may have phone_mobile only
        if (!normalized.phone && raw.phone_mobile) {
          normalized.phone = raw.phone_mobile;
        }

        // Start date fallback for older records
        if (!normalized.start_date && raw.hire_date) {
          normalized.start_date = raw.hire_date;
        }

        // Map server residency tokens to UI values. mapServerResidencyToUI collapses
        // multiple legacy synonyms (e.g. "citizen" and "canadian") onto one UI option, so
        // mapping back on save isn't guaranteed to reproduce the exact original string --
        // saving this form for an unrelated reason (e.g. only Employment Office) could
        // silently rewrite residency_status in the database to a different raw value with
        // the same meaning, showing up as a spurious "changed" entry in the audit log.
        // Keeping the untouched original value lets save skip the lossy round-trip
        // whenever the user never actually touched this dropdown.
        try {
          const serverVal =
            normalized.residency_status || normalized.residencyStatus || null;
          if (serverVal !== undefined && serverVal !== null) {
            normalized.residency_status_raw = serverVal;
            normalized.residency_status = mapServerResidencyToUI(serverVal);
          }
        } catch (e) {
          console.warn("Office residency mapping failed", e);
        }

        setData(normalized);
        setSnapshot(normalized);
        (async () => {
          try {
            if (
              normalized.residency_document ||
              normalized.residency_document_url ||
              normalized.residency_document_name
            )
              return;

            let fileId =
              normalized.work_permit_file_id ||
              normalized.study_permit_file_id ||
              normalized.refugee_file_id ||
              null;

            if (!fileId && typeof normalized.note === "string") {
              const mRef = normalized.note.match(
                /refugee_file_id\s*:\s*(\d+)/i,
              );
              const mWork = normalized.note.match(
                /work_permit_file_id\s*:\s*(\d+)/i,
              );
              const mStudy = normalized.note.match(
                /study_permit_file_id\s*:\s*(\d+)/i,
              );
              if (mRef) fileId = mRef[1];
              else if (mWork) fileId = mWork[1];
              else if (mStudy) fileId = mStudy[1];
            }

            if (!fileId) return;

            const resp = await fetch(
              `${BASE_URL}/api/registration/file/${fileId}/url`,
              { headers: getAuthHeaders() },
            );
            const j = await resp.json().catch(() => null);
            if (!resp.ok || !j) return;
            const url = j.url || j.signedUrl || j.data?.url || "";
            const name =
              j.name || j.filename || (url ? url.split("/").pop() : "") || "";
            if (!url) return;
            setData((prev) => ({
              ...(prev || {}),
              residency_document: fileId,
              residency_document_url: url,
              residency_document_name: name,
            }));
            setSnapshot((prev) => ({
              ...(prev || {}),
              residency_document: fileId,
              residency_document_url: url,
              residency_document_name: name,
            }));
          } catch (e) {
            console.warn("Failed to resolve office residency file", e);
          }
        })();
        // Preload option lists so selected labels render in view/edit without opening dropdown first.
        try {
          await Promise.all([loadDivisions(), loadOffices()]);
        } catch (_) {}
      } catch (_) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [person?.id]);

  const loadDivisions = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/divisions`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      const rows = Array.isArray(json)
        ? json
        : json && Array.isArray(json.data)
          ? json.data
          : [];
      setDivisionOptions(rows);
    } catch (e) {
      console.error("Failed to load divisions", e);
    }
  };

  const loadOffices = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/offices`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      const rows = Array.isArray(json)
        ? json
        : json && Array.isArray(json.data)
          ? json.data
          : [];
      setOfficeOptions(rows);
    } catch (e) {
      console.error("Failed to load offices", e);
    }
  };

  function MultiSelectDropdown({
    label,
    options = [],
    value = [],
    onChange,
    disabled = false,
    onOpen,
    required = false,
  }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // ✅ Close dropdown when clicking outside
    useEffect(() => {
      if (DEBUG_DISABLE_MULTISELECT_OUTSIDE_CLOSE) {
        console.log("MultiSelect outside-close disabled (debug) for", label);
        return;
      }
      const handleOutsideClick = (e) => {
        try {
          const target = e && e.target;
          const contains = ref.current && ref.current.contains(target);
          console.log("MultiSelect handleOutsideClick", {
            label,
            open,
            targetTag: target && target.tagName,
            targetClass: target && target.className,
            contains,
          });
          if (ref.current && !contains) {
            setOpen(false);
          }
        } catch (err) {
          console.error("MultiSelect handleOutsideClick error", err);
        }
      };

      document.addEventListener("click", handleOutsideClick);
      return () => document.removeEventListener("click", handleOutsideClick);
    }, []);

    // ✅ Toggle checkbox value (accepts ids or option objects)
    const toggleValue = (id) => {
      const valueIds = Array.isArray(value)
        ? value.map((v) =>
            v && typeof v === "object"
              ? (v.id ?? v.value ?? v.account_id ?? v.division_id ?? v)
              : v,
          )
        : [];
      const exists = valueIds.map(String).includes(String(id));
      if (exists) {
        onChange(
          valueIds
            .filter((v) => String(v) !== String(id))
            .map((v) => (Number.isNaN(Number(v)) ? v : Number(v))),
        );
      } else {
        onChange([...valueIds, Number.isNaN(Number(id)) ? id : Number(id)]);
      }
    };

    // ✅ Convert selected IDs → label text (normalize value objects)
    const valueIds = Array.isArray(value)
      ? value.map((v) =>
          v && typeof v === "object"
            ? (v.id ?? v.value ?? v.account_id ?? v.division_id ?? v)
            : v,
        )
      : [];
    const selectedText = options
      .filter((o) => valueIds.map(String).includes(String(o.id)))
      .map((o) => o.label)
      .join(", ");

    return (
      <div className="relative" ref={ref}>
        <label className="text-xs font-medium text-gray-600">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>

        {/* ✅ Dropdown trigger */}
        <div
          className={`border rounded px-3 py-2 bg-white cursor-pointer ${
            disabled ? "opacity-60 pointer-events-none" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            const nextOpen = !open;
            console.log("MultiSelect toggle", {
              label,
              nextOpen,
              optionsLength: options?.length,
            });
            if (nextOpen) {
              // delay opening to let the originating click event finish
              setTimeout(() => setOpen(true), 50);
            } else {
              setOpen(false);
            }
            if (!open && onOpen) {
              console.log("MultiSelect onOpen called", { label });
              onOpen(); // ✅ lazy load here
            }
          }}
        >
          {selectedText || "Select"}
        </div>

        {/* ✅ Dropdown list */}
        {open && !disabled && (
          <div className="absolute z-50 mt-1 w-full bg-white border rounded shadow max-h-48 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">
                No data available
              </div>
            ) : (
              options.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-sm cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={valueIds.map(String).includes(String(opt.id))}
                    onChange={() => toggleValue(opt.id)}
                  />
                  {opt.label}
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  function SingleSelectDropdown({
    label,
    options = [],
    value = "",
    onChange,
    disabled = false,
    onOpen,
  }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const valueId =
      value && typeof value === "object"
        ? (value.id ??
          value.value ??
          value.account_id ??
          value.office_id ??
          value.employment_office_id ??
          value)
        : value;

    const selected = options.find((o) => String(o.id) === String(valueId));
    const selectedText = selected ? selected.label : "Select";

    const toggle = async () => {
      if (disabled) return;
      if (!open && typeof onOpen === "function") {
        try {
          await onOpen();
        } catch (err) {
          console.warn("onOpen failed for SingleSelectDropdown", { label, err });
        }
      }
      const next = !open;
      if (next) {
        setTimeout(() => setOpen(true), 50);
      } else {
        setOpen(false);
      }
    };

    const onChoose = (id) => {
      const out = Number.isNaN(Number(id)) ? id : Number(id);
      onChange(out);
      setOpen(false);
    };

    return (
      <div className="relative" ref={ref}>
        {open && (
          <div className="fixed inset-0 z-[9999]" onClick={() => setOpen(false)} />
        )}
        <div
          className={`w-full border rounded px-3 py-2 cursor-pointer ${
            disabled ? "opacity-60 pointer-events-none" : ""
          }`}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <div className="text-sm text-gray-800 break-words whitespace-normal">
            {selectedText}
          </div>
        </div>

        {open && (
          <div className="absolute z-[10000] mt-1 w-80 max-w-full bg-white border rounded shadow max-h-60 overflow-y-auto">
            {options.map((opt) => (
              <div
                key={String(opt.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  onChoose(opt.id);
                }}
                className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer whitespace-normal break-words"
              >
                {opt.label}
              </div>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No data</div>
            )}
          </div>
        )}
      </div>
    );
  }

  const officeGetVal = (k, fallback) =>
    data && data[k] !== undefined && data[k] !== null ? data[k] : fallback;
  const officeSetField = (k, v) =>
    setData((prev) => ({ ...(prev || {}), [k]: v }));
  const officeHandleResidencyFile = async (file) => {
    if (!file || !person?.id) return;
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      return;
    }
    const docType = (
      officeGetVal("residency_status", person?.residency_status) || ""
    ).trim();
    if (!["work_permit", "study_permit", "refugee"].includes(docType)) {
      console.warn("Residency upload attempted without valid docType:", docType);
      return;
    }

    setResidencyUpload({ name: file.name, uploading: true, error: "" });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);

    try {
      const resp = await axios.post(
        `${BASE_URL}/api/registration/${person.id}/upload-and-link`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getAuthHeaders(),
          },
        },
      );
      const d = resp.data || {};
      if (!d || !d.file_id) {
        console.error(
          "Residency upload failed: server did not return file id",
          d,
        );
        setResidencyUpload({
          name: file.name,
          uploading: false,
          error: "Upload failed. Please try again.",
        });
        return;
      }

      officeSetField("residency_document", d.file_id);
      officeSetField("residency_document_name", file.name);
      officeSetField("residency_document_url", d.url || d.s3Key || "");
      setResidencyUpload({ name: "", uploading: false, error: "" });
    } catch (err) {
      console.error("Residency upload error", err);
      setResidencyUpload({
        name: file.name,
        uploading: false,
        error: "Upload failed. Please try again.",
      });
    }
  };

  const officeRemoveResidencyFile = async () => {
    const fileId = officeGetVal("residency_document");
    setResidencyUpload({ name: "", uploading: false, error: "" });
    if (!fileId) {
      officeSetField("residency_document", null);
      officeSetField("residency_document_url", "");
      officeSetField("residency_document_name", "");
      return;
    }

    try {
      const resp = await axios.delete(
        `${BASE_URL}/api/registration/${person.id}/files/${fileId}`,
        { headers: getAuthHeaders() },
      );
      const d = resp.data || {};
      if (d && d.success === false) {
        console.warn("Failed to remove residency file", d);
      }
    } catch (err) {
      console.error("Error removing residency file", err);
    }

    officeSetField("residency_document", null);
    officeSetField("residency_document_url", "");
    officeSetField("residency_document_name", "");
  };

  const officeDownloadResidencyFile = async () => {
    try {
      let url = officeGetVal("residency_document_url", "");
      const fileId = officeGetVal("residency_document", "");
      if (!url && fileId) {
        const resp = await fetch(
          `${BASE_URL}/api/registration/file/${fileId}/url`,
          { headers: getAuthHeaders() },
        );
        const j = await resp.json().catch(() => null);
        if (resp.ok && j) {
          url = j.url || j.signedUrl || j.data?.url || "";
        }
      }
      if (!url) {
        showError("Unable to download residency document.", "Download Error");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download =
        officeGetVal("residency_document_name", "") || "residency-document";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Residency download error", err);
      showError("Unable to download residency document.", "Download Error");
    }
  };
  const officeOnCancel = () => {
    setData(snapshot);
    setEditing(false);
  };

  const officeOnSave = async () => {
    if (!person?.id || !data) return;
    setSaving(true);
    try {
      const residency = (
        officeGetVal(
          "residency_status",
          person?.residencyStatus || person?.residency_status || "",
        ) || ""
      ).trim();
      const hasResidencyFile = Boolean(
        officeGetVal("residency_document") ||
          officeGetVal("residency_document_url") ||
          officeGetVal("residency_document_name") ||
          person?.residency_document ||
          person?.residency_document_url ||
          person?.residency_document_name,
      );
      if (
        ["work_permit", "study_permit", "refugee"].includes(residency) &&
        !hasResidencyFile
      ) {
        const label =
          residency === "work_permit"
            ? "Work Permit"
            : residency === "study_permit"
              ? "Study Permit"
              : "Refugee document";
        showError(
          `Please upload the required ${label} before saving.`,
          "Missing Residency Document",
        );
        setSaving(false);
        return;
      }
      const payload = {
        first_name: officeGetVal("first_name"),
        last_name: officeGetVal("last_name"),
        preferred_name: officeGetVal("preferred_name"),
        middle_name: officeGetVal("middle_name"),
        email: officeGetVal("email"),
        gender: officeGetVal("gender"),
        date_of_birth: officeGetVal("date_of_birth"),
        phone: officeGetVal("phone"),
        personal_number: officeGetVal("personal_number"),
        personal_email: officeGetVal("personal_email"),
        emergency_name: officeGetVal("emergency_name"),
        emergency_phone: officeGetVal("emergency_phone"),
        type: "officestaff",
        start_date: officeGetVal("start_date"),
        state: officeGetVal("state"),
        hst_number: officeGetVal("hst_number"),
        cheque_method: officeGetVal("cheque_method"),
        // Ensure we always send the UI-selected payment_delivery_method so backend
        // normalization can reliably map it to the DB enum. Fall back to cheque_method
        // for legacy clients.
        payment_delivery_method:
          officeGetVal("payment_delivery_method") ||
          officeGetVal("cheque_method") ||
          null,
        payout_account_id: officeGetVal("payout_account_id"),
        payer_id: officeGetVal("payer_id"),
        // Keep legacy office_id for backwards compatibility, but drive persistence with office_ids.
        office_id:
          officeGetVal("office_id") ||
          officeGetVal("employment_office_id") ||
          (officeGetVal("office_ids", []) || [])[0] ||
          null,
        office_ids:
          officeGetVal("office_ids", [])?.length > 0
            ? officeGetVal("office_ids", [])
            : officeGetVal("employment_office_id")
              ? [officeGetVal("employment_office_id")]
              : [],
        division_ids: officeGetVal("division_ids", []) || [],
        max_hours: officeGetVal("max_hours"),
        schedule_note: officeGetVal("schedule_note"),
        recruiter_account_id: officeGetVal("recruiter_account_id"),
        applicable_tax_form: officeGetVal("applicable_tax_form"),
        // If the dropdown still shows exactly what was loaded, send back the untouched
        // original raw value instead of re-deriving one -- mapUIResidencyToServer isn't
        // guaranteed to reproduce the original server string (multiple legacy values can
        // collapse onto the same UI option), so re-mapping an unchanged selection could
        // silently rewrite the stored value to a different-but-equivalent one. Only
        // genuinely changed selections go through the mapping.
        residency_status:
          officeGetVal("residency_status") === snapshot?.residency_status &&
          snapshot?.residency_status_raw != null
            ? snapshot.residency_status_raw
            : mapUIResidencyToServer(officeGetVal("residency_status")),
        line1: officeGetVal("line1"),
        line2: officeGetVal("line2"),
        city: officeGetVal("city"),
        province: officeGetVal("province"),
        postal_code: officeGetVal("postal_code"),
        country: officeGetVal("country"),
      };
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) throw new Error("Save failed");
      setSnapshot(data);
      setEditing(false);
      onUpdate?.({
        ...payload,
        id: person.id,
        account_id: person.id,
        avatar_url: data.avatar_url || data.profile_picture || person.avatar_url,
        profile_picture: data.profile_picture || data.avatar_url || person.profile_picture,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const officeOnAvatarSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !person?.id) return;

    console.log("Avatar upload started:", {
      fileName: file.name,
      fileSize: file.size,
      personId: person.id,
    });

    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/avatar`,
        { method: "POST", body: form },
      );
      const json = await res.json();

      console.log("Avatar upload response:", json);

      if (json.success && json.url) {
        const timestamp = Date.now();
        const cacheBustedUrl = json.url.includes("?")
          ? `${json.url}&t=${timestamp}`
          : `${json.url}?t=${timestamp}`;

        officeSetField("profile_picture", cacheBustedUrl);
        officeSetField("avatar_url", cacheBustedUrl);
        console.log("Set profile_picture and avatar_url to:", cacheBustedUrl);

        onUpdate?.({
          id: person.id,
          account_id: person.id,
          profile_picture: cacheBustedUrl,
          avatar_url: cacheBustedUrl,
        });

        // Refresh the data to get updated avatar
        const refreshRes = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}`,
        );
        const refreshData = await refreshRes.json();
        console.log("Refresh response:", refreshData);

        if (refreshData.data) {
          const freshBackendAvatar =
            json.url ||
            refreshData.data.avatar_url ||
            refreshData.data.profile_picture;
          const freshBackendUrl = freshBackendAvatar
            ? freshBackendAvatar.includes("?")
              ? `${freshBackendAvatar}&t=${timestamp}`
              : `${freshBackendAvatar}?t=${timestamp}`
            : cacheBustedUrl;

          setData((prev) => ({
            ...refreshData.data,
            profile_picture: freshBackendUrl,
            avatar_url: freshBackendUrl,
          }));

          onUpdate?.({
            ...refreshData.data,
            id: person.id,
            account_id: person.id,
            profile_picture: freshBackendUrl,
            avatar_url: freshBackendUrl,
          });
          console.log("Avatar updated successfully, new data:", freshBackendUrl);
        }
      } else {
        console.error("Avatar upload failed:", json);
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="flex items-center justify-start min-h-[40px]">
          <div className="flex gap-2">
            {!editing && (
              <button
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setEditing(true)}
                disabled={loading}
              >
                Edit
              </button>
            )}
            {editing && (
              <>
                <button
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  onClick={officeOnCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-2 bg-red-600 text-white rounded"
                  onClick={officeOnSave}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
          </div>
        </div>

        <Section title="General Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">Type</div>
              <select
                className="border border-gray-200 rounded px-3 py-2 bg-gray-50 text-gray-500"
                value="officestaff"
                disabled
              >
                <option value="officestaff">Office Staff</option>
              </select>
            </div>
            {/* removed Admin field per request */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="First Name"
                value={officeGetVal("first_name", "")}
                onChange={(v) => officeSetField("first_name", v)}
                disabled={!editing}
                required
              />
              <LabeledInput
                label="Last Name"
                value={officeGetVal("last_name", "")}
                onChange={(v) => officeSetField("last_name", v)}
                disabled={!editing}
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="Middle Name"
                value={officeGetVal("middle_name", "")}
                onChange={(v) => officeSetField("middle_name", v)}
                disabled={!editing}
              />
              <LabeledInput
                label="Preferred Name"
                value={officeGetVal("preferred_name", "")}
                onChange={(v) => officeSetField("preferred_name", v)}
                hint="Only if different from First Name"
                disabled={!editing}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="Email"
                value={officeGetVal("email", "")}
                onChange={(v) => officeSetField("email", v)}
                disabled={!editing}
                required
              />
              <div className="flex flex-col gap-1">
                <div className="text-xs font-medium text-gray-600">Gender</div>
                <select
                  className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
                  value={officeGetVal("gender", person?.gender) || ""}
                  onChange={(v) => officeSetField("gender", v.target.value)}
                  disabled={!editing}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:col-span-2">
              <LabeledInput
                label="Date of Birth"
                type="date"
                value={officeGetVal("date_of_birth", "")}
                onChange={(v) => officeSetField("date_of_birth", v)}
                disabled={!editing}
                min="1900-01-01"
              />
              <div></div>
            </div>
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Profile Picture
              </label>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-red-500 to-gray-800 border-4 border-white shadow-lg flex items-center justify-center">
                    {(() => {
                      const avatarUrl =
                        officeGetVal("profile_picture") ||
                        officeGetVal("avatar_url") ||
                        person?.profile_picture ||
                        person?.avatar_url;
                      const resolvedAvatarUrl = resolveBackendUrl(avatarUrl);
                      return (
                        <ProfileAvatarDisplay
                          url={resolvedAvatarUrl}
                          firstName={officeGetVal("first_name", "")}
                          lastName={officeGetVal("last_name", "")}
                          isOffice={true}
                        />
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 disabled:opacity-30"
                    title="Change profile picture"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </button>
                </div>
                <ProfilePhotoInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={officeOnAvatarSelected}
                />
              </div>
            </div>
          </div>
        </Section>

        <Section title="Contact Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <LabeledInput
              label="Mobile number on record with Talent"
              value={officeGetVal("phone_mobile", officeGetVal("phone", ""))}
              onChange={(v) => {
                officeSetField("phone_mobile", v);
                officeSetField("phone", v);
              }}
              disabled={!editing}
              required
            />
            <div />
            <div className="sm:col-span-2 flex flex-col gap-2">
              <div className="text-xs font-medium text-gray-600">
                Address <span className="text-red-500">*</span>
              </div>
              <AddressAutocomplete
                value={buildAddressAutocompleteValue(data || {})}
                onChange={(v) => applyAddressToForm(officeSetField, v)}
                disabled={!editing}
              />
            </div>
            <LabeledInput
              label="Emergency Contact Name"
              value={officeGetVal("emergency_name", "")}
              onChange={(v) => officeSetField("emergency_name", v)}
              disabled={!editing}
              required
            />
            <LabeledInput
              label="Emergency Contact Number"
              value={officeGetVal("emergency_phone", "")}
              onChange={(v) => officeSetField("emergency_phone", v)}
              disabled={!editing}
              required
            />
            <LabeledInput
              label="Personal Number"
              value={officeGetVal("personal_number", "")}
              onChange={(v) => officeSetField("personal_number", v)}
              disabled={!editing}
            />
            <LabeledInput
              label="Personal Email"
              value={officeGetVal("personal_email", "")}
              onChange={(v) => officeSetField("personal_email", v)}
              disabled={!editing}
            />
          </div>
        </Section>

        <Section title="Employment Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Divisions
              </label>
              <ModernMultiSelect
                options={divisionOptions}
                value={officeGetVal("division_ids", []) || []}
                onChange={(vals) => officeSetField("division_ids", vals)}
                onOpen={loadDivisions}
                disabled={!editing}
                placeholder="Select divisions..."
                displayKey="name"
                valueKey="division_id"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Office <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
                value={
                  officeGetVal(
                    "employment_office_id",
                    person?.employment_office_id ||
                      person?.office_id ||
                      (person?.office_ids && person.office_ids[0]) ||
                      (officeGetVal("office_ids", []) || [])[0] ||
                      "",
                  ) || ""
                }
                onFocus={loadOffices}
                onChange={(e) => {
                  const v = e.target.value;
                  const out = Number.isNaN(Number(v)) ? v : Number(v);
                  officeSetField("employment_office_id", out);
                  officeSetField("office_ids", v ? [out] : []);
                }}
                disabled={!editing}
              >
                <option value="">Select</option>
                {officeOptions.map((o) => (
                  <option
                    key={String(o.office_id || o.id)}
                    value={String(o.office_id || o.id)}
                  >
                    {o.name || o.label || String(o)}
                  </option>
                ))}
              </select>
            </div>
            <LabeledInput
              label="Maximum Hours per Week"
              value={officeGetVal("max_hours", "")}
              onChange={(v) => officeSetField("max_hours", v)}
              disabled={!editing}
            />
            <LabeledInput
              label="Start Date"
              type="date"
              value={officeGetVal("start_date", officeGetVal("hire_date", ""))}
              onChange={(v) => officeSetField("start_date", v)}
              disabled={!editing}
            />
            <LabeledInput
              label="Schedule Note"
              value={officeGetVal("schedule_note", "")}
              onChange={(v) => officeSetField("schedule_note", v)}
              disabled={!editing}
            />
          </div>
        </Section>

        <Section title="Payroll Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600">
                Residency Status <span className="text-red-500">*</span>
              </label>
              <select
                value={officeGetVal("residency_status", "") || ""}
                onChange={(e) =>
                  officeSetField("residency_status", e.target.value)
                }
                disabled={!editing}
                className={`w-full border rounded px-3 py-2 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 ${
                  !editing ? "opacity-80" : ""
                }`}
              >
                <option value="">Select</option>
                <option value="canadian">
                  Canadian Citizen / Permanent Resident
                </option>
                <option value="work_permit">Work permit</option>
                <option value="study_permit">Study permit</option>
                <option value="refugee">Refugee</option>
              </select>

              {editing &&
                ["work_permit", "study_permit", "refugee"].includes(
                  (
                    officeGetVal("residency_status", person?.residency_status) ||
                    ""
                  ).trim(),
                ) && (
                  <div className="mt-3">
                    <div
                      className="residency-upload-box border border-dashed rounded p-4 text-center"
                      onDrop={(e) => {
                        e.preventDefault();
                        const f =
                          e.dataTransfer.files && e.dataTransfer.files[0];
                        if (f) officeHandleResidencyFile(f);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      style={{
                        minHeight: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 8,
                        zIndex: 11000,
                        pointerEvents: "auto",
                      }}
                    >
                      <div className="text-sm text-gray-600">
                        Drag & Drop your file or use the button below
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        {residencyUpload.name && (
                          <div className="text-amber-700">
                            Selected: {residencyUpload.name}
                            {residencyUpload.uploading
                              ? " (uploading...)"
                              : ""}
                          </div>
                        )}
                        {residencyUpload.error && (
                          <div className="text-red-600">
                            {residencyUpload.error}
                          </div>
                        )}
                        {officeGetVal("residency_document_url") ||
                        officeGetVal("residency_document") ? (
                          <div className="inline-flex items-center gap-2">
                            {officeGetVal("residency_document_url") ? (
                              <a
                                href={officeGetVal("residency_document_url")}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 underline break-words max-w-xs"
                              >
                                {officeGetVal("residency_document_name") ||
                                  "Open document"}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-700 break-words max-w-xs">
                                {officeGetVal("residency_document_name") ||
                                  "Uploaded document"}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={officeDownloadResidencyFile}
                              className="text-sm text-blue-600 underline"
                              title="Download document"
                            >
                              Download
                            </button>
                            {officeGetVal("residency_document") && (
                              <button
                                type="button"
                                onClick={officeRemoveResidencyFile}
                                title="Remove file"
                                className="text-gray-500 hover:text-red-600 ml-1 px-2 py-1 text-xs rounded"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : !residencyUpload.name ? (
                          "No file selected"
                        ) : null}
                      </div>
                      <input
                        id="officeResidencyFile"
                        type="file"
                        accept={STAFF_DOCUMENT_ACCEPT}
                        ref={residencyFileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) officeHandleResidencyFile(f);
                        }}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <label
                          htmlFor="officeResidencyFile"
                          onClick={(ev) => {
                            ev.preventDefault();
                            try {
                              residencyFileInputRef?.current?.click();
                            } catch (__) {}
                          }}
                          className="inline-block px-4 py-2 bg-red-600 text-white rounded-3xl cursor-pointer"
                        >
                          Upload
                        </label>
                      </div>
                    </div>
                  </div>
                )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Payment Delivery Method
              </div>
              <div className="flex items-center gap-4">
                {["Pickup", "Mail"].map((opt) => (
                  <label
                    key={opt}
                    className={`inline-flex items-center gap-2 ${
                      !editing ? "opacity-60" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={
                        (
                          officeGetVal(
                            "payment_delivery_method",
                            officeGetVal("cheque_method", ""),
                          ) || ""
                        ).toLowerCase() === opt.toLowerCase()
                      }
                      onChange={() =>
                        officeSetField("payment_delivery_method", opt)
                      }
                      disabled={!editing}
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <LabeledInput
              label="Social Insurance Number"
              value={officeGetVal("sin", "")}
              onChange={(v) => officeSetField("sin", v)}
              disabled={!editing}
              required
            />
            <LabeledInput
              label="HST Number"
              value={officeGetVal("hst_number", "")}
              onChange={(v) => officeSetField("hst_number", v)}
              disabled={!editing}
            />
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Applicable Tax Form
              </div>
              <div className="flex items-center gap-4">
                {["T4", "T4A", "None"].map((opt) => (
                  <label
                    key={opt}
                    className={`inline-flex items-center gap-2 ${
                      !editing ? "opacity-60" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={
                        (officeGetVal("applicable_tax_form", "") || "") === opt
                      }
                      onChange={() =>
                        officeSetField("applicable_tax_form", opt)
                      }
                      disabled={!editing}
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      <div className="space-y-6 lg:sticky lg:top-0 self-start">
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-sm font-semibold text-gray-700">
            Account Status
          </div>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label="Created"
              value={formatAccountDateTime(officeGetVal("create_date", ""))}
            />
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                Account Status
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px]">
                  {(() => {
                    const s = (officeGetVal("state", "") || "").toLowerCase();
                    if (
                      s === "pending" ||
                      s === "pendingactivation" ||
                      s === "pending_activation" ||
                      s === "pending activation"
                    )
                      return "Pending Activation";
                    if (s === "active") return "Active";
                    if (s === "deleted") return "Deleted";
                    return officeGetVal("state", "");
                  })()}
                </div>
                <div className="relative" ref={statusMenuRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusMenuOpen(!statusMenuOpen);
                    }}
                    className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                    title="Account Status Options"
                  >
                    <FaEllipsisV className="text-gray-600" />
                  </button>
                  {statusMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setStatusMenuOpen(false)}
                      />
                      <div
                        className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                        style={
                          Object.keys(dropdownStyle).length > 0
                            ? dropdownStyle
                            : {
                                position: "fixed",
                                top: statusMenuRef.current
                                  ? `${statusMenuRef.current.getBoundingClientRect().bottom + 4}px`
                                  : "auto",
                                right: statusMenuRef.current
                                  ? `${window.innerWidth - statusMenuRef.current.getBoundingClientRect().right}px`
                                  : "16px",
                              }
                        }
                      >
                        {(officeGetVal("state", "") || "").toLowerCase() ===
                        "active" ? (
                          <div
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                            onClick={async (e) => {
                              e.stopPropagation();
                              setStatusMenuOpen(false);
                              setDialog({
                                title: "Confirm",
                                message:
                                  "Are you sure you would like to delete this account?",
                                onConfirm: () => handleStatusChange("deleted"),
                                onCancel: () => setDialog(null),
                              });
                            }}
                          >
                            <div className="text-sm font-medium text-red-600">
                              Delete Account
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Remove the account from the system, the person
                              will not able to Sign-in
                            </div>
                          </div>
                        ) : (officeGetVal("state", "") || "").toLowerCase() ===
                          "deleted" ? (
                          <>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to activate this account?",
                                  onConfirm: () => handleStatusChange("active"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-green-600">
                                Active
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to set this account to pending activation?",
                                  onConfirm: () =>
                                    handleStatusChange("pending activation"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-blue-600">
                                Pending Activation
                              </div>
                            </div>
                          </>
                        ) : (officeGetVal("state", "") || "").toLowerCase() ===
                            "pending" ||
                          (officeGetVal("state", "") || "").toLowerCase() ===
                            "pendingactivation" ||
                          (officeGetVal("state", "") || "").toLowerCase() ===
                            "pending_activation" ||
                          (officeGetVal("state", "") || "").toLowerCase() ===
                            "pending activation" ? (
                          <>
                            <div
                              className={`px-4 py-3 border-b border-gray-100 ${
                                isSendingActivation
                                  ? "cursor-not-allowed bg-gray-50"
                                  : "hover:bg-gray-50 cursor-pointer"
                              }`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isSendingActivation) return;
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to send an activation email?",
                                  onConfirm: () => {
                                    setDialog(null);
                                    handleSendActivationEmail();
                                  },
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-blue-600">
                                {isSendingActivation
                                  ? "Sending Activation Email..."
                                  : activationEmailSentDate
                                    ? "Resend Activation Email"
                                    : "Send Activation Email"}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                An email will be sent to{" "}
                                {officeGetVal("email", "")} to setup their
                                sign-in password
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to activate this account?",
                                  onConfirm: () => handleStatusChange("active"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-green-600">
                                Activate Account
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Account will be activated without notifying the
                                person
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(false);
                                setDialog({
                                  title: "Confirm",
                                  message:
                                    "Are you sure you would like to delete this account?",
                                  onConfirm: () =>
                                    handleStatusChange("deleted"),
                                  onCancel: () => setDialog(null),
                                });
                              }}
                            >
                              <div className="text-sm font-medium text-red-600">
                                Delete Account
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Remove the account from the system, the person
                                will not able to Sign-in
                              </div>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {(officeGetVal("state", "") || "").toLowerCase() === "pending" ||
              (officeGetVal("state", "") || "").toLowerCase() ===
                "pendingactivation" ||
              (officeGetVal("state", "") || "").toLowerCase() ===
                "pending_activation" ? (
                <div className="text-xs text-gray-500 mt-1">
                  {activationEmailSentDate
                    ? `Activation email sent ${formatAppDate(activationEmailSentDate, { emptyValue: "" })}`
                    : "Notification Not Sent"}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-sm font-semibold text-gray-700">
            Sign-in & Security
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">Email</div>
              <div className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px] overflow-x-auto whitespace-nowrap">
                {officeGetVal("email", "")}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-3">
                <Field label="Password" value="••••••••" />
              </div>
              <button
                className="px-3 py-2 bg-red-600 text-white rounded"
                onClick={() => setResetModalOpen(true)}
              >
                Reset Password
              </button>
            </div>
            <Field
              label="Last Sign-In"
              value={formatAccountDateTime(officeGetVal("last_sign_in", ""), {
                emptyValue: "Never",
              })}
            />
          </div>
        </div>
      </div>
      {resetModalOpen && (
        <ResetPasswordModal
          person={person}
          email={officeGetVal("email", "")}
          isOpen={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
        />
      )}
    </div>
  );
}

function OfficeFiles({ person }) {
  // NOTE: Personnel Files tab uses `TempFiles` for all staff types. This component is kept for reference only — keep SIN/expiry behavior in sync with TempFiles if this is ever wired up.
  const ALLOWED_FILE_TYPE_NAMES = new Set([
    "Object PDF",
    "SIN Document",
    "Identity Document",
    "Resume and Other",
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const [formData, setFormData] = useState({
    state: "active",
    type_id: "",
    note: "",
    file: null,
  });
  const [filterData, setFilterData] = useState({ state: "", type_id: "" }); // Separate state for filters (empty = show all)
  const [searchTerm, setSearchTerm] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [sortBy, setSortBy] = useState("file_name"); // defaultSortBy
  const [sortDirection, setSortDirection] = useState("asc"); // defaultSortDirection
  const [showSortByMenu, setShowSortByMenu] = useState(false);
  const [showSortDirMenu, setShowSortDirMenu] = useState(false);
  const sortByMenuRef = useRef(null);
  const sortDirMenuRef = useRef(null);

  // Edit modal state
  const [editingFile, setEditingFile] = useState(null);
  const [editFormData, setEditFormData] = useState({
    state: "",
    type_id: "",
    note: "",
    file: null,
  });
  const [editFormOriginal, setEditFormOriginal] = useState({
    state: "",
    type_id: "",
    note: "",
    file: null,
  });
  const allowedFileTypes = useMemo(
    () =>
      fileTypes.filter((t) =>
        ALLOWED_FILE_TYPE_NAMES.has((t.name || "").trim()),
      ),
    [fileTypes],
  );
  const editFileTypeOptions = useMemo(() => {
    const base = Array.isArray(allowedFileTypes) ? [...allowedFileTypes] : [];
    if (
      editingFile?.type_id &&
      !base.some((t) => String(t.type_id) === String(editingFile.type_id))
    ) {
      base.unshift({
        type_id: editingFile.type_id,
        name: editingFile.type_name || `Type ${editingFile.type_id}`,
      });
    }
    return base;
  }, [allowedFileTypes, editingFile]);

  const existingTypeIds = new Set(rows.map((r) => r.type_id).filter(Boolean));
  const selectedExists =
    formData.type_id && existingTypeIds.has(Number(formData.type_id));

  // Extract filename from file_url if file_name is missing or equals type_name
  const getFileName = (row) => {
    // If file_name exists and is different from type_name, use it
    if (row.file_name && row.file_name !== row.type_name) {
      return row.file_name;
    }

    // Otherwise, try to extract from file_url
    if (row.file_url) {
      try {
        const urlObj = new URL(row.file_url);
        const pathParts = urlObj.pathname.split("/");
        const lastPart = pathParts[pathParts.length - 1];
        // Remove timestamp prefix (format: timestamp_filename.ext)
        if (lastPart.includes("_")) {
          return lastPart.substring(lastPart.indexOf("_") + 1);
        }
        return lastPart;
      } catch (e) {
        // Fallback: try to extract from URL string directly
        const match = row.file_url.match(/\/([^\/]+)$/);
        if (match) {
          const lastPart = match[1];
          if (lastPart.includes("_")) {
            return lastPart.substring(lastPart.indexOf("_") + 1);
          }
          return lastPart;
        }
      }
    }

    // Final fallback
    return row.file_name || "—";
  };

  // Filter and sort logic
  const filteredRows = rows.filter((r) => {
    let matches = true;

    // Filter by status (use filterData, not formData - formData is for add modal)
    if (filterData.state) {
      let fileState = r.state;
      if (fileState === "pendingReview" || fileState === "pending_review") {
        fileState = "Pending Review";
      }
      matches = matches && fileState === filterData.state;
    }

    // Filter by type name (use filterData, not formData)
    if (filterData.type_id) {
      matches = matches && String(r.type_id) === String(filterData.type_id);
    }

    // Filter by search box
    if (searchTerm.trim() !== "") {
      const s = searchTerm.toLowerCase();
      matches =
        matches &&
        (r.file_name?.toLowerCase().includes(s) ||
          r.note?.toLowerCase().includes(s) ||
          r.type_name?.toLowerCase().includes(s) ||
          (r.file_url && r.file_url.toLowerCase().includes(s)));
    }

    return matches;
  });

  const sortedRows = [...filteredRows].sort((a, b) => {
    let x, y;

    // For file_name, use the extracted filename
    if (sortBy === "file_name") {
      x = getFileName(a);
      y = getFileName(b);
    } else {
      x = a[sortBy];
      y = b[sortBy];
    }

    // Handle null/undefined values
    if (x == null && y == null) return 0;
    if (x == null) return sortDirection === "asc" ? 1 : -1;
    if (y == null) return sortDirection === "asc" ? -1 : 1;

    // Handle date strings
    if (sortBy === "create_date") {
      const dateX = new Date(x);
      const dateY = new Date(y);
      if (isNaN(dateX.getTime()) && isNaN(dateY.getTime())) return 0;
      if (isNaN(dateX.getTime())) return sortDirection === "asc" ? 1 : -1;
      if (isNaN(dateY.getTime())) return sortDirection === "asc" ? -1 : 1;
      return sortDirection === "asc" ? dateX - dateY : dateY - dateX;
    }

    // Handle strings
    if (typeof x === "string") x = x.toLowerCase();
    if (typeof y === "string") y = y.toLowerCase();

    if (x < y) return sortDirection === "asc" ? -1 : 1;
    if (x > y) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const formatState = (state) => {
    if (!state) return "—";
    // Capitalize first letter of each word
    if (state === "active") return "Active";
    if (state === "inactive") return "Inactive";
    if (
      state === "Pending Review" ||
      state === "pendingReview" ||
      state === "pending_review"
    )
      return "Pending Review";
    // Fallback: capitalize first letter
    return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
  };

  const sortLabelMap = {
    file_name: "Name",
    state: "State",
    type_name: "Type",
  };
  const currentSortLabel = sortLabelMap[sortBy] || "Name";
  const currentDirectionLabel =
    sortDirection === "desc" ? "Descending" : "Ascending";
  const activeType = filterData.type_id
    ? fileTypes.find((t) => String(t.type_id) === String(filterData.type_id))
        ?.name || ""
    : "";
  const hasActiveFilters = Boolean(activeType || filterData.state);

  const load = async () => {
    if (!person?.id) return;
    setLoading(true);
    setError("");
    try {
      const query = filterData.showDeleted ? "?showDeleted=true" : "";
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files${query}`,
        { credentials: "include" },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to load files");
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      const msg = e.message || "Failed to load files";
      setError(msg);
      showError(msg, "Failed to Load Files");
    } finally {
      setLoading(false);
    }
  };

  const loadFileTypes = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/file-types`, {
        credentials: "include",
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to load file types");
      setFileTypes(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      // best-effort; show in UI when opening modal
    }
  };

  useEffect(() => {
    load();
    loadFileTypes();
  }, [person?.id, filterData.showDeleted]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    const validationMessage = file && validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      e.target.value = "";
      return;
    }
    setFormData((prev) => ({ ...prev, file }));
  };

  const handleSubmit = async () => {
    if (!formData.type_id || !formData.file) {
      showError("Type and file are required", "Validation");
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("state", formData.state);
      fd.append("type_id", String(formData.type_id));
      if (formData.note) fd.append("note", formData.note);
      fd.append("file", formData.file);
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: "include",
          body: fd,
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to add file");
      setShowModal(false);
      setFormData({ state: "active", type_id: "", note: "", file: null });
      load();
      showSuccess("File added successfully!");
    } catch (e) {
      const msg = e.message || "Failed to add file";
      setError(msg);
      showError(msg, "Failed to Add File");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (file) => {
    const rawState = String(file.state || "").trim();
    const normalizedStateKey = rawState
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .toLowerCase();
    const normalizedState =
      normalizedStateKey === "pending review" ||
      normalizedStateKey === "pending approval"
        ? "Pending Review"
        : rawState || "active";
    setEditingFile(file);
    const initialData = {
      state: normalizedState,
      type_id: String(file.type_id || ""),
      note: file.note || "",
      file: null,
    };
    setEditFormData(initialData);
    setEditFormOriginal(initialData);
  };

  const handleEditCancel = () => {
    setEditingFile(null);
    setEditFormData({ state: "", type_id: "", note: "", file: null });
    setEditFormOriginal({ state: "", type_id: "", note: "", file: null });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingFile) return;

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("state", editFormData.state);
      formDataToSend.append("type_id", editFormData.type_id);
      formDataToSend.append("note", editFormData.note || "");
      if (editFormData.file) {
        formDataToSend.append("file", editFormData.file);
      }

      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${editingFile.file_id}`,
        {
          method: "PUT",
          headers: { ...getAuthHeaders() },
          credentials: "include",
          body: formDataToSend,
        },
      );

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to update file");

      handleEditCancel();
      load(); // Reload the list
      showSuccess("File updated successfully!");
    } catch (e) {
      const errorMsg = e.message || "Failed to update file";
      setError(errorMsg);
      showError(errorMsg, "Failed to Update File");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!editingFile || !person?.id) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${editingFile.file_id}/download`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || "Failed to download file");
      }

      const blob = await res.blob();
      const expectedPdf =
        String(editingFile?.mime_type || "")
          .toLowerCase()
          .includes("pdf") ||
        String(editingFile?.extension || "").toLowerCase() === "pdf" ||
        String(editingFile?.name || editingFile?.file_name || "")
          .toLowerCase()
          .endsWith(".pdf");
      if (expectedPdf) {
        const header = await blob
          .slice(0, 5)
          .text()
          .catch(() => "");
        if (header !== "%PDF-") {
          throw new Error(
            "Downloaded content is not a valid PDF. Please re-upload this file.",
          );
        }
      }
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;

      let filename =
        editingFile.name ||
        editingFile.file_name ||
        getFileName(editingFile) ||
        "download";
      const contentDisposition = res.headers.get("content-disposition") || "";
      const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const headerName = utfMatch?.[1] || basicMatch?.[1];
      if (headerName) {
        try {
          filename = decodeURIComponent(headerName.trim());
        } catch (_) {
          filename = headerName.trim();
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 5000);
    } catch (e) {
      showError(e.message || "Failed to download file", "Download Error");
    }
  };

  // Check if edit form has changes
  const hasEditChanges =
    editingFile &&
    (editFormData.state !== editFormOriginal.state ||
      editFormData.type_id !== editFormOriginal.type_id ||
      editFormData.note !== editFormOriginal.note ||
      editFormData.file !== null);

  const handleDelete = async (fileId) => {
    if (!(await showConfirm("Are you sure you want to delete this file?")))
      return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/files/${fileId}`,
        {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: "include",
        },
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to delete file");
      load();
      showSuccess("File deleted successfully!");
    } catch (e) {
      const msg = e.message || "Failed to delete file";
      setError(msg);
      showError(msg, "Failed to Delete File");
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (sortByMenuRef.current && !sortByMenuRef.current.contains(e.target)) {
        setShowSortByMenu(false);
      }
      if (
        sortDirMenuRef.current &&
        !sortDirMenuRef.current.contains(e.target)
      ) {
        setShowSortDirMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (sortByMenuRef.current && !sortByMenuRef.current.contains(e.target)) {
        setShowSortByMenu(false);
      }
      if (
        sortDirMenuRef.current &&
        !sortDirMenuRef.current.contains(e.target)
      ) {
        setShowSortDirMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700">
        📎 Files for {person?.name || `#${person?.id}`}
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-1.5 border border-gray-400 rounded-xl bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active w-56"
        />

        <FilterButton onClick={() => setOpenFilter(true)} />

        <FilterBar
          isOpen={openFilter}
          onClose={() => setOpenFilter(false)}
          sortByOptions={[
            { value: "file_name", label: "Name" },
            { value: "state", label: "State" },
            { value: "type_name", label: "Type" },
          ]}
          afterSortBy={
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!filterData.showDeleted}
                onChange={(e) =>
                  setFilterData({
                    ...filterData,
                    showDeleted: e.target.checked,
                  })
                }
              />
              Show Deleted
            </label>
          }
          defaultSortBy={sortBy}
          defaultSortDirection={sortDirection}
          onDone={({ sortBy, sortDirection }) => {
            setSortBy(sortBy);
            setSortDirection(sortDirection);
          }}
          onClear={() => {
            // Clear filters AND reset sorting
            setSortBy("file_name");
            setSortDirection("asc");
            setFilterData({ type_id: "", state: "", showDeleted: false });
            setSearchTerm("");
          }}
        >
          <div className="flex flex-col gap-4">
            <label>
              {" "}
              Type
              <select
                className="border p-2 rounded w-full mt-2"
                value={filterData.type_id}
                onChange={(e) =>
                  setFilterData({ ...filterData, type_id: e.target.value })
                }
              >
                <option value="">All</option>
                {allowedFileTypes.map((item) => (
                  <option key={item.type_id} value={item.type_id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {" "}
              Status
              <select
                className="border p-2 rounded w-full mt-2"
                value={filterData.state}
                onChange={(e) =>
                  setFilterData({ ...filterData, state: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="Pending Review">Pending Review</option>
              </select>
            </label>
          </div>
        </FilterBar>
      </div>

      <div className="text-sm text-gray-600 -mt-1">
        Showing {filteredRows.length} of {rows.length} results sorted by{" "}
        <span className="relative inline-block" ref={sortByMenuRef}>
          <button
            type="button"
            className="text-red-600 hover:underline"
            onClick={() => {
              setShowSortByMenu((v) => !v);
            }}
          >
            {currentSortLabel}
          </button>
          {showSortByMenu && (
            <div className="absolute left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg min-w-[140px] z-20">
              {Object.entries(sortLabelMap).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setSortBy(key);
                    setShowSortByMenu(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${sortBy === key ? "text-red-600 font-semibold" : "text-gray-700"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </span>
        ,{" "}
        <button
          type="button"
          className="text-red-600 hover:underline"
          onClick={() =>
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
          }
        >
          {currentDirectionLabel}
        </button>
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {activeType && (
            <button
              type="button"
              onClick={() => setOpenFilter(true)}
              className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200"
              title="Open filters"
            >
              <span>Type: {activeType}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterData((prev) => ({ ...prev, type_id: "" }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setFilterData((prev) => ({ ...prev, type_id: "" }));
                  }
                }}
                className="font-bold"
                aria-label="Clear type filter"
              >
                ×
              </span>
            </button>
          )}
          {filterData.state && (
            <button
              type="button"
              onClick={() => setOpenFilter(true)}
              className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200"
              title="Open filters"
            >
              <span>Status: {formatState(filterData.state)}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterData((prev) => ({ ...prev, state: "" }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setFilterData((prev) => ({ ...prev, state: "" }));
                  }
                }}
                className="font-bold"
                aria-label="Clear status filter"
              >
                ×
              </span>
            </button>
          )}
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {filteredRows.length} file{filteredRows.length !== 1 ? "s" : ""}{" "}
          uploaded
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            if (fileTypes.length === 0) loadFileTypes();
          }}
          className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          Add File
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
          No files uploaded
        </div>
      ) : (
        <div className="border border-gray-200 rounded overflow-x-auto min-w-0 bg-white shadow-sm">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Note</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r.file_id} className="border-t border-gray-200">
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleEdit(r)}
                      className="text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                    >
                      {getFileName(r)}
                    </button>
                  </td>
                  <td className="px-3 py-2">{r.note || "—"}</td>
                  <td className="px-3 py-2">{formatState(r.state)}</td>
                  <td className="px-3 py-2">{r.type_name || "—"}</td>
                  <td className="px-3 py-2">
                    {r.create_date
                      ? formatAppDateTime(r.create_date, { emptyValue: "—" })
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleDelete(r.file_id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add File</h3>

            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">
                  Status <span className="text-red-500">*</span>
                </div>
                <select
                  value={formData.state}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, state: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="Pending Review">Pending Review</option>
                </select>
              </div>

              <div>
                <div className="text-xs text-gray-600 mb-1">
                  Type <span className="text-red-500">*</span>
                </div>
                <select
                  value={formData.type_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      type_id: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                  required
                >
                  <option value="">Select a type…</option>
                  {editFileTypeOptions.map((t) => (
                    <option key={t.type_id} value={t.type_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-xs text-gray-600 mb-1">Note</div>
                <textarea
                  value={formData.note}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, note: e.target.value }))
                  }
                  rows={3}
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>

              <div>
                <div className="text-xs text-gray-600 mb-1">
                  File <span className="text-red-500">*</span>
                </div>
                <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  onChange={handleFileChange}
                  className="w-full"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit File Modal */}
      {editingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit File</h3>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={editFormData.state}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, state: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="Pending Review">Pending Review</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={editFormData.type_id}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      type_id: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="">Select file type...</option>
                  {allowedFileTypes.map((t) => (
                    <option key={t.type_id} value={t.type_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={editFormData.note}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, note: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows={3}
                  placeholder="Optional note about this file..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File (optional - leave empty to keep current file)
                </label>
                <div className="border border-gray-300 rounded-lg p-4 bg-white relative">
                  {editFormData.file ? (
                    // Show newly selected file
                    <div className="flex flex-col items-center justify-center py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-700 font-medium">
                        {editFormData.file.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(editFormData.file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  ) : editingFile ? (
                    // Show current uploaded file
                    <div className="flex flex-col items-center justify-center py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-2 relative">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        {editingFile.extension && (
                          <span className="absolute bottom-1 text-xs font-semibold text-gray-600">
                            {editingFile.extension.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 font-medium text-center">
                        {(() => {
                          // Extract filename from S3 URL
                          const url = editingFile.file_url || "";
                          if (url) {
                            const parts = url.split("/");
                            const filename = parts[parts.length - 1];
                            const cleanFilename = filename.includes("_")
                              ? filename.substring(filename.indexOf("_") + 1)
                              : filename;
                            return cleanFilename || url;
                          }
                          return editingFile.file_name || "No file";
                        })()}
                      </p>
                    </div>
                  ) : (
                    // Show empty state
                    <div className="flex flex-col items-center justify-center py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500">No file uploaded</p>
                    </div>
                  )}

                  {/* Upload button in bottom right */}
                  <label className="absolute bottom-3 right-3 w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors">
                    <input
                      type="file"
                      accept={STAFF_DOCUMENT_ACCEPT}
                      onChange={(e) =>
                        (() => {
                          const file = e.target.files?.[0] || null;
                          const validationMessage =
                            file && validateStaffDocumentFile(file);
                          if (validationMessage) {
                            showError(validationMessage);
                            e.target.value = "";
                            return;
                          }
                          setEditFormData({ ...editFormData, file });
                        })()
                      }
                      className="hidden"
                    />
                    <FaUpload className="w-5 h-5 text-white" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={saving}
                >
                  Download
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={saving || !hasEditChanges}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}





